import type { Session } from '@supabase/supabase-js';
import {
  authService,
  publicData,
  streamResearch,
  type GuideDetailRecord,
  type GuideRecord,
  type Language,
  type LegalSource,
  type LegalUpdateRecord,
  type LibraryRecord,
  type ProductProofRecord,
  type ResearchResult,
  type ResourceState,
  type Role,
} from './services';
import { localizedPath, parseLocalizedPath, ui } from './i18n';
import { chatStore, type ChatThread } from './chatStore';
import { learningCatalog, getSection } from './learning/catalog';
import { bindLearningSession, buildGoDeeperQuery, renderLearningHome } from './learning/ui';
import { openDocumentOcrModal } from './document-ocr';
import { openMatterWorkspaceModal, getActiveMatter } from './matter-workspace';
import { openWhatsAppModal } from './whatsapp-modal';
import { initVoiceInput } from './voice-input';
import { analytics } from './analytics';

const appRoot = document.getElementById('app');
if (!appRoot) throw new Error('App root was not found.');

// Justor AI is exclusively for Advocates / Legal Professionals and Law Students
const roleLabels: Record<Role, string> = { student: 'Law Student', professional: 'Legal Professional' };
/** Roles shown on public landing & start pages */
const publicRoleOrder: Role[] = ['professional', 'student'];
const guideTopics = [
  { label: 'Property & Land', value: 'property' },
  { label: 'Family', value: 'family' },
  { label: 'Consumer Rights', value: 'consumer' },
  { label: 'Employment', value: 'employment' },
  { label: 'Digital & Online', value: 'digital' },
  { label: 'Cyber Safety', value: 'cyber' },
  { label: 'Tax & Finance', value: 'tax' },
  { label: 'Government Services', value: 'government' },
] as const;

function formatRelativeTime(isoStr: string): string {
  try {
    const date = new Date(isoStr);
    const now = new Date();
    const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffSec < 60) return 'Just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 172800) return 'Yesterday';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

const roleSuggestedQueries: Record<Role, Array<{ title: string; desc: string; query: string; icon: string }>> = {
  professional: [
    { title: 'Temporary Injunction', desc: 'CPC Order XXXIX Rules 1–2 vs CrPC s.144 boundary', query: 'What are the legal requirements to obtain a temporary injunction under CPC Order 39 in a property suit?', icon: 'scale' },
    { title: 'Cheque Dishonour Notice', desc: 'NI Act Section 138 30-day notice and Section 141', query: 'What is the mandatory procedure and 30-day notice timeline for cheque dishonour under Section 138 of the Negotiable Instruments Act?', icon: 'clock' },
    { title: 'Specific Performance & Deposit', desc: 'SRA s.21A balance consideration deposit rule', query: 'Can an unregistered contract for sale be specifically enforced under Section 21A of the Specific Relief Act?', icon: 'book' },
    { title: 'Juvenile Bail Presumption', desc: 'Children Act 2013 s.44 & s.54 mandatory presumption', query: 'Does the Children Act 2013 create a mandatory presumption of bail for juveniles overriding CrPC Section 497?', icon: 'shield' },
    { title: 'Inherent Quashing Power', desc: 'High Court Division powers under CrPC Section 561A', query: 'Under what circumstances can the High Court Division quash a criminal proceeding under Section 561A of CrPC?', icon: 'source' },
    { title: 'Family Court Exclusive Powers', desc: 'Family Courts Act 2023 jurisdiction over dower & maintenance', query: 'Which court has exclusive jurisdiction to try suits for dower and maintenance under the Family Courts Act 2023?', icon: 'home' }
  ],
  student: [
    { title: 'Doctrine of Part Performance', desc: 'Transfer of Property Act Section 53A essentials', query: 'Explain the Doctrine of Part Performance under Section 53A of the Transfer of Property Act with landmark case principles.', icon: 'book' },
    { title: 'Masdar Hossain Precedent', desc: 'Judicial separation from executive (53 DLR AD 1)', query: 'Summarize the landmark case brief for Secretary Ministry of Finance v. Masdar Hossain on judicial independence.', icon: 'scale' },
    { title: 'Dying Declaration Admissibility', desc: 'Evidence Act Section 32(1) exception to hearsay', query: 'What are the legal conditions for admissibility of a Dying Declaration under Section 32(1) of the Evidence Act?', icon: 'source' },
    { title: 'Criminal Breach of Trust vs Cheating', desc: 'Penal Code s.405/406 vs s.415/420 differences', query: 'What is the distinction between Criminal Breach of Trust under Section 406 and Cheating under Section 420 of the Penal Code?', icon: 'shield' }
  ]
};
const localizedRoleLabel = (role: Role): string => ui(state.language, role === 'student' ? 'student' : 'professional');
const rolePromise = (role: Role): string => ui(state.language, role === 'student' ? 'studentPromise' : 'professionalPromise');
const roleBody = (role: Role): string => ui(state.language, role === 'student' ? 'studentBody' : 'professionalBody');
const mobileRoleBody = (role: Role): string => {
  if (state.language === 'bn') {
    return role === 'professional' ? 'আইন ও কর্তৃত্বপূর্ণ উৎস গবেষণা করুন।' : 'মামলা ও আইন থেকে শিখুন।';
  }
  return role === 'professional' ? 'Research laws and authority.' : 'Learn cases and statutes.';
};
const roleContinue = (role: Role): string => ui(state.language, role === 'student' ? 'continueStudent' : 'continueProfessional');

const state: {
  language: Language;
  routePath: string;
  role: Role;
  menuOpen: boolean;
  sidebarOpen: boolean;
  session: Session | null;
  lastResearch: ResearchResult | null;
  lastResearchRole: Role | null;
  lastResearchQuery: string;
  selectedSource: number;
  guidePage: number;
  guideQuery: string;
  guideCluster: string;
  citizenHasSearched: boolean;
  productProof: ProductProofRecord | null;
  liveSearchEnabled: boolean;
} = {
  ...parseLocalizedPath(window.location.pathname),
  role: (localStorage.getItem('justor-role') as Role | null) === 'student' ? 'student' : 'professional',
  menuOpen: false,
  sidebarOpen: false,
  session: null,
  lastResearch: null,
  lastResearchRole: null,
  lastResearchQuery: '',
  selectedSource: 0,
  guidePage: 1,
  guideQuery: '',
  guideCluster: '',
  citizenHasSearched: false,
  productProof: null,
  liveSearchEnabled: false,
};


const escapeHtml = (value: unknown): string => String(value ?? '').replace(/[&<>'"]/g, (character) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;',
})[character] ?? character);

const safeUrl = (value?: string): string | null => {
  if (!value) return null;
  try {
    const url = new URL(value, window.location.origin);
    return ['http:', 'https:'].includes(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
};

const icon = (name: string, size = 20): string => {
  const paths: Record<string, string> = {
    arrow: '<path d="M5 12h14M13 6l6 6-6 6"/>',
    'arrow-left': '<path d="M19 12H5M12 19l-7-7 7-7"/>',
    back: '<path d="M19 12H5M12 19l-7-7 7-7"/>',
    search: '<circle cx="11" cy="11" r="7"/><path d="m20 20-3.6-3.6"/>',
    menu: '<path d="M4 7h16M4 12h16M4 17h16"/>',
    close: '<path d="m6 6 12 12M18 6 6 18"/>',
    book: '<path d="M4 5.5A3.5 3.5 0 0 1 7.5 2H11v18H7.5A3.5 3.5 0 0 0 4 23zM20 5.5A3.5 3.5 0 0 0 16.5 2H13v18h3.5A3.5 3.5 0 0 1 20 23z"/>',
    source: '<path d="M6 3h9l3 3v15H6z"/><path d="M14 3v4h4M9 12h6M9 16h6"/>',
    shield: '<path d="M12 3 5 6v5c0 4.6 2.8 8 7 10 4.2-2 7-5.4 7-10V6z"/><path d="m9 12 2 2 4-5"/>',
    scale: '<path d="M12 3v18M7 21h10M4 7h16M6 7 3 13h6L6 7Zm12 0-3 6h6l-3-6Z"/>',
    clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
    check: '<path d="m5 12 4 4L19 6"/>',
    external: '<path d="M14 4h6v6M20 4l-9 9"/><path d="M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6"/>',
    plus: '<path d="M12 5v14M5 12h14"/>',
    user: '<circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/>',
    gear: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
    home: '<path d="m3 11 9-8 9 8"/><path d="M5 10v11h14V10M9 21v-7h6v7"/>',
    cards: '<rect x="4" y="6" width="12" height="16" rx="2"/><path d="M10 4h8a2 2 0 0 1 2 2v14"/>',
    mail: '<rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>',
    message: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
    upload: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>',
    mic: '<path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3M8 22h8"/>',
    globe: '<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
    briefcase: '<rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>',
    whatsapp: '<path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21"/><path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1"/>',
    fileText: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>',
  };
  return `<svg aria-hidden="true" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${paths[name] ?? paths.source}</svg>`;
};

const pageBackButton = (fallbackHref = '/', label?: string): string => {
  const text = label || (state.language === 'bn' ? 'ফিরে যান' : 'Back');
  return `
    <div class="page-back-row">
      <button type="button" class="page-back-btn" data-action="go-back" data-fallback="${escapeHtml(fallbackHref)}" aria-label="${escapeHtml(text)}">
        ${icon('arrow-left', 16)} <span>${escapeHtml(text)}</span>
      </button>
    </div>
  `;
};

interface UserProfileData {
  fullName: string;
  email: string;
  role: Role;
  partnerOrg?: string; // e.g. 'habiganj-bar-council' | '' (empty = unaffiliated)
}

const getStoredProfile = (): UserProfileData => {
  try {
    const raw = localStorage.getItem('justor_user_profile');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.role === 'citizen') parsed.role = 'professional';
      return parsed;
    }
  } catch {}
  return {
    fullName: state.session?.user?.user_metadata?.full_name || (state.session?.user?.email ? state.session.user.email.split('@')[0] : 'User'),
    email: state.session?.user?.email || '',
    role: state.role || 'professional',
  };
};

const saveStoredProfile = (data: Partial<UserProfileData>): void => {
  const current = getStoredProfile();
  const updated = { ...current, ...data };
  if (updated.role === ('citizen' as any)) updated.role = 'professional';
  localStorage.setItem('justor_user_profile', JSON.stringify(updated));
};

const VALID_ROLES: Role[] = ['professional', 'student'];
const ROLE_LOCK_KEY = 'justor-role-locked';
const isValidRole = (value: unknown): value is Role => VALID_ROLES.includes(value as Role);
const isRoleLocked = (): boolean => localStorage.getItem(ROLE_LOCK_KEY) === 'true' && isValidRole(localStorage.getItem('justor-role'));
const lockedRole = (): Role | null => (isRoleLocked() ? (localStorage.getItem('justor-role') as Role) : null);
const lockUserRole = (role: Role): void => {
  localStorage.setItem('justor-role', role);
  localStorage.setItem(ROLE_LOCK_KEY, 'true');
  state.role = role;
  saveStoredProfile({ role });
};
const workspacePathForRole = (role: Role): string => localizedPath(`/workspace/${role}`, state.language);

const brandMarkSvg = (inverse = false): string => {
  const strokeColor = inverse ? '#3B82F6' : '#1E3AC8';
  const fillColor = inverse ? 'rgba(59, 130, 246, 0.18)' : 'rgba(30, 58, 200, 0.12)';
  const jColor = inverse ? '#FFFFFF' : '#1E3AC8';
  return `<svg class="brand-mark-svg" width="34" height="20" viewBox="0 0 68 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect x="3" y="4" width="28" height="28" rx="4" stroke="${strokeColor}" stroke-width="4.5" fill="${fillColor}"/><line x1="31" y1="18" x2="41" y2="18" stroke="${strokeColor}" stroke-width="4.5" stroke-linecap="round"/><circle cx="53" cy="18" r="13" stroke="${strokeColor}" stroke-width="4.5" fill="${fillColor}"/><path d="M54.5 11.5V19C54.5 20.6569 53.1569 22 51.5 22H49.5" stroke="${jColor}" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
};

const brand = (inverse = false): string => `<span class="brand-lockup ${inverse ? 'brand-lockup-inverse' : ''}">${brandMarkSvg(inverse)}<span class="brand-name">Justor <strong class="brand-ai">AI</strong></span></span>`;
const route = (path: string, label: string, className = ''): string => `<a href="${localizedPath(path, state.language)}" data-route class="${className}">${label}</a>`;

const header = (): string => {
  const profile = getStoredProfile();
  const firstName = state.session ? (profile.fullName.split(' ')[0] || ui(state.language, 'profile')) : '';
  const locked = lockedRole();
  const workspaceNavLinks = locked
    ? route(`/workspace/${locked}`, localizedRoleLabel(locked))
    : `${route('/workspace/professional', ui(state.language, 'legalProfessional'))}
        ${route('/workspace/student', ui(state.language, 'lawStudent'))}`;
  return `
  <header class="site-header" data-header>
    <div class="nav-shell">
      ${route('/', brand(true), 'brand-link')}
      <nav class="desktop-nav" aria-label="Primary navigation">
        ${workspaceNavLinks}
        ${route('/legal-library', ui(state.language, 'library'))}
        ${route('/guides', ui(state.language, 'guides'))}
      </nav>
      <div class="nav-actions">
        <button class="language-switch" type="button" data-action="language" aria-label="Switch language">${ui(state.language, 'language')}</button>
        ${state.session ? route('/profile', `<span class="nav-profile-pill">${icon('user', 14)} <span>${escapeHtml(firstName)}</span></span>`, 'nav-profile-link') : ''}
        ${state.session ? `<button class="nav-signin desktop-only-auth" type="button" data-action="sign-out">${ui(state.language, 'signOut')}</button>` : route('/login', ui(state.language, 'signInOrSignUp'), 'nav-signin')}
        <button class="menu-button" type="button" data-action="menu" aria-label="${state.menuOpen ? ui(state.language, 'close') : ui(state.language, 'menu')}" aria-expanded="${state.menuOpen}">${icon(state.menuOpen ? 'close' : 'menu')}</button>
      </div>
    </div>
    
    <!-- Mobile Full-Width Slide-in Drawer (Section M & N) -->
    <div class="mobile-drawer-overlay ${state.menuOpen ? 'is-open' : ''}" data-action="close-menu" aria-hidden="${!state.menuOpen}"></div>
    <nav class="mobile-drawer ${state.menuOpen ? 'is-open' : ''}" aria-label="Mobile navigation" aria-hidden="${!state.menuOpen}">
      <div class="mobile-drawer-header">
        ${route('/', brand(true), 'brand-link')}
        <button class="mobile-drawer-close" type="button" data-action="close-menu" aria-label="${ui(state.language, 'close')}">✕</button>
      </div>
      <div class="mobile-drawer-content">
        <!-- Founding Pilot Featured Card in Mobile Sidebar Drawer -->
        <div class="drawer-pilot-card" data-action="open-pilot-modal" role="button" tabindex="0" aria-label="${ui(state.language, 'foundingPilotCohort')}">
          <div class="drawer-pilot-card-top">
            <span class="drawer-pilot-kicker">
              <span class="pilot-pulse-dot" aria-hidden="true"></span>
              ${ui(state.language, 'foundingPilotCohort')}
            </span>
            <span class="drawer-pilot-price-tag">৳499/mo</span>
          </div>
          <div class="drawer-pilot-card-body">
            <strong class="drawer-pilot-title">${state.language === 'bn' ? 'ফাউন্ডিং চেম্বার্সে যুক্ত হোন ⚖️' : 'Join Founding Chambers ⚖️'}</strong>
            <p class="drawer-pilot-desc">${ui(state.language, 'foundingPilotCardDesc')}</p>
          </div>
          <div class="drawer-pilot-card-footer">
            <span class="drawer-pilot-action-text">${ui(state.language, 'foundingPilotApply')}</span>
            <span class="drawer-pilot-arrow" aria-hidden="true">→</span>
          </div>
        </div>

        <span class="menu-section-label">${ui(state.language, 'product')}</span>
        ${locked
          ? route(`/workspace/${locked}`, localizedRoleLabel(locked), 'menu-nav-link')
          : `${route('/workspace/professional', ui(state.language, 'legalProfessional'), 'menu-nav-link')}${route('/workspace/student', ui(state.language, 'lawStudent'), 'menu-nav-link')}`}
        ${route('/legal-library', ui(state.language, 'library'), 'menu-nav-link')}
        ${route('/workspace/student/learn', ui(state.language, 'biteSize'), 'menu-nav-link')}
        ${route('/guides', ui(state.language, 'guides'), 'menu-nav-link')}
        ${route('/legal-updates', ui(state.language, 'updates'), 'menu-nav-link')}
        <button class="menu-nav-link menu-nav-pilot-link" type="button" data-action="open-pilot-modal">
          <span class="menu-nav-pilot-left">
            <span class="pilot-pulse-dot" aria-hidden="true"></span>
            <span>${ui(state.language, 'foundingPilot')}</span>
          </span>
          <span class="menu-nav-pilot-badge">৳499</span>
        </button>
        
        <span class="menu-section-label" style="margin-top: 16px;">${ui(state.language, 'resources')}</span>
        ${route('/careers', 'Careers', 'menu-nav-link')}
        ${route('/profile', `${icon('user', 16)} ${ui(state.language, 'profile')} & ${ui(state.language, 'accountSettings')}`, 'menu-nav-link menu-nav-profile')}
        ${route('/trust', ui(state.language, 'trust'), 'menu-nav-link')}
        ${route('/about', ui(state.language, 'about'), 'menu-nav-link')}

        <div class="mobile-drawer-footer">
          <button class="button button-secondary language-drawer-btn" type="button" data-action="language">${ui(state.language, 'language')}</button>
          ${state.session 
            ? `<button class="button button-outline signout-drawer-btn" type="button" data-action="sign-out">${ui(state.language, 'signOut')}</button>` 
            : route('/login', ui(state.language, 'signInOrSignUp'), 'button signin-drawer-btn')
          }
        </div>
      </div>
    </nav>
  </header>`;
};

const footer = (): string => `
  <footer class="site-footer">
    <div class="footer-grid">
      <div>${route('/', brand(true), 'brand-link')}<p>Bangladesh legal intelligence for professional research and legal study.</p><span class="beta-label">${ui(state.language, 'controlledBeta')}</span></div>
      <nav aria-label="Product"><strong>Product</strong>${lockedRole() ? route(`/workspace/${lockedRole()}`, localizedRoleLabel(lockedRole() as Role)) : `${route('/workspace/professional', 'Legal Professional')}${route('/workspace/student', 'Law Student')}`}${route('/guides', 'Legal Guides')}${route('/start', 'Start Justor')}</nav>
      <nav aria-label="Resources"><strong>Resources</strong>${route('/legal-library', ui(state.language, 'library'))}${route('/guides', ui(state.language, 'guides'))}${route('/legal-updates', ui(state.language, 'updates'))}${route('/trust', ui(state.language, 'trust'))}</nav>
      <nav aria-label="Company"><strong>Company</strong>${route('/about', ui(state.language, 'about'))}${route('/about#team', 'Team')}${route('/careers', 'Careers')}${route('/about#investors', 'Investors')}${route('/contact', 'Contact')}${route('/feedback', 'Feedback')}</nav>
      <nav aria-label="Legal"><strong>Legal</strong>${route('/privacy', 'Privacy')}${route('/terms', 'Terms')}${route('/disclaimer', 'Disclaimer')}<a href="mailto:tajuddinahamed.contact@gmail.com">Email us</a></nav>
    </div>
    <div class="footer-bottom"><span>© 2026 Justor AI</span><span>Strictly for Bangladesh advocates, legal professionals, and law students.</span><a href="tel:+8801764662967">+880 1764-662967</a></div>
  </footer>`;

const roleRows = (surface: 'hero' | 'start' = 'hero'): string => {
  const locked = lockedRole();
  const roles = locked ? [locked] : publicRoleOrder;
  return `
  <nav class="${surface === 'hero' ? 'role-selectors' : 'start-roles'}" aria-label="Choose your Justor experience">
    ${roles.map((role) => `<a href="${workspacePathForRole(role)}" data-route data-role="${role}" class="role-row ${surface === 'start' ? 'start-role-row' : ''}"><span class="role-row-body"><span class="role-label">${localizedRoleLabel(role)}</span><strong class="role-heading">${rolePromise(role)}</strong><span class="role-desc role-desc-desktop">${roleBody(role)}</span><span class="role-desc role-desc-mobile">${mobileRoleBody(role)}</span></span><span class="role-arrow" aria-hidden="true">→</span><span class="sr-only">${roleContinue(role)}</span></a>`).join('')}
  </nav>`;
};

const libraryPreviewFallback = (): string => `
  <section class="library-band light-section">
    <div class="library-band-inner"><h2>${ui(state.language, 'libraryHeading')}</h2>${route('/legal-library', `${ui(state.language, 'exploreLibrary')} <span aria-hidden="true">→</span>`, 'link-arrow-light')}</div>
  </section>`;

const libraryPreviewFull = (records: LibraryRecord[]): string => `
  <section class="library-preview light-section"><div class="section-shell">
    <div class="section-heading section-heading-row"><div><span class="section-kicker">${ui(state.language, 'library')}</span><h2>${ui(state.language, 'libraryHeading')}</h2></div>${route('/legal-library', `${ui(state.language, 'libraryExplore')} ${icon('arrow', 16)}`, 'text-link')}</div>
    <form class="public-search" data-library-home-search><label><span class="sr-only">${ui(state.language, 'search')}</span>${icon('search')}<input name="query" placeholder="${ui(state.language, 'libraryPlaceholder')}"></label><button class="button" type="submit">${ui(state.language, 'search')}</button></form>
    <div class="filter-row" aria-label="Library types">
      ${route('/legal-library?type=law', 'Laws', 'filter-pill-link')}
      ${route('/legal-library?type=section', 'Sections', 'filter-pill-link')}
      ${route('/legal-library?type=case', 'Cases', 'filter-pill-link')}
      ${route('/legal-library?type=amendment', 'Amendments', 'filter-pill-link')}
      ${route('/guides', 'Guides', 'filter-pill-link')}
      ${route('/legal-updates', 'Updates', 'filter-pill-link')}
    </div>
    <div class="record-grid">${records.map(recordCard).join('')}</div>
  </div></section>`;

const homePage = (): string => `
  <main id="page-content">
    <section class="home-hero">
      <div class="hero-copy">
        <h1 class="hero-h1">${ui(state.language, 'heroHeadline')}</h1>
        <p class="hero-subtitle">${ui(state.language, 'heroBody')}</p>
        ${roleRows()}
        <p class="hero-guide-note">${state.language === 'bn' ? 'আইনি রেফারেন্স ও পাবলিক গাইড পড়তে <a href="/guides" data-route>আইনি গাইড</a> দেখুন।' : 'Looking for public legal references? <a href="/guides" data-route>Browse Legal Guides →</a>'}</p>
      </div>
      <div class="hero-visual hero-3d-canvas" role="presentation" aria-hidden="true">
        <img src="/visuals/hero-legal-environment-v2.webp" alt="" width="1" height="1" loading="lazy" decoding="async" fetchpriority="low" class="hero-3d-fallback" hidden data-hero-source>
        <canvas width="1536" height="1024" class="hero-3d-static" data-source="/visuals/hero-legal-environment-v2.webp"></canvas>
      </div>
    </section>
    <div class="hero-to-content-transition" aria-hidden="true"></div>
    <div data-home-proof></div>
    <div data-home-library-preview>${libraryPreviewFallback()}</div>

    <section class="trust-signal">
      <div class="section-shell trust-signal-grid"><div class="trust-signal-heading"><span class="section-kicker section-kicker-light">${ui(state.language, 'trustMethod')}</span><h2>${ui(state.language, 'trustHeading')}</h2></div><div class="trust-signal-definitions"><article><strong>Primary Source</strong><p>The authority itself.</p></article><article><strong>Source Checked</strong><p>The proposition–source relationship.</p></article><article><strong>Human Legal Reviewed</strong><p>A reviewed content version.</p></article></div>${route('/trust', `${ui(state.language, 'readTrust')} ${icon('arrow', 16)}`, 'link-arrow-dark')}</div>
    </section>

    <section class="section-shell incubation-signal"><img src="/visuals/nsu-startups-next.png" alt="NSU Startups Next" loading="lazy"><div><span class="section-kicker">${ui(state.language, 'incubation')}</span><h2>${ui(state.language, 'incubationStatement')}</h2><p>${ui(state.language, 'incubationSupport')}</p></div></section>

    <!-- Official Partners & Collaborations -->
    <section class="section-shell partners-band">
      <span class="section-kicker section-kicker-light">${state.language === 'bn' ? 'অফিসিয়াল পার্টনার ও সহযোগিতা' : 'Official Partners & Collaborations'}</span>
      <h2>${state.language === 'bn' ? 'আমাদের সাথে কারা আছেন' : 'Trusted by leading legal institutions'}</h2>
      <div class="partners-grid">
        <a class="partner-card" href="https://habiganjbar.com.bd" target="_blank" rel="noopener" aria-label="Habiganj Bar Council — Official Legal Partner">
          <div class="partner-logo-wrap">
            <img src="/visuals/habiganj-bar-council.jpg" alt="Habiganj Bar Council" class="partner-logo">
          </div>
          <div class="partner-info">
            <span class="partner-type">${state.language === 'bn' ? 'অফিসিয়াল লিগ্যাল পার্টনার' : 'Official Legal Partner'}</span>
            <strong class="partner-name">${state.language === 'bn' ? 'হবিগঞ্জ জেলা বার কাউন্সিল' : 'Habiganj Bar Council'}</strong>
            <span class="partner-link">habiganjbar.com.bd ${icon('external', 12)}</span>
          </div>
        </a>
        <a class="partner-card" href="https://iexf.org" target="_blank" rel="noopener" aria-label="IEXF — Official Community & Ecosystem Partner">
          <div class="partner-logo-wrap partner-logo-wrap-light">
            <img src="/visuals/iexf-logo.jpg" alt="IEXF" class="partner-logo partner-logo-iexf">
          </div>
          <div class="partner-info">
            <span class="partner-type">${state.language === 'bn' ? 'অফিসিয়াল কমিউনিটি ও ইকোসিস্টেম পার্টনার' : 'Official Community & Ecosystem Partner'}</span>
            <strong class="partner-name">IEXF</strong>
            <span class="partner-link">iexf.org ${icon('external', 12)}</span>
          </div>
        </a>
      </div>
    </section>

    <section class="early-access section-shell"><div><span class="section-kicker">${ui(state.language, 'controlledBeta')}</span><h2>${ui(state.language, 'earlyHeading')}</h2><p>${ui(state.language, 'earlyBody')}</p></div><a class="button" href="mailto:tajuddinahamed.contact@gmail.com?subject=Justor%20AI%20early%20access">${ui(state.language, 'earlyCta')} ${icon('arrow', 16)}</a></section>
  </main>`;

const startPage = (): string => `
  <main id="page-content" class="start-page">
    <header class="start-header">${route('/', brand(), 'brand-link')}<div><button class="language-switch" type="button" data-action="language" aria-label="Switch language">${ui(state.language, 'language')}</button>${state.session ? '<button type="button" data-action="sign-out" class="text-button">Sign Out</button>' : route('/login', ui(state.language, 'signIn'), 'text-button')}</div></header>
    <section class="start-content"><span class="section-kicker">${ui(state.language, 'start')}</span><h1>${ui(state.language, 'startHeading')}</h1><p>${ui(state.language, 'startBody')}</p>
      ${roleRows('start')}
    </section>
  </main>`;

const workspaceNav = (role: Role, items: Array<{ label: string; href: string; icon: string }>, active: string): string => {
  const threads = chatStore.getThreadsByRole(role);
  const activeThreadId = chatStore.getActiveThreadId(role);
  const profile = getStoredProfile();
  const initials = profile.fullName
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'JA';

  return `
  <div class="workspace-sidebar-overlay ${state.sidebarOpen ? 'is-open' : ''}" data-action="close-sidebar" aria-hidden="${!state.sidebarOpen}"></div>
  <aside class="workspace-sidebar ${state.sidebarOpen ? 'is-open' : ''}" aria-label="${localizedRoleLabel(role)} sidebar">
    <div class="workspace-brand">
      ${route('/', `${brand(true)}<span class="workspace-beta-badge">Beta</span>`, 'workspace-brand-link')}
      <button class="sidebar-close-btn mobile-only-close" type="button" data-action="close-sidebar" aria-label="${ui(state.language, 'close')}">✕</button>
    </div>
    <button class="new-research-capsule" type="button" data-action="new-research">
      ${icon('plus', 16)} <span>${role === 'professional' ? 'New Research' : role === 'student' ? 'New Study Chat' : 'New Legal Inquiry'}</span>
    </button>
    <button class="new-research-capsule matter-capsule-btn" type="button" data-action="open-matter-modal">
      ${icon('briefcase', 15)} <span>${state.language === 'bn' ? 'মোকদ্দমা ও চেম্বার' : 'Chamber OS · Matters'}</span>
    </button>
    ${role === 'professional' ? `
    <button class="new-research-capsule whatsapp-capsule-btn" type="button" data-action="open-whatsapp-modal" title="${state.language === 'bn' ? 'চেম্বার হোয়াটসঅ্যাপ অটোমেশন' : 'Chamber WhatsApp Gateway'}">
      ${icon('whatsapp', 15)} <span>${state.language === 'bn' ? 'চেম্বার হোয়াটসঅ্যাপ' : 'Chamber WhatsApp'}</span>
    </button>
    ` : ''}
    <nav class="sidebar-main-nav" aria-label="${localizedRoleLabel(role)} navigation">
      ${items.map((item) => route(item.href, `${icon(item.icon, 18)} <span>${item.label}</span>`, item.label === active ? 'active' : '')).join('')}
    </nav>

    <div class="sidebar-history-section">
      <div class="history-section-header">
        <span class="history-title">${icon('message', 14)} <span>Recent Research</span></span>
        ${threads.length > 0 ? `<button class="clear-history-btn" type="button" data-action="clear-threads" title="Clear all history">Clear</button>` : ''}
      </div>
      <div class="history-threads-list">
        ${threads.length === 0 ? `
          <div class="history-empty-note">No recent research yet.</div>
        ` : threads.map((t) => {
          const isActive = t.id === activeThreadId;
          const timeStr = formatRelativeTime(t.updatedAt);
          return `
            <div class="history-thread-item ${isActive ? 'is-active' : ''}" data-action="select-thread" data-thread-id="${t.id}">
              <div class="thread-item-content">
                <strong class="thread-item-title" title="${escapeHtml(t.title)}">${escapeHtml(t.title)}</strong>
                <span class="thread-item-time">${timeStr}</span>
              </div>
              <button class="thread-delete-btn" type="button" data-action="delete-thread" data-thread-id="${t.id}" title="${state.language === 'bn' ? `গবেষণা মুছুন: ${escapeHtml(t.title)}` : `Delete research thread: ${escapeHtml(t.title)}`}" aria-label="${state.language === 'bn' ? `গবেষণা মুছুন: ${escapeHtml(t.title)}` : `Delete research thread: ${escapeHtml(t.title)}`}">✕</button>
            </div>
          `;
        }).join('')}
      </div>
    </div>

    <!-- ChatGPT-Style Sidebar User Account Card & Footer Actions -->
    <div class="sidebar-bottom-actions">
      <button class="sidebar-pilot-pill" type="button" data-action="open-pilot-modal" title="Founding Advocate Tier (৳499/mo)">
        <span class="pilot-pulse-dot" aria-hidden="true"></span>
        <span class="sidebar-pilot-text">${ui(state.language, 'foundingPilot')}</span>
        <span class="sidebar-pilot-badge">৳499</span>
      </button>
      ${isRoleLocked() ? '' : `<button class="switch-experience" type="button" data-action="switch-experience">${ui(state.language, 'switchExperience')} ${icon('arrow', 15)}</button>`}
      
      <div class="sidebar-user-footer">
        ${route('/profile', `
          <div class="sidebar-user-card" title="Open User Profile &amp; Settings">
            <div class="sidebar-user-avatar">
              <span>${escapeHtml(initials)}</span>
              <span class="user-status-dot"></span>
            </div>
            <div class="sidebar-user-info">
              <strong class="sidebar-user-name">${escapeHtml(profile.fullName)}</strong>
              <span class="sidebar-user-plan">${localizedRoleLabel(profile.role)}</span>
            </div>
            <span class="sidebar-user-settings-icon">${icon('gear', 16)}</span>
          </div>
        `, 'sidebar-user-link')}
      </div>
    </div>
  </aside>`;
};

export const UNLIMITED_EMAILS: Set<string> = new Set([
  'shakhawatofficial00@gmail.com',
]);

export const isUnlimitedUser = (email?: string | null): boolean => {
  if (!email) return false;
  return UNLIMITED_EMAILS.has(email.trim().toLowerCase());
};

export const isBengaliRequested = (query?: string | null, currentLanguage?: string | null): boolean => {
  if (currentLanguage && currentLanguage.trim().toLowerCase().startsWith('bn')) return true;
  if (!query) return false;
  if (/[\u0980-\u09FF]/.test(query)) return true;
  const pattern = /(?:in\s+(?:bangla|bengali|bengla)|(?:answer|reply|respond|response|explain|write|tell(?:\s+me)?|speak|give(?:\s+me)?(?:\s+answer)?|provide(?:\s+answer)?)\s+in\s+(?:bangla|bengali|bengla)|(?:bangla|bengali)\s+(?:te|language|version|please|uttor|format)|banglay|banglate|uttor\s+din|bangla\s+bhashay)/i;
  return pattern.test(query);
};

const PARTNER_ORG_LABEL: Record<string, string> = {
  'habiganj-bar-council': 'Habiganj Bar Council',
  'unlimited-vip': 'VIP Unlimited ★',
};
const PARTNER_ORG_DAILY_LIMIT: Record<string, number> = {
  'habiganj-bar-council': 100,
  'unlimited-vip': 999999,
};
const UNAFFILIATED_DAILY_LIMIT = 10;

const getDailyLimit = (): number => {
  const profile = getStoredProfile();
  const email = state.session?.user?.email || profile.email;
  if (isUnlimitedUser(email) || (profile.partnerOrg || '').toLowerCase() === 'unlimited-vip') {
    return 999999;
  }
  const org = (profile.partnerOrg || '').toLowerCase();
  return PARTNER_ORG_DAILY_LIMIT[org] ?? UNAFFILIATED_DAILY_LIMIT;
};

const workspaceTopbar = (role: Role, title?: string): string => {
  const isGuest = localStorage.getItem('justor_guest_mode') === 'true';
  const profile = getStoredProfile();
  const userEmail = state.session?.user?.email || profile.email;
  const isVip = isUnlimitedUser(userEmail) || (profile.partnerOrg || '').toLowerCase() === 'unlimited-vip';
  const firstName = state.session ? (profile.fullName.split(' ')[0] || ui(state.language, 'profile')) : (isGuest ? 'Guest' : '');
  const initials = isGuest ? 'G' : (profile.fullName
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'U');

  const dailyLimit = getDailyLimit();
  const partnerOrg = (profile.partnerOrg || '').toLowerCase();
  const orgLabel = isVip ? (state.language === 'bn' ? 'ভিআইপি আনলিমিটেড ★' : 'VIP Unlimited ★') : (PARTNER_ORG_LABEL[partnerOrg] || null);

  return `
  <header class="workspace-topbar">
    <div class="workspace-topbar-start">
      <button class="workspace-menu-button" type="button" data-action="toggle-sidebar" aria-label="${state.sidebarOpen ? ui(state.language, 'close') : ui(state.language, 'menu')}" aria-expanded="${state.sidebarOpen}">${icon(state.sidebarOpen ? 'close' : 'menu')}</button>
      <a href="${localizedPath('/', state.language)}" data-route class="workspace-mobile-brand">${brand()}</a>
    </div>
    <span class="workspace-topbar-role">${localizedRoleLabel(role)}${title ? ` <span class="topbar-thread-title">&middot; ${escapeHtml(title)}</span>` : ''}</span>
    <div class="workspace-topbar-actions">
      <button class="topbar-matter-btn" type="button" data-action="open-matter-modal" title="${state.language === 'bn' ? 'মোকদ্দমা ফাইল ও চেম্বার ওএস' : 'Chamber OS · Legal Workspace'}">
        ${icon('briefcase', 13)} <span>${state.language === 'bn' ? 'চেম্বার ওএস' : 'Chamber OS'}</span>
      </button>
      ${role === 'professional' ? `
      <button class="topbar-wa-btn" type="button" data-action="open-whatsapp-modal" title="${state.language === 'bn' ? 'আইনজীবী চেম্বার হোয়াটসঅ্যাপ' : 'Lawyer Chamber WhatsApp Gateway'}">
        ${icon('whatsapp', 13)} <span>Chamber WA</span>
      </button>
      ` : ''}
      <span class="credit-counter-pill ${isVip ? 'credit-pill-vip' : ''}" data-credit-pill title="${isVip ? (state.language === 'bn' ? 'আনলিমিটেড ভিআইপি অ্যাক্সেস' : 'Unlimited VIP Access') : (state.language === 'bn' ? 'দৈনিক ক্রেডিট' : 'Daily credits')}" ${isVip ? 'style="background: rgba(124, 58, 237, 0.1); border-color: rgba(124, 58, 237, 0.35); color: #7C3AED; font-weight: 600;"' : ''}>
        ⬡ <span data-credit-remaining>${isVip ? '∞' : dailyLimit}</span>/<span data-credit-limit>${isVip ? '∞' : dailyLimit}</span>
        ${orgLabel ? `<span class="credit-partner-tag" ${isVip ? 'style="background: linear-gradient(135deg, #1E38C8, #7C3AED); color: #fff; font-weight: 700;"' : ''}>${orgLabel}</span>` : ''}
      </span>
      <button class="language-switch" type="button" data-action="language" aria-label="Switch language">${ui(state.language, 'language')}</button>
      ${state.session ? route('/profile', `
        <span class="topbar-profile-pill" title="User Profile">
          <span class="topbar-avatar-circle">${escapeHtml(initials)}</span>
          <span class="topbar-profile-name">${escapeHtml(firstName)}</span>
        </span>
      `, 'workspace-profile-btn') : route('/login', ui(state.language, 'signInOrSignUp'), 'button button-small')}
    </div>
  </header>`;
};

const quotaLine = (_role: Role): string => {
  const profile = getStoredProfile();
  const userEmail = state.session?.user?.email || profile.email;
  const isVip = isUnlimitedUser(userEmail) || (profile.partnerOrg || '').toLowerCase() === 'unlimited-vip';
  if (isVip) {
    return `<span class="quota-line" data-quota style="color: #6D28D9; font-weight: 600;">⭐ ${state.language === 'bn' ? 'আনলিমিটেড ভিআইপি লিগ্যাল রিসার্চ ও ওসিআর সক্রিয়' : 'Unlimited VIP Legal Research & OCR Active'}</span>`;
  }
  const limit = getDailyLimit();
  return `<span class="quota-line" data-quota>${state.session ? `${ui(state.language, 'dailyAllowance')}: ${limit}` : `${ui(state.language, 'signInQuotaPrefix')} ${limit} ${ui(state.language, 'answersPerDay')}`}</span>`;
};



const renderEmptyLanding = (role: Role): string => {
  const suggestions = roleSuggestedQueries[role] ?? roleSuggestedQueries.professional;
  return `
    <div class="chat-empty-landing">
      <div class="empty-landing-brand">
        <div class="landing-logo-badge">
          ${brandMarkSvg(false)}
        </div>
      </div>
      <h1 class="empty-landing-title">${role === 'professional' ? (state.language === 'bn' ? 'পেশাগত আইনি গবেষণা ও বুদ্ধিমত্তা' : 'Bangladesh Legal Research & Intelligence') : (state.language === 'bn' ? 'উৎস-সংযুক্ত আইনি শিক্ষা' : 'Source-Linked Legal Study')}</h1>
      <p class="empty-landing-subtitle">
        ${role === 'professional' 
          ? (state.language === 'bn' ? 'নিয়ন্ত্রণকারী আইন, গেজেট ও সুপ্রিম কোর্টের নজিরের ভিত্তিতে কর্তৃত্বপূর্ণ বিশ্লেষণ।' : 'Grounded legal analysis across controlling statutes, gazettes, and Supreme Court of Bangladesh precedents.') 
          : (state.language === 'bn' ? 'ধারা ও নজির বিশ্লেষণ করুন এবং সরকারি উৎস থেকে আইন শিখুন।' : 'Break down principles, structure case briefs, and verify statutory rules with official sources.')}
      </p>
      <div class="chat-suggested-grid">
        ${suggestions.map((item) => `
          <button type="button" class="suggested-card" data-suggested-query="${escapeHtml(item.query)}">
            <div class="suggested-card-header">
              ${icon(item.icon, 16)} <span>${escapeHtml(item.title)}</span>
            </div>
            <p>${escapeHtml(item.desc)}</p>
          </button>
        `).join('')}
      </div>
    </div>
  `;
};

const aiAuthorHeader = (): string => `
  <div class="ai-response-author">
    <div class="ai-author-badge">
      <img src="/visuals/justor-mark.png" alt="Justor AI" width="17" height="17">
    </div>
    <div class="ai-author-text">
      <strong class="ai-author-name">Justor <span class="ai-highlight">AI</span></strong>
      <span class="ai-author-pill">${state.language === 'bn' ? 'আইনি এআই সহকারী' : 'Legal Intelligence'}</span>
    </div>
  </div>
`;

const renderChatStream = (thread: ChatThread, role: Role): string => {
  if (thread.messages.length === 0) {
    return renderEmptyLanding(role);
  }
  return `
    <div class="chat-conversation-thread" data-chat-thread-id="${thread.id}">
      ${thread.messages.map((msg) => {
        if (msg.sender === 'user') {
          return `
            <div class="chat-message-row user-row" id="${msg.id}">
              <div class="chat-user-bubble">
                <p class="user-query-text">${escapeHtml(msg.content)}</p>
                <span class="user-bubble-time">${formatRelativeTime(msg.timestamp)}</span>
              </div>
            </div>
          `;
        } else {
          return `
            <div class="chat-message-row assistant-row" id="${msg.id}">
              <div class="chat-assistant-container">
                ${aiAuthorHeader()}
                <div class="assistant-content-wrapper">
                  ${msg.result ? renderResearchResult(msg.result, role) : `<div class="research-formatted-markdown">${formatAnswerMarkdown(msg.content, role)}</div>`}
                </div>
              </div>
            </div>
          `;
        }
      }).join('')}
    </div>
  `;
};

const renderBottomChatBar = (role: Role, placeholder: string, _quickActions?: string[], context?: { id: string; title: string; topic: string }): string => `
  <div class="chat-sticky-bottom-bar">
    <div class="chat-bottom-inner">
      <form class="chat-floating-composer" data-research-form data-role="${role}" ${context ? `data-context-id="${escapeHtml(context.id)}" data-context-title="${escapeHtml(context.title)}" data-context-topic="${escapeHtml(context.topic)}"` : ''}>
        <div class="composer-input-box">
          <div class="composer-left-tools">
            <button type="button" class="composer-tool-btn composer-matter-btn" data-action="open-matter-modal" aria-label="Open Chamber OS" title="${state.language === 'bn' ? 'মোকদ্দমা ফাইল ও চেম্বার ওএস (Chamber OS)' : 'Chamber OS · Matter Workspace & Dictaphone'}">
              ${icon('briefcase', 16)}
            </button>
            ${role === 'professional' ? `
            <button type="button" class="composer-tool-btn composer-wa-btn" data-action="open-whatsapp-modal" aria-label="Open Chamber WhatsApp" title="${state.language === 'bn' ? 'আইনজীবী চেম্বার হোয়াটসঅ্যাপ' : 'Lawyer Chamber WhatsApp Gateway'}">
              ${icon('whatsapp', 16)}
            </button>
            ` : ''}
            <button type="button" class="composer-tool-btn composer-attach-btn" data-action="open-ocr-modal" aria-label="Upload document for OCR" title="${state.language === 'bn' ? 'দলিল বা রায় আপলোড করুন (Google Vision OCR)' : 'Upload Deed / FIR / Order (Vision OCR)'}">
              ${icon('upload', 17)}
            </button>
            <button type="button" class="composer-tool-btn composer-search-toggle ${state.liveSearchEnabled ? 'is-active' : ''}" data-action="toggle-live-search" aria-pressed="${state.liveSearchEnabled}" title="${state.language === 'bn' ? 'গুগল লাইভ ওয়েব সার্চ অন/অফ' : 'Toggle Live Google Web Search'}">
              ${icon('globe', 14)}
              <span class="search-toggle-label">${state.language === 'bn' ? 'লাইভ সার্চ' : 'Search'}</span>
            </button>
          </div>
          <div class="composer-textarea-wrapper">
            <textarea name="query" rows="1" required placeholder="${placeholder}" data-auto-resize aria-label="${placeholder}"></textarea>
          </div>
          <div class="composer-right-tools">
            <button type="button" class="composer-tool-btn composer-voice-btn" data-action="toggle-voice-input" aria-label="Voice input" title="${state.language === 'bn' ? 'ভয়েস টাইপিং (বাংলা / ইংরেজি)' : 'Voice Input (Bengali / English)'}">
              ${icon('mic', 17)}
            </button>
            <button type="submit" class="composer-send-btn" aria-label="Submit query" title="Send (Enter)">
              ${icon('arrow', 16)}
            </button>
          </div>
        </div>
        <div class="composer-subline">
          <span class="composer-privacy-hint">${ui(state.language, 'composerPrivacyHint')}</span>
          ${quotaLine(role)}
        </div>
      </form>
    </div>
  </div>
`;



const professionalWorkspace = (): string => {
  const thread = chatStore.getOrCreateActiveThread('professional');
  return `
  <main id="page-content" class="workspace workspace-professional">
    <h1 class="sr-only">${state.language === 'bn' ? 'আইনি গবেষণা — পেশাদার ওয়ার্কস্পেস' : 'Legal Research — Professional Workspace'}</h1>
    ${workspaceNav('professional', [
      { label: state.language === 'bn' ? 'হোম' : 'Home', href: '/', icon: 'home' },
      { label: ui(state.language, 'researchHome'), href: '/workspace/professional', icon: 'source' },
      { label: ui(state.language, 'legalLibrary'), href: '/legal-library', icon: 'book' },
      { label: ui(state.language, 'guides'), href: '/guides', icon: 'book' },
      { label: ui(state.language, 'cases'), href: '/legal-library?type=case', icon: 'scale' },
      { label: ui(state.language, 'statutes'), href: '/legal-library?type=law', icon: 'source' },
      { label: ui(state.language, 'updates'), href: '/legal-updates', icon: 'clock' },
      { label: ui(state.language, 'profile'), href: '/profile', icon: 'user' },
    ], ui(state.language, 'researchHome'))}
    <section class="workspace-main">
      ${workspaceTopbar('professional', thread.title !== 'New Legal Research' ? thread.title : undefined)}
      <div class="workspace-chat-container">
        <div class="chat-scroll-area" data-chat-scroll>
          ${renderChatStream(thread, 'professional')}
        </div>
        ${renderBottomChatBar('professional', ui(state.language, 'professionalPlaceholder'))}
      </div>
    </section>
  </main>`;
};

const studentPreAuth = (): string => `
  <main id="page-content" class="preauth-page"><header>${route('/', brand(), 'brand-link')}<div><button class="language-switch" type="button" data-action="language" aria-label="Switch language">${ui(state.language, 'language')}</button>${route('/login', ui(state.language, 'signInOrSignUp'), 'text-link')}</div></header><section><span class="section-kicker">${ui(state.language, 'studentKicker')}</span><h1>${ui(state.language, 'studentHeading')}</h1><p>${ui(state.language, 'studentAllowance')}</p><button class="button google-button" type="button" data-action="google-sign-in" data-next="${localizedPath('/choose-role', state.language)}"><span>G</span> ${ui(state.language, 'continueGoogle')}</button><small>${ui(state.language, 'publicReading')}</small></section></main>`;

const studentNavItems = (): Array<{ label: string; href: string; icon: string }> => [
  { label: state.language === 'bn' ? 'হোম' : 'Home', href: '/', icon: 'home' },
  { label: ui(state.language, 'studyHome'), href: '/workspace/student', icon: 'home' },
  { label: ui(state.language, 'biteSize'), href: '/workspace/student/learn', icon: 'cards' },
  { label: ui(state.language, 'askJustor'), href: '/workspace/student#ask', icon: 'source' },
  { label: ui(state.language, 'guides'), href: '/guides', icon: 'book' },
  { label: ui(state.language, 'legalLibrary'), href: '/legal-library', icon: 'book' },
  { label: ui(state.language, 'cases'), href: '/legal-library?type=case', icon: 'scale' },
  { label: ui(state.language, 'statutes'), href: '/legal-library?type=law', icon: 'book' },
  { label: ui(state.language, 'profile'), href: '/profile', icon: 'user' },
];

const studentWorkspace = (): string => {
  if (!state.session) return studentPreAuth();
  const thread = chatStore.getOrCreateActiveThread('student');
  return `
  <main id="page-content" class="workspace workspace-student">
    <h1 class="sr-only">${state.language === 'bn' ? 'আইন শিক্ষা — শিক্ষার্থী ওয়ার্কস্পেস' : 'Legal Study — Student Workspace'}</h1>
    ${workspaceNav('student', studentNavItems(), ui(state.language, 'studyHome'))}
    <section class="workspace-main">
      ${workspaceTopbar('student', thread.title !== 'New Study Session' ? thread.title : undefined)}
      <div class="workspace-chat-container">
        <div class="chat-scroll-area" data-chat-scroll>
          ${renderChatStream(thread, 'student')}
        </div>
        ${renderBottomChatBar('student', state.language === 'bn' ? 'আইন, ধারা বা মামলার নীতি সম্পর্কে জিজ্ঞাসা করুন...' : 'Ask about a case, statute, legal concept or principle...')}
      </div>
    </section>
  </main>`;
};

const studentLearnWorkspace = (): string => {
  if (!state.session) return studentPreAuth();
  const sectionSlug = state.routePath.replace('/workspace/student/learn', '').replace(/^\//, '').split('/')[0] || '';
  const nav = workspaceNav('student', studentNavItems(), ui(state.language, 'biteSize'));
  const topbar = workspaceTopbar('student', ui(state.language, 'biteSize'));
  if (!sectionSlug) {
    return renderLearningHome(learningCatalog, state.language, route, nav, topbar, '');
  }
  return `
  <main id="page-content" class="workspace workspace-student workspace-learn workspace-learn-session">
    ${nav}
    <section class="workspace-main">
      ${topbar}
      <div class="learn-session-mount" data-learn-mount data-section="${escapeHtml(sectionSlug)}"></div>
    </section>
  </main>`;
};

const unavailable = (message?: string): string => `<div class="empty-state"><span>${icon('shield', 24)}</span><h3>${ui(state.language, 'unavailableTitle')}</h3><p>${message ?? ui(state.language, 'unavailableBody')}</p></div>`;
const empty = (): string => `<div class="empty-state"><span>${icon('search', 24)}</span><h3>${ui(state.language, 'noResults')}</h3><p>${ui(state.language, 'noResultsBody')}</p></div>`;

const libraryPage = (): string => {
  const query = new URLSearchParams(window.location.search).get('q') ?? '';
  const type = new URLSearchParams(window.location.search).get('type') ?? '';
  return `<main id="page-content" class="inner-page"><section class="compact-hero section-shell">${pageBackButton('/workspace/' + state.role, state.language === 'bn' ? 'ওয়ার্কস্পেসে ফিরে যান' : 'Back to Workspace')}<span class="section-kicker">${ui(state.language, 'library')}</span><h1>${ui(state.language, 'libraryPageHeading')}</h1><p>${ui(state.language, 'libraryPageBody')}</p><form class="public-search" data-library-search><label>${icon('search')}<span class="sr-only">${ui(state.language, 'search')}</span><input name="query" value="${escapeHtml(query)}" placeholder="${ui(state.language, 'libraryPlaceholder')}"></label><button class="button" type="submit">${ui(state.language, 'search')}</button></form><div class="filter-row" data-library-filters>${['', 'law', 'section', 'case', 'amendment', 'guide', 'update'].map((value) => `<button type="button" data-library-type="${value}" class="${value === type ? 'active' : ''}">${value ? `${value[0]?.toUpperCase()}${value.slice(1)}s` : 'All'}</button>`).join('')}</div></section><section class="section-shell results-section"><div class="result-summary"><strong data-library-count>—</strong><span>${ui(state.language, 'publishedRecords')}</span></div><div data-library-results class="record-grid"><div class="data-loading">Checking canonical records…</div></div></section></main>`;
};

const guidesPage = (): string => `
  <main id="page-content" class="inner-page">
    <section class="compact-hero section-shell">
      ${pageBackButton('/workspace/' + state.role, state.language === 'bn' ? 'ওয়ার্কস্পেসে ফিরে যান' : 'Back to Workspace')}
      <span class="section-kicker">${ui(state.language, 'citizenGuides')}</span>
      <h1>${ui(state.language, 'guidePageHeading')}</h1>
      <p>${ui(state.language, 'guidePageBody')}</p>
      <form class="public-search" data-guide-directory-search>
        <label>${icon('search')}<span class="sr-only">${ui(state.language, 'search')}</span><input name="query" placeholder="${ui(state.language, 'guidePlaceholder')}"></label>
        <button class="button" type="submit">${ui(state.language, 'search')}</button>
      </form>
    </section>
    <section class="section-shell guide-directory">
      <div class="directory-topics">
        <h2>${ui(state.language, 'topics')}</h2>
        ${guideTopics.map((topic) => `<button type="button" data-guide-cluster="${topic.value}">${topic.label}<span>${icon('arrow', 15)}</span></button>`).join('')}
      </div>
      <div class="directory-results">
        <div class="section-heading section-heading-row">
          <div>
            <span class="section-kicker">${ui(state.language, 'publishedLibrary')}</span>
            <h2>${ui(state.language, 'browseGuides')}</h2>
          </div>
          <span data-guide-count>—</span>
        </div>
        <div data-guide-results class="guide-list">
          <div class="data-loading">Loading published citizen guides…</div>
        </div>
        <button class="button button-secondary load-more" type="button" data-guide-more hidden>${ui(state.language, 'loadMore')}</button>
      </div>
    </section>
  </main>`;

const guideDetailShell = (slug: string): string => `
  <main id="page-content" class="inner-page">
    <section class="section-shell detail-loading" data-guide-detail data-slug="${escapeHtml(slug)}">
      ${pageBackButton('/guides', state.language === 'bn' ? 'সকল গাইডে ফিরে যান' : 'Back to Legal Guides')}
      <div class="data-loading">Checking the published guide and its source status…</div>
    </section>
  </main>`;

const updatesPage = (): string => `
  <main id="page-content" class="inner-page">
    <section class="compact-hero section-shell">
      ${pageBackButton('/workspace/' + state.role, state.language === 'bn' ? 'ওয়ার্কস্পেসে ফিরে যান' : 'Back to Workspace')}
      <span class="section-kicker">${ui(state.language, 'updates')}</span>
      <h1>${ui(state.language, 'updatesHeading')}</h1>
      <p>${ui(state.language, 'updatesBody')}</p>
    </section>
    <section class="section-shell results-section">
      <div data-update-results class="update-list"><div class="data-loading">Checking current update records…</div></div>
    </section>
  </main>`;

const updateDetailShell = (id: string): string => `
  <main id="page-content" class="inner-page">
    <section class="section-shell detail-loading" data-update-detail data-id="${escapeHtml(id)}">
      ${pageBackButton('/legal-updates', state.language === 'bn' ? 'সকল আপডেটে ফিরে যান' : 'Back to Updates')}
      <div class="data-loading">Checking the current update record…</div>
    </section>
  </main>`;

const trustPage = (): string => `
  <main id="page-content" class="inner-page trust-page">
    <section class="compact-hero trust-hero section-shell">
      ${pageBackButton('/', state.language === 'bn' ? 'হোমে ফিরে যান' : 'Back to Home')}
      <span class="section-kicker">Trust Method</span>
      <h1>How Justor handles legal information.</h1>
      <p>Verification language is precise because a source, a checked relationship and human review are different claims.</p>
    </section>
    <section class="section-shell trust-content">
      <div class="trust-definitions">
        <div class="trust-row"><span class="trust-term">Primary Source</span><p class="trust-def">The law, judgment, gazette or official authority itself.</p></div>
        <div class="trust-row"><span class="trust-term">Source Checked</span><p class="trust-def">The relationship between a proposition and its cited source was checked.</p></div>
        <div class="trust-row"><span class="trust-term">Human Legal Reviewed</span><p class="trust-def">That specific content version received human legal review.</p></div>
      </div>
      <article><span>Evidence insufficient</span><h2>Uncertainty stays visible</h2><p>If a reliable source or current status is unavailable, Justor should say so rather than infer a badge.</p></article>
      <section><h2>Legal update process</h2><p>Current-law checks depend on versioned legal records, amendment history and official publication data. The frontend displays only the status returned by that system.</p></section>
      <section><h2>Coverage limitations</h2><p>Coverage varies by topic and source availability. Justor does not promise completeness, absolute accuracy or zero hallucination.</p></section>
      <section><h2>Corrections</h2><p>Report an unsupported citation, outdated provision, translation problem or missing authority directly to the team.</p><a href="mailto:tajuddinahamed.contact@gmail.com?subject=Justor%20correction">Report a correction ${icon('arrow', 15)}</a></section>
      <section><h2>Privacy and limits</h2><p>Do not submit unnecessary sensitive information. Justor provides legal information and research support, not individual legal representation.</p>${route('/privacy', 'Privacy overview', 'text-link')} ${route('/disclaimer', 'Read disclaimer', 'text-link')}</section>
    </section>
  </main>`;

const aboutPage = (): string => `
  <main id="page-content" class="inner-page about-page">
    <section class="compact-hero section-shell">
      ${pageBackButton('/', state.language === 'bn' ? 'হোমে ফিরে যান' : 'Back to Home')}
      <span class="section-kicker">About Justor</span>
      <h1>Building a better interface to Bangladesh law.</h1>
      <p>Justor AI is a Bangladesh-focused legal intelligence startup building structured, source-linked tools for understanding, learning and researching law.</p>
      <div class="mission-vision"><div><strong>Mission</strong><p>Make Bangladesh law easier to access, understand, research and verify.</p></div><div><strong>Vision</strong><p>Build digital legal intelligence infrastructure for Bangladesh that improves access to legal information, legal learning and professional research.</p></div></div>
    </section>
    <nav class="about-anchor-nav" aria-label="About page sections"><a href="#about">About</a><a href="#team">Team</a><a href="#stage">Stage</a><a href="#incubation">Incubation</a><a href="#partners">Partners</a><a href="#collaborate">Collaborate</a><a href="#investors">Investors</a><a href="#contact">Contact</a></nav>
    <section class="section-shell about-block"><span class="section-kicker">What we are building</span><div class="plain-columns"><article><h2>Citizen Guidance</h2><p>Practical routes and public legal information.</p></article><article><h2>Legal Learning</h2><p>Source-linked assistance for cases, statutes and concepts.</p></article><article><h2>Professional Intelligence</h2><p>Research that keeps authority beside the analysis.</p></article><article><h2>Shared Legal Knowledge</h2><p>A structured layer across role-specific experiences.</p></article></div></section>
    <section class="about-narrative"><div class="section-shell"><span class="section-kicker section-kicker-light">Why Justor exists</span><h2>Legal information is difficult to navigate.</h2><p>Finding the legal rule that applies to a problem can require navigating dense statutes, scattered government websites and unfamiliar terminology.</p><p>Citizens often do not know where to start. Students need to connect concepts to authority. Professionals need faster ways to locate and verify relevant law.</p><p>Justor is building a more structured interface to that information.</p></div></section>
    <section class="section-shell principle-quote"><span class="section-kicker">Our product principle</span><blockquote>“Don't ask users to trust the AI. Make important legal propositions easy to verify.”</blockquote>${route('/trust', `Read our Trust Method ${icon('arrow', 16)}`, 'text-link')}</section>
    <section id="stage" class="section-shell stage-block"><div><span class="section-kicker">Where we are today</span><h2>Controlled beta</h2><p>Current work includes source-grounded AI, Citizen Authority Guides, Legal Library, professional research, student learning, Legal Updates, and Bangla + English.</p></div><div id="incubation" class="nsusn-block"><img src="/visuals/nsu-startups-next.png" alt="NSU Startups Next"><strong>Incubation</strong><h3>Justor AI is incubated at NSU Startups Next.</h3><p>Part of the NSU Startups Next incubation program, supporting the team's product development, validation and startup growth.</p></div></section>
    <section id="team" class="section-shell team-block"><span class="section-kicker">Team</span><div class="team-list"><article><div>TA</div><span>Founder & CEO</span><h2>Tajuddin Ahamed</h2><p>Leads Justor AI's product vision, company strategy and overall execution. Works across product architecture, UX design, business development, market validation and legal-tech strategy. Also contributes directly to engineering decisions alongside the CTO, translating product requirements into the platform.</p></article><article><div>MH</div><span>Co-founder & CTO</span><h2>Mehedi Hasan</h2><p>Leads Justor AI's engineering and technical development, including backend infrastructure, legal retrieval systems, AI and RAG architecture, database design and production engineering.</p></article><article><div>AS</div><span>Legal Q&A</span><h2>Anisur Rahman Sanjib</h2><p>Contributes to Justor's legal Q&A process, helping ensure legal information used in the platform's workflows is appropriately structured and checked.</p></article></div></section>
    <section class="section-shell upcoming-block"><span class="section-kicker">Upcoming</span><h2>What's coming next</h2><div class="upcoming-rows"><article><strong>For Law Students</strong><p>Exam Mode · Moot Practice · Notes · Concept Maps<br>Compare Laws · Case Navigator</p></article><article><strong>For Legal Professionals</strong><p>Document Analysis · Compare Authorities · Saved Authorities<br>Research History · Matter Workspace · Citation Workspace · Drafting Tools</p></article><article><strong>For Citizens</strong><p>Document Explanation · OCR · Complaint Tracking<br>Official Authority Routing</p></article></div><p class="roadmap-note">Roadmap features are under development and may change during beta.</p></section>
    <section id="collaborate" class="work-with"><div class="section-shell"><span class="section-kicker section-kicker-light">Work with Justor</span><h2>Build Bangladesh legal intelligence with us.</h2><div class="collaboration-tracks"><a href="mailto:tajuddinahamed.contact@gmail.com?subject=Academic%20collaboration">Universities &amp; Academic Institutions</a><a href="mailto:tajuddinahamed.contact@gmail.com?subject=Legal%20professional%20collaboration">Law Firms &amp; Legal Professionals</a><a href="mailto:tajuddinahamed.contact@gmail.com?subject=Media%20collaboration">Media &amp; Publishers</a><a href="mailto:tajuddinahamed.contact@gmail.com?subject=Technology%20collaboration">Technology &amp; Ecosystem</a></div></div></section>
    <section id="partners" class="section-shell about-partners-block">
      <span class="section-kicker">Official Partners &amp; Collaborations</span>
      <h2>Institutions we work with</h2>
      <p>Justor AI is proud to collaborate with established legal and ecosystem partners shaping Bangladesh's legal intelligence future.</p>
      <div class="about-partners-grid">
        <a class="about-partner-card" href="https://habiganjbar.com.bd" target="_blank" rel="noopener">
          <div class="about-partner-logo-wrap">
            <img src="/visuals/habiganj-bar-council.jpg" alt="Habiganj Bar Council" class="about-partner-logo">
          </div>
          <div class="about-partner-body">
            <span class="about-partner-type">Official Legal Partner</span>
            <h3>Habiganj Bar Council</h3>
            <p>Habiganj Jela Advocates Association — collaborating with JustorAI to build tailored legal research tools for practicing advocates in Bangladesh.</p>
            <span class="about-partner-url">habiganjbar.com.bd ${icon('external', 13)}</span>
          </div>
        </a>
        <a class="about-partner-card" href="https://iexf.org" target="_blank" rel="noopener">
          <div class="about-partner-logo-wrap about-partner-logo-wrap-light">
            <img src="/visuals/iexf-logo.jpg" alt="IEXF" class="about-partner-logo about-partner-logo-iexf">
          </div>
          <div class="about-partner-body">
            <span class="about-partner-type">Official Community &amp; Ecosystem Partner</span>
            <h3>IEXF</h3>
            <p>IEXF is an official community and ecosystem partner of Justor AI — connecting, collaborating and growing the legal tech ecosystem together.</p>
            <span class="about-partner-url">iexf.org ${icon('external', 13)}</span>
          </div>
        </a>
      </div>
    </section>
    <section id="investors" class="section-shell investor-block"><span class="section-kicker">For Investors & Strategic Partners</span><h2>Legal intelligence infrastructure focused on Bangladesh.</h2><p>We welcome conversations with investors and strategic partners interested in LegalTech, AI infrastructure, emerging markets and access to legal information.</p><dl class="investor-grid"><div><dt>Product</dt><dd>Role-specific legal intelligence</dd></div><div><dt>Market</dt><dd>Bangladesh legal-information, learning and professional workflows</dd></div><div><dt>Infrastructure</dt><dd>Structured legal knowledge + AI</dd></div><div><dt>Stage</dt><dd>Controlled Beta</dd></div></dl><div class="button-row"><a class="button" href="mailto:tajuddinahamed.contact@gmail.com?subject=Investor%20inquiry">Investor Inquiry</a><a class="button button-secondary" href="mailto:tajuddinahamed.contact@gmail.com?subject=Contact%20founder">Contact Founder</a></div><a href="tel:+8801764662967">+880 1764-662967</a></section>
    <section id="contact" class="section-shell direct-contact"><span class="section-kicker">Contact</span><h2>Start the right conversation.</h2><a href="mailto:tajuddinahamed.contact@gmail.com">tajuddinahamed.contact@gmail.com</a><a href="tel:+8801764662967">+880 1764-662967</a></section>
  </main>`;

const contactPage = (): string => `
  <main id="page-content" class="inner-page">
    <section class="compact-hero section-shell">
      ${pageBackButton('/', state.language === 'bn' ? 'হোমে ফিরে যান' : 'Back to Home')}
      <span class="section-kicker">Contact Justor</span>
      <h1>Start the right conversation.</h1>
      <p>Choose a direct route to the team. No submission is silently stored by this page.</p>
    </section>
    <section class="section-shell contact-options">
      <a href="mailto:tajuddinahamed.contact@gmail.com"><span>Email</span><strong>tajuddinahamed.contact@gmail.com</strong>${icon('arrow', 16)}</a>
      <a href="tel:+8801764662967"><span>Phone</span><strong>+880 1764-662967</strong>${icon('arrow', 16)}</a>
      <a href="https://wa.me/8801764662967" target="_blank" rel="noopener"><span>WhatsApp</span><strong>Open a conversation</strong>${icon('external', 16)}</a>
      <a href="https://docs.google.com/forms/d/e/1FAIpQLSdMfVydj2kMXZkf3SpYi_soA37YtTmAIB7VquPNkadYOmLSrg/viewform" target="_blank" rel="noopener"><span>User Survey</span><strong>Share your product feedback</strong>${icon('external', 16)}</a>
    </section>
    <section class="section-shell inquiry-links">
      <h2>Inquiry type</h2>
      <div>
        <a href="mailto:tajuddinahamed.contact@gmail.com?subject=University%20partnership">University partnership</a>
        <a href="mailto:tajuddinahamed.contact@gmail.com?subject=Legal%20collaboration">Legal collaboration</a>
        <a href="mailto:tajuddinahamed.contact@gmail.com?subject=Media%20inquiry">Media & press</a>
        <a href="mailto:tajuddinahamed.contact@gmail.com?subject=Investor%20inquiry">Investor / strategic partnership</a>
        <a href="https://docs.google.com/forms/d/e/1FAIpQLSdMfVydj2kMXZkf3SpYi_soA37YtTmAIB7VquPNkadYOmLSrg/viewform" target="_blank" rel="noopener">Product Feedback Survey ↗</a>
      </div>
    </section>
  </main>`;

const loginPage = (): string => {
  const next = new URLSearchParams(window.location.search).get('next') || localizedPath('/choose-role', state.language);
  const isBn = state.language === 'bn';
  return `
  <main id="page-content" class="login-page">
    <!-- Left Hero & Brand Column -->
    <section class="login-brand-panel">
      <div class="login-brand-top">
        <a href="${localizedPath('/', state.language)}" data-route class="login-logo-link">${brand(true)}</a>
        <div class="login-badge-pill">
          <span class="badge-dot"></span>
          <span>${isBn ? 'নিয়ন্ত্রিত বেটা সংস্করণ · বাংলাদেশ আইন' : 'Controlled Beta · Bangladesh Legal Intelligence'}</span>
        </div>
      </div>
      
      <div class="login-brand-hero">
        <span class="section-kicker section-kicker-light">${isBn ? 'আইনি বুদ্ধিমত্তা প্ল্যাটফর্ম' : 'Source-Grounded Legal Intelligence'}</span>
        <h1>${isBn ? 'আইনি গবেষণায় কর্তৃত্বপূর্ণ নির্ভুলতা ও গতি।' : 'Legal Intelligence Grounded on Authority.'}</h1>
        <p class="login-hero-desc">
          ${isBn 
            ? 'আইনজীবী, শিক্ষার্থী এবং সাধারণ নাগরিকদের জন্য সুপ্রিম কোর্টের নজির ও বাংলাদেশ গেজেটের উপর ভিত্তি করে তৈরি ডিজিটাল লিগ্যাল প্ল্যাটফর্ম।' 
            : 'Access source-linked statutory intelligence, Supreme Court precedents, and citizen guidance tailored for Bangladesh.'}
        </p>
        
        <div class="login-feature-list">
          <div class="login-feature-card">
            <span class="login-feature-icon">⚖️</span>
            <div>
              <strong>${isBn ? 'উৎস যাচাইকৃত কর্তৃত্ব' : 'Primary Source Grounding'}</strong>
              <p>${isBn ? 'প্রত্যেকটি বিশ্লেষণ মূল আইন ও গেজেটের সাথে যুক্ত।' : 'Direct citations to controlling Acts, Sections & DLR precedents.'}</p>
            </div>
          </div>
          <div class="login-feature-card">
            <span class="login-feature-icon">⚡</span>
            <div>
              <strong>${isBn ? 'তিনটি নির্দিষ্ট ওয়ার্কস্পেস' : 'One locked workspace'}</strong>
              <p>${isBn ? 'সাইন আপের পর আপনার রোল বেছে নিন — পরে পরিবর্তন করা যাবে না।' : 'After sign-up you choose Citizen, Student, or Professional. That role stays locked.'}</p>
            </div>
          </div>
          <div class="login-feature-card">
            <span class="login-feature-icon">🛡️</span>
            <div>
              <strong>${isBn ? 'চেম্বার গোপনীয়তা নীতি' : 'Chambers-Grade Privacy'}</strong>
              <p>${isBn ? 'অনুসন্ধান তথ্য নিরাপদ এবং অনুমতি ছাড়া সংরক্ষিত হয় না।' : 'Zero data retention on search queries for confidential research.'}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="login-brand-footer">
        <div class="login-incubation-badge">
          <img src="/visuals/nsu-startups-next.png" alt="NSU Startups Next">
          <span>Incubated at <strong>NSU Startups Next</strong></span>
        </div>
      </div>
    </section>

    <!-- Right Authentication Box Column -->
    <section class="login-auth-panel">
      <div class="login-auth-topbar">
        ${pageBackButton('/', isBn ? 'হোমে ফিরে যান' : 'Back to Home')}
        <button class="language-switch login-language-btn" type="button" data-action="language" aria-label="Switch language">${ui(state.language, 'language')}</button>
      </div>

      <div class="login-card-box">
        <div class="login-card-header">
          <span class="section-kicker">${ui(state.language, 'signInOrSignUp')}</span>
          <h2>${isBn ? 'জাস্টরে প্রবেশ করুন' : 'Welcome to Justor AI'}</h2>
          <p>${isBn ? 'গুগল দিয়ে সাইন ইন বা সাইন আপ করুন। এরপর আপনার রোল বেছে নিলে চ্যাটে যেতে পারবেন।' : 'Sign in or create an account with Google. Next you will choose a role, then enter your chat workspace.'}</p>
        </div>

        <div class="login-action-group">
          <button class="button google-sign-btn" type="button" data-action="google-sign-in" data-next="${escapeHtml(next)}">
            <svg class="google-icon" width="20" height="20" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.616z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
            </svg>
            <span>${ui(state.language, 'continueGoogle')}</span>
          </button>
          <p class="login-google-hint">${isBn ? 'নতুন অ্যাকাউন্টও একই বাটনে তৈরি হয়।' : 'New accounts are created with the same Google button.'}</p>

          <div class="auth-divider">
            <span>${isBn ? 'অথবা অ্যাকাউন্ট ছাড়া টেস্ট করুন' : 'or test immediately without account'}</span>
          </div>

          <button class="guest-sign-card" type="button" data-action="guest-sign-in" data-next="${escapeHtml(next)}">
            <div class="guest-card-left">
              <span class="guest-icon-badge">${icon('user', 18)}</span>
              <div>
                <strong>${isBn ? 'গেস্ট হিসেবে প্রবেশ করুন' : 'Continue as Guest'}</strong>
                <p>${isBn ? 'রোল বেছে নিয়ে চ্যাট টেস্ট করুন' : 'Choose a role, then try the chat workspace'}</p>
              </div>
            </div>
            <span class="guest-arrow">${icon('arrow', 16)}</span>
          </button>
        </div>

        <div class="login-card-footer">
          <div class="login-privacy-badge">
            ${icon('shield', 14)}
            <span>${isBn ? 'পাবলিক গাইড এবং আইনের তালিকা সর্বদা বিনামূল্যে উন্মুক্ত।' : 'Public statutory records and citizen guides are openly accessible.'}</span>
          </div>
          <div class="login-policy-links">
            ${route('/privacy', isBn ? 'গোপনীয়তা' : 'Privacy', 'policy-link')} ·
            ${route('/terms', isBn ? 'ব্যবহারের শর্তাবলী' : 'Terms', 'policy-link')} ·
            ${route('/disclaimer', isBn ? 'দাবিত্যাগ' : 'Disclaimer', 'policy-link')}
          </div>
        </div>
      </div>
    </section>
  </main>`;
};

const onboardPage = (): string => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';
  const isBn = state.language === 'bn';

  return `
  <main id="page-content" class="login-page onboard-page">
    <!-- Left Hero & Brand Column -->
    <section class="login-brand-panel">
      <div class="login-brand-top">
        <a href="${localizedPath('/', state.language)}" data-route class="login-logo-link">${brand(true)}</a>
        <div class="login-badge-pill" style="border-color: #10b981; color: #10b981;">
          <span class="badge-dot" style="background: #10b981;"></span>
          <span>${isBn ? 'হোয়াটসঅ্যাপ ভেরিফাইড কানেকশন' : 'WhatsApp Verified Connection'}</span>
        </div>
      </div>
      
      <div class="login-brand-hero">
        <span class="section-kicker section-kicker-light">${isBn ? 'আইনজীবী চেম্বার অ্যাক্টিভেশন' : 'Advocate Chamber Activation'}</span>
        <h1>${isBn ? 'আপনার চেম্বার চালু করুন — হোয়াটসঅ্যাপে সার্বক্ষণিক এআই অ্যাসিস্ট্যান্ট।' : 'Activate Your Chamber — 24/7 AI Legal Assistant on WhatsApp.'}</h1>
        <p class="login-hero-desc">
          ${isBn 
            ? 'আপনার হোয়াটসঅ্যাপ নম্বরটি সফলভাবে সংযুক্ত হতে প্রস্তুত। চেম্বারের নাম ও তথ্য দিয়ে চালু করলেই সরাসরি হোয়াটসঅ্যাপে কোর্ট ডকেট, ড্রাফটিং ও কজলিস্ট ব্রিফিং পাবেন।' 
            : 'Your WhatsApp number is ready for activation. Enter your chamber details to unlock instant corridor dictation, trial cross-exam, and daily cause-list briefs on WhatsApp.'}
        </p>
        
        <div class="login-feature-list">
          <div class="login-feature-card">
            <span class="login-feature-icon">📱</span>
            <div>
              <strong>${isBn ? '১-ক্লিকে ফোন নম্বর লিঙ্ক' : 'Zero-Password Instant Sync'}</strong>
              <p>${isBn ? 'হোয়াটসঅ্যাপ মেসেজ থেকেই আপনার নম্বরটি সরাসরি যাচাই করা হয়েছে।' : 'Pre-verified from your WhatsApp handshake. No complex OTPs needed.'}</p>
            </div>
          </div>
          <div class="login-feature-card">
            <span class="login-feature-icon">⚖️</span>
            <div>
              <strong>${isBn ? 'বাংলাদেশ সুপ্রিম কোর্ট ও জেলা আদালত' : 'Bangladesh Courts Spec'}</strong>
              <p>${isBn ? 'দেওয়ানি, ফৌজদারি ও রিট মামলার বিশেষায়িত ড্রাফটিং ও তামাদি গণক।' : 'Specialized CPC/CrPC drafting, Limitation Act 1908, and DLR precedents.'}</p>
            </div>
          </div>
          <div class="login-feature-card">
            <span class="login-feature-icon">🛡️</span>
            <div>
              <strong>${isBn ? 'চেম্বার গোপনীয়তা ও এনক্রিপশন' : 'Enterprise Chamber Isolation'}</strong>
              <p>${isBn ? 'আপনার চেম্বারের মামলা ও মক্কেলের তথ্য সম্পূর্ণ আলাদা ও সুরক্ষিত।' : 'Multi-tenant docket encryption. Zero cross-chamber data leaks.'}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="login-brand-footer">
        <div class="login-incubation-badge">
          <span>Justor AI — Empowering Advocates Across Bangladesh</span>
        </div>
      </div>
    </section>

    <!-- Right Authentication / Onboarding Box -->
    <section class="login-auth-panel">
      <div class="login-auth-topbar">
        ${pageBackButton('/', isBn ? 'হোমে ফিরে যান' : 'Back to Home')}
        <button class="language-switch login-language-btn" type="button" data-action="language" aria-label="Switch language">${ui(state.language, 'language')}</button>
      </div>

      <div class="login-card-box" id="onboard-card-container">
        <div class="login-card-header">
          <span class="section-kicker" style="color: #10b981;">● ${isBn ? 'হোয়াটসঅ্যাপ থেকে আগমন' : 'Verified WhatsApp Source'}</span>
          <h2>${isBn ? 'চেম্বার প্রোফাইল তৈরি করুন' : 'Activate Chamber Profile'}</h2>
          <p>${isBn ? 'মাত্র ৩০ সেকেন্ডে নিচের তথ্যগুলো দিন — সক্রিয় হওয়ার সাথে সাথে হোয়াটসঅ্যাপে নোটিফিকেশন পাবেন।' : 'Complete this quick 30-second setup. Your WhatsApp bot will activate immediately.'}</p>
        </div>

        <form class="onboard-form" data-action="submit-onboard-form" style="display: flex; flex-direction: column; gap: 14px; margin-top: 12px;">
          <input type="hidden" name="token" value="${escapeHtml(token)}" />

          <div class="form-group" style="display: flex; flex-direction: column; gap: 4px;">
            <label style="font-size: 13px; font-weight: 600; color: var(--text-secondary, #94a3b8);">
              ${isBn ? 'যাচাইকৃত হোয়াটসঅ্যাপ নম্বর' : 'Verified WhatsApp Number'}
            </label>
            <div id="onboard-phone-badge" style="padding: 10px 14px; background: rgba(16, 185, 129, 0.1); border: 1px solid rgba(16, 185, 129, 0.3); border-radius: 8px; color: #10b981; font-weight: 600; display: flex; align-items: center; gap: 8px;">
              <span>🟢</span>
              <span id="onboard-phone-display">${isBn ? 'যাচাই করা হচ্ছে...' : 'Validating token...'}</span>
            </div>
          </div>

          <div class="form-group" style="display: flex; flex-direction: column; gap: 4px;">
            <label for="onboard-name" style="font-size: 13px; font-weight: 600; color: var(--text-primary, #e2e8f0);">
              ${isBn ? 'বিজ্ঞ আইনজীবীর পুরো নাম *' : 'Advocate Full Name *'}
            </label>
            <input type="text" id="onboard-name" name="full_name" required placeholder="${isBn ? 'যেমন: Advocate Mehide Hasan' : 'e.g. Advocate Mehide Hasan'}" style="padding: 11px 14px; border-radius: 8px; border: 1px solid var(--border-color, #334155); background: var(--bg-primary, #0f172a); color: #fff; font-size: 14px;" />
          </div>

          <div class="form-group" style="display: flex; flex-direction: column; gap: 4px;">
            <label for="onboard-chamber" style="font-size: 13px; font-weight: 600; color: var(--text-primary, #e2e8f0);">
              ${isBn ? 'চেম্বার / ফার্মের নাম *' : 'Chamber / Law Firm Name *'}
            </label>
            <input type="text" id="onboard-chamber" name="chamber_name" required placeholder="${isBn ? 'যেমন: Justor Law Chambers' : 'e.g. Justor Law Chambers'}" style="padding: 11px 14px; border-radius: 8px; border: 1px solid var(--border-color, #334155); background: var(--bg-primary, #0f172a); color: #fff; font-size: 14px;" />
          </div>

          <div class="form-group" style="display: flex; flex-direction: column; gap: 4px;">
            <label for="onboard-bar" style="font-size: 13px; font-weight: 600; color: var(--text-primary, #e2e8f0);">
              ${isBn ? 'বার অ্যাসোসিয়েশন / আদালত (ঐচ্ছিক)' : 'Bar Association / Court (Optional)'}
            </label>
            <input type="text" id="onboard-bar" name="bar_roll" placeholder="${isBn ? 'যেমন: Supreme Court Bar / Dhaka Bar' : 'e.g. Supreme Court Bar Association'}" style="padding: 11px 14px; border-radius: 8px; border: 1px solid var(--border-color, #334155); background: var(--bg-primary, #0f172a); color: #fff; font-size: 14px;" />
          </div>

          <div class="form-group" style="display: flex; flex-direction: column; gap: 4px;">
            <label for="onboard-email" style="font-size: 13px; font-weight: 600; color: var(--text-secondary, #94a3b8);">
              ${isBn ? 'ইমেইল অ্যাড্রেস (ওয়েব লগইনের জন্য ঐচ্ছিক)' : 'Email Address (Optional for web login)'}
            </label>
            <input type="email" id="onboard-email" name="email" placeholder="advocate@example.com" style="padding: 11px 14px; border-radius: 8px; border: 1px solid var(--border-color, #334155); background: var(--bg-primary, #0f172a); color: #fff; font-size: 14px;" />
          </div>

          <button class="button" type="submit" id="onboard-submit-btn" style="margin-top: 8px; padding: 13px 20px; font-size: 15px; font-weight: 700; background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: #fff; border: none; border-radius: 8px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;">
            <span>🚀</span>
            <span>${isBn ? 'চেম্বার চালু করুন ও হোয়াটসঅ্যাপ কানেক্ট করুন' : 'Activate Chamber & Connect WhatsApp'}</span>
          </button>
        </form>

        <div id="onboard-status-msg" style="margin-top: 14px; display: none;"></div>

        <div class="login-card-footer" style="margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--border-color, #232B3E);">
          <div class="login-privacy-badge">
            ${icon('shield', 14)}
            <span>${isBn ? 'আপনার নম্বর ও মামলা তথ্য সর্বোচ্চ গোপনীয়তায় সংরক্ষিত।' : 'Your number and client files are strictly confidential and encrypted.'}</span>
          </div>
        </div>
      </div>
    </section>
  </main>
  `;
};

const chooseRolePage = (): string => {
  if (!state.session) {
    return loginPage();
  }
  if (isRoleLocked()) {
    return `<main id="page-content" class="choose-role-page"><p class="choose-role-redirect">Opening your workspace…</p></main>`;
  }
  const isBn = state.language === 'bn';
  return `
  <main id="page-content" class="choose-role-page">
    <header class="choose-role-header">
      ${route('/', brand(), 'brand-link')}
      <button class="language-switch" type="button" data-action="language">${ui(state.language, 'language')}</button>
    </header>
    <section class="choose-role-card">
      <span class="section-kicker">${ui(state.language, 'signUp')}</span>
      <h1>${ui(state.language, 'chooseRoleHeading')}</h1>
      <p>${isBn ? 'আপনার পেশা অনুযায়ী রোল বেছে নিন। এই রোল আপনার অ্যাকাউন্টে লক হয়ে যাবে।' : 'Pick your role. This is locked to your account and cannot be changed later.'}</p>
      <div class="choose-role-grid">
        ${publicRoleOrder.map((role) => `
          <button type="button" class="choose-role-option" data-action="lock-role" data-role="${role}">
            <span class="role-label">${localizedRoleLabel(role)}</span>
            <strong>${rolePromise(role)}</strong>
            <span>${roleBody(role)}</span>
          </button>
        `).join('')}
      </div>
      <div class="choose-role-guide-note">
        <span>${isBn ? '📋 পাবলিক আইনি রেফারেন্স ও গাইড দেখতে চান?' : '📋 Looking for public statutory records & guides?'}</span>
        ${route('/guides', isBn ? 'আইনি গাইড ব্রাউজ করুন →' : 'Browse Legal Guides →', 'link-arrow')}
      </div>
    </section>
  </main>`;
};

const policyPage = (kind: 'privacy' | 'terms' | 'disclaimer'): string => {
  const pages = {
    privacy: ['Privacy overview', 'Justor should collect only the information needed to provide the service, protect accounts, measure quality and meet legal obligations.', [['Information you provide', 'Queries, account information and messages may be processed when you use connected features. Avoid adding unnecessary sensitive information.'], ['Retention and control', 'Production retention, access, correction and deletion rules depend on the connected account and data systems.'], ['Contact', 'Privacy questions can be sent to the verified project email.']]],
    terms: ['Controlled-beta terms', 'The beta is provided for lawful legal information, research and learning workflows.', [['Permitted use', 'Do not misuse the service, attempt unauthorized access or present an AI output as verified authority without checking its sources.'], ['Coverage', 'Availability and completeness vary by source, jurisdictional topic and connected system state.'], ['Changes', 'The product and these beta terms may change as testing continues.']]],
    disclaimer: ['Legal information disclaimer', 'Justor provides legal information and research support, not individual legal representation.', [['Not legal advice', 'Using Justor does not create a lawyer-client relationship or replace advice about your facts.'], ['Verify before acting', 'Open the cited authority and obtain professional help when rights, deadlines, money, liberty or safety are at risk.'], ['Urgent matters', 'Do not rely on an AI interface for an emergency, arrest, imminent deadline or immediate threat.']]],
  } as const;
  const [title, intro, sections] = pages[kind];
  return `
  <main id="page-content" class="inner-page">
    <section class="compact-hero section-shell">
      ${pageBackButton('/', state.language === 'bn' ? 'হোমে ফিরে যান' : 'Back to Home')}
      <span class="section-kicker">Legal & product notice</span>
      <h1>${title}</h1>
      <p>${intro}</p>
    </section>
    <article class="section-shell policy-content">${sections.map(([heading, body]) => `<section><h2>${heading}</h2><p>${body}</p></section>`).join('')}<p>Questions: <a href="mailto:tajuddinahamed.contact@gmail.com">tajuddinahamed.contact@gmail.com</a></p></article>
  </main>`;
};

interface CareerRole {
  tag: string;
  tagClass: string;
  title: string;
  body: string;
  tasks: string[];
  meta: string;
}

const careerRoles: CareerRole[] = [
  {
    tag: 'Legal',
    tagClass: 'badge-legal',
    title: 'Legal Research Intern',
    body: 'You are the quality gate. Every answer Justor AI gives must be grounded in verified Bangladeshi law. You review, fact-check, and help us close the gap between what the model says and what the law actually says. This is the most important role on the team right now.',
    tasks: [
      'Review AI-generated legal answers against primary statute text',
      'Flag citation errors, outdated provisions, and missing authorities',
      'Research and summarise key Acts for the knowledge corpus',
      'Help write and verify the bite-size legal learning card library',
      'Support Bangla legal terminology accuracy checks',
    ],
    meta: 'Law student · Any year · Passion over credentials · 14–25 hrs/week',
  },
  {
    tag: 'Legal',
    tagClass: 'badge-legal',
    title: 'Legal Content Writer',
    body: 'Bangladesh has a massive backlog of litigations and manual case preparation. You change that. You write authoritative legal explainers, statutory guides, and learning content that simplifies research for advocates and students.',
    tasks: [
      'Write authoritative guides on procedural law, land enactments, civil appeals, and criminal procedure',
      'Script the bite-size Contract Act and other learning card series',
      'Translate complex legal provisions into plain Bangla and English',
      'Fact-check all content against verified statute text and Supreme Court precedents',
      'Research real case scenarios that advocates and students face',
    ],
    meta: 'Law student preferred · Strong Bangla writing essential · 14–25 hrs/week',
  },
  {
    tag: 'Design',
    tagClass: 'badge-design',
    title: 'Product Designer',
    body: 'Justor serves two core personas — lawyers and law students. Each needs an uncompromisingly professional interface. You think in user journeys, not just screens, and you understand that in legal tech, clarity is a safety feature.',
    tasks: [
      'Design and iterate on workspace UX for Chamber OS, matter intelligence, and student modes',
      'Create intuitive precedent search and clause comparison layouts',
      'Build and maintain a component library in Figma',
      'Run informal usability tests with advocates and legal practitioners',
      'Work directly with CTO on frontend implementation handoff',
    ],
    meta: 'Basic Figma knowledge enough · We will teach the rest · 14–25 hrs/week',
  },
  {
    tag: 'Brand',
    tagClass: 'badge-brand',
    title: 'Brand Visualiser',
    body: "Justor AI's visual identity needs to feel like authority — not a startup trying to look authoritative. You create graphics, templates, and visual assets that make people trust us before they read a single word.",
    tasks: [
      'Design pitch deck slides, one-pagers, and investor materials',
      'Create social media templates aligned with brand DNA',
      'Build visual assets for product launches and announcements',
      'Develop iconography consistent with the legal corpus UI',
      'Maintain and evolve Justor AI brand guidelines document',
    ],
    meta: 'Canva / Figma / any design tool · Show us anything you have made · 14–25 hrs/week',
  },
  {
    tag: 'Data',
    tagClass: 'badge-data',
    title: 'Data & Research Analyst',
    body: 'We run legal accuracy benchmarks and need someone to make sense of the results. No advanced skills required — just curiosity, attention to detail, and the willingness to learn. AI tools handle the hard parts. You handle the thinking.',
    tasks: [
      'Run and record results from the legal accuracy benchmark test suite',
      'Spot patterns in where the model fails — by topic, language, query type',
      'Help track citation accuracy and domain classification over time',
      'Research comparable legal AI products globally',
      'Prepare readable summaries from raw data for team decisions',
    ],
    meta: 'Any background · No coding required · Curiosity is the skill · 14–25 hrs/week',
  },
  {
    tag: 'Content',
    tagClass: 'badge-content',
    title: 'Reels & Legal Content Creator',
    body: 'Two things need to be made: content about Justor AI as a product, and content about Bangladeshi law that makes people stop scrolling. Laws about land disputes, inheritance, worker rights, digital fraud — these stories are everywhere. You find them, script them, and make them impossible to ignore.',
    tasks: [
      'Script and produce short Reels explaining real Bangladeshi laws in plain language',
      'Create Justor AI product reels — demos, features, behind-the-scenes',
      'Make bilingual (EN/BN) carousel posts on statutory precedents and court procedures',
      'Research interesting, surprising, or alarming laws for content hooks',
      'Collaborate with legal intern to verify every legal fact before publishing',
    ],
    meta: 'Any video editor · Phone camera is fine · Content ideas matter more · 14–25 hrs/week',
  },
  {
    tag: 'Marketing',
    tagClass: 'badge-marketing',
    title: 'Marketing & Comms Intern',
    body: 'We are incubated, we have bar council relationships, and we are raising a pre-seed round. You help us tell that story — to users, to press, to funders, and to the legal community who needs to know we exist.',
    tasks: [
      'Write and distribute press releases and product announcements',
      'Manage and grow LinkedIn, Facebook, and X presence',
      'Research and draft applications for grants and accelerator programs',
      'Build outreach lists for bar councils, universities, and legal aid orgs',
      'Track weekly reach, engagement, and conversion metrics',
    ],
    meta: 'Strong Bangla and English writing · Passion for the mission · 14–25 hrs/week',
  },
  {
    tag: 'R&D',
    tagClass: 'badge-rd',
    title: 'Research & Development Intern',
    body: 'Legal tech in Bangladesh is years behind where it needs to be. You research what is being built globally, what our users actually need locally, and what problems we should be solving in 12 months that we are not solving today. You write memos. We make decisions from them.',
    tasks: [
      'Map global legal AI products and identify what they do better or worse',
      'Conduct user interviews with advocates, chamber counsel, and law students',
      'Research Bangladesh legal aid gaps using DBLA, bar council, and court data',
      'Write short internal research memos that inform product decisions',
      'Track papers and news on RAG, legal AI accuracy, and LLM reasoning',
    ],
    meta: 'Any discipline · Writing clarity is the only hard requirement · 14–25 hrs/week',
  },
];

const careersPage = (): string => {
  const isBn = state.language === 'bn';
  return `
  <main id="page-content" class="careers-page">
    <!-- Hero Section -->
    <section class="careers-hero">
      <div class="careers-hero-inner">
        ${pageBackButton('/', isBn ? 'হোমে ফিরে যান' : 'Back to Home')}
        <div class="careers-kicker-badge">
          <span class="careers-kicker-dot"></span>
          <span>Careers at Justor AI</span>
        </div>
        <h1 class="careers-h1">Build the future of Bangladesh law.</h1>
        <p class="careers-subheading">
          We are a small team building Bangladesh's authority-first bilingual legal intelligence platform — incubated at NSU Startups Next, part of the IEXF startup ecosystem, and trusted by bar councils. Join us before we scale.
        </p>

        <!-- Stats Row -->
        <div class="careers-stats-grid">
          <div class="career-stat-card">
            <strong class="stat-value">8+</strong>
            <span class="stat-label">Open intern roles</span>
          </div>
          <div class="career-stat-card">
            <strong class="stat-value">Remote</strong>
            <span class="stat-label">Work arrangement</span>
          </div>
          <div class="career-stat-card">
            <strong class="stat-value">Unpaid</strong>
            <span class="stat-label">With certification</span>
          </div>
          <div class="career-stat-card">
            <strong class="stat-value">Dhaka</strong>
            <span class="stat-label">51 Madani Avenue</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Perks Strip (Blue bar #1E38C8 full width) -->
    <section class="careers-perks-strip">
      <div class="careers-perks-inner">
        <div class="perks-heading">
          <span>What interns get <span aria-hidden="true">→</span></span>
        </div>
        <div class="perks-list">
          <div class="perk-item">Founding contributor title</div>
          <div class="perk-item">Product credit listing</div>
          <div class="perk-item">Certification letter</div>
          <div class="perk-item">Reference from founder</div>
          <div class="perk-item">IEXF startup ecosystem access</div>
          <div class="perk-item">Real work that ships to real users</div>
          <div class="perk-item">First consideration for full-time roles</div>
          <div class="perk-item">Your ideas get evaluated and potentially built</div>
        </div>
      </div>
    </section>

    <!-- Open Roles Section -->
    <section class="careers-roles-section">
      <div class="careers-section-container">
        <div class="careers-roles-header">
          <div>
            <span class="section-kicker">Opportunities</span>
            <h2 class="section-title">Open Intern Roles</h2>
          </div>
          <div class="roles-header-meta">
            <span>8 positions · Remote · Unpaid with certification · 14–25 hrs/week</span>
          </div>
        </div>

        <div class="careers-roles-grid">
          ${careerRoles.map((role) => `
            <article class="career-role-card">
              <div class="role-card-top">
                <span class="role-tag-badge ${role.tagClass}">${escapeHtml(role.tag)}</span>
                <a href="mailto:contact@justorai.com?subject=${encodeURIComponent(`Internship Application — ${role.title}`)}" class="role-apply-pill">
                  Apply for Role <span aria-hidden="true">↗</span>
                </a>
              </div>
              <h3 class="role-card-title">${escapeHtml(role.title)}</h3>
              <p class="role-card-body">${escapeHtml(role.body)}</p>
              
              <div class="role-tasks-wrapper">
                <strong class="role-tasks-heading">Key responsibilities:</strong>
                <ul class="role-tasks-list">
                  ${role.tasks.map((task) => `<li><span class="task-arrow">→</span> <span>${escapeHtml(task)}</span></li>`).join('')}
                </ul>
              </div>

              <div class="role-card-footer">
                <span class="role-meta-text">${escapeHtml(role.meta)}</span>
              </div>
            </article>
          `).join('')}
        </div>
      </div>
    </section>

    <!-- How to Apply (4 Steps) -->
    <section class="careers-apply-section">
      <div class="careers-section-container">
        <div class="careers-apply-header">
          <span class="section-kicker">Application Process</span>
          <h2 class="section-title">How to Apply</h2>
          <p class="section-subtext">Four straightforward steps from application to shipping real work.</p>
        </div>

        <div class="careers-steps-grid">
          <div class="career-step-card">
            <div class="step-number-badge">01</div>
            <h3 class="step-title">Send your CV</h3>
            <p class="step-body">Email your CV and one paragraph on why this role to <strong>contact@justorai.com</strong>. No formal cover letter needed.</p>
          </div>
          <div class="career-step-card">
            <div class="step-number-badge">02</div>
            <h3 class="step-title">Show us something you have made</h3>
            <p class="step-body">A design, a video, a piece of writing, a research note — anything. No prior experience required, but show us you can make things.</p>
          </div>
          <div class="career-step-card">
            <div class="step-number-badge">03</div>
            <h3 class="step-title">Brief call with founder</h3>
            <p class="step-body">20 minutes with Taj. We talk about the product, your role, and fit.</p>
          </div>
          <div class="career-step-card">
            <div class="step-number-badge">04</div>
            <h3 class="step-title">Start on a real task</h3>
            <p class="step-body">No probation. Week one you work on something real. Remote-first. Flexible hours. 14–25 hrs/week commitment.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Email CTA Block -->
    <section class="careers-cta-section">
      <div class="careers-section-container">
        <div class="careers-cta-card">
          <h2 class="cta-heading">Ready to apply?</h2>
          <p class="cta-subheading">Send your CV and a short paragraph to our founding team.</p>
          
          <div class="cta-email-action">
            <a href="mailto:contact@justorai.com?subject=Internship%20Application" class="cta-email-button">
              ${icon('mail', 18)} <span>contact@justorai.com</span>
            </a>
          </div>

          <div class="cta-meta-bar">
            <div class="cta-meta-item">
              <span class="meta-label">Subject Format:</span>
              <code>Internship Application — [Role Name]</code>
            </div>
            <div class="cta-meta-divider"></div>
            <div class="cta-meta-item">
              <span class="meta-label">Incubator & Office:</span>
              <span>Grameen Banglar Akshay Tower, 3rd Floor, G-407 · 51 Madani Avenue, Dhaka · NSU Startups Next Cohort 4 · IEXF</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>`;
};

const notFoundPage = (): string => `<main id="page-content" class="not-found"><span class="badge">404</span><h1>This legal path was not found.</h1><p>The page may have moved or may not be part of the published beta.</p>${route('/legal-library', `Search Legal Library ${icon('arrow', 16)}`, 'button')}</main>`;

const hydrateHeroVisual = (): void => {
  const canvas = document.querySelector<HTMLCanvasElement>('.hero-3d-static');
  const source = canvas?.dataset.source;
  const context = canvas?.getContext('2d');
  if (!canvas || !source || !context) return;
  const image = new Image();
  image.decoding = 'async';
  image.fetchPriority = 'low';
  image.addEventListener('load', () => {
    const ratio = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.round(canvas.clientWidth * ratio));
    const height = Math.max(1, Math.round(canvas.clientHeight * ratio));
    canvas.width = width;
    canvas.height = height;
    const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
    const drawWidth = image.naturalWidth * scale;
    const drawHeight = image.naturalHeight * scale;
    context.drawImage(image, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
    canvas.classList.add('is-ready');
  }, { once: true });
  image.src = source;
};

const feedbackPage = (): string => `
  <main id="page-content" class="inner-page feedback-page">
    <section class="compact-hero section-shell">
      ${pageBackButton('/', state.language === 'bn' ? 'হোমে ফিরে যান' : 'Back to Home')}
      <span class="section-kicker">${state.language === 'bn' ? 'ব্যবহারকারী প্রতিক্রিয়া ও সমীক্ষা' : 'User Feedback & Evaluation'}</span>
      <h1>${state.language === 'bn' ? 'জাস্টর এআই ব্যবহারকারী মতামত সমীক্ষা' : 'Justor AI User Experience Survey'}</h1>
      <p>${state.language === 'bn' ? 'আপনার মূল্যবান মতামত আমাদের এআই ও আইনি গবেষণা প্ল্যাটফর্মকে আরও উন্নত করতে সাহায্য করে।' : 'Your feedback helps us refine our legal AI intelligence, statutory verification, and user experience for lawyers and law students.'}</p>
      <div style="display: flex; gap: 12px; margin-top: 16px; flex-wrap: wrap;">
        <a class="button" href="https://docs.google.com/forms/d/e/1FAIpQLSdMfVydj2kMXZkf3SpYi_soA37YtTmAIB7VquPNkadYOmLSrg/viewform" target="_blank" rel="noopener">
          ${state.language === 'bn' ? 'নতুন উইন্ডোতে ফর্মটি খুলুন' : 'Open in New Window'} ${icon('external', 14)}
        </a>
      </div>
    </section>
    
    <article class="section-shell" style="max-width: 840px; margin-top: 24px; padding-bottom: 60px;">
      <div style="background: var(--bg-card, #131827); border: 1px solid var(--border-color, #232B3E); border-radius: 12px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.15);">
        <iframe 
          src="https://docs.google.com/forms/d/e/1FAIpQLSdMfVydj2kMXZkf3SpYi_soA37YtTmAIB7VquPNkadYOmLSrg/viewform?embedded=true" 
          width="100%" 
          height="950" 
          frameborder="0" 
          marginheight="0" 
          marginwidth="0"
          style="display: block; width: 100%; border: none; background: #ffffff; border-radius: 12px;"
          title="Justor AI User Feedback Form">
          Loading feedback form…
        </iframe>
      </div>

      <div style="margin-top: 40px; padding: 24px; background: rgba(30, 56, 200, 0.05); border: 1px solid rgba(30, 56, 200, 0.2); border-radius: 12px;">
        <h3 style="margin-top: 0; color: var(--text-primary, #F1F5F9); font-size: 17px;">
          ${state.language === 'bn' ? 'নির্দিষ্ট আইনি ভুল বা সাইটেশন রিপোর্ট করতে চান?' : 'Need to report a specific citation, section or statute error?'}
        </h3>
        <p style="color: var(--text-secondary, #94A3B8); font-size: 14px; line-height: 1.6;">
          ${state.language === 'bn' ? 'সরাসরি আমাদের আইনি পর্যালোচনা টিমের কাছে নির্দিষ্ট ধারা বা মামলার ভুল রিপোর্ট করতে পারেন।' : 'You can also submit specific statutory discrepancies, superseded laws, or wrong citations directly to our QA triage pipeline.'}
        </p>
        <form class="public-search" style="flex-direction: column; gap: 16px; background: var(--bg-primary, #0D0F14); padding: 20px; border-radius: 8px; border: 1px solid var(--border-color, #232B3E); margin-top: 12px;" data-action="submit-qa-feedback">
          <label style="display: flex; flex-direction: column; gap: 6px; font-weight: 600; font-size: 13px; color: var(--text-primary, #F1F5F9);">
            ${state.language === 'bn' ? 'আইনি প্রশ্ন বা বিষয়' : 'Legal Question or Topic'}
            <input name="query" placeholder="e.g. Specific Relief Act s.21A contract for sale" required style="width: 100%; padding: 10px 14px; border: 1px solid var(--border-color, #232B3E); border-radius: 6px; background: var(--bg-card, #131827); color: var(--text-primary, #F1F5F9);">
          </label>
          <label style="display: flex; flex-direction: column; gap: 6px; font-weight: 600; font-size: 13px; color: var(--text-primary, #F1F5F9);">
            ${state.language === 'bn' ? 'সমস্যার ধরণ' : 'Issue Category'}
            <select name="category" required style="width: 100%; padding: 10px 14px; border: 1px solid var(--border-color, #232B3E); border-radius: 6px; background: var(--bg-card, #131827); color: var(--text-primary, #F1F5F9);">
              <option value="">Select category...</option>
              <option value="wrong_law">Wrong law or statute applied</option>
              <option value="wrong_citation">Incorrect section or case citation</option>
              <option value="outdated_law">Outdated or superseded legal text</option>
              <option value="missing_authority">Missed a mandatory controlling authority</option>
              <option value="incomplete_answer">Incomplete legal analysis</option>
              <option value="misunderstood_question">Misunderstood facts / scenario</option>
              <option value="other">Other issue</option>
            </select>
          </label>
          <label style="display: flex; flex-direction: column; gap: 6px; font-weight: 600; font-size: 13px; color: var(--text-primary, #F1F5F9);">
            ${state.language === 'bn' ? 'সঠিক তথ্য বা সাইটেশন কী হওয়া উচিত?' : 'What should the correct answer or authority be?'}
            <textarea name="comment" rows="3" placeholder="Mention the exact Section, Act, or Supreme Court judgment..." required style="width: 100%; padding: 10px 14px; border: 1px solid var(--border-color, #232B3E); border-radius: 6px; background: var(--bg-card, #131827); color: var(--text-primary, #F1F5F9);"></textarea>
          </label>
          <button class="button button-small" type="submit" style="align-self: flex-start;">${state.language === 'bn' ? 'রিপোর্ট জমা দিন ↗' : 'Submit QA Report ↗'}</button>
        </form>
      </div>
    </article>
  </main>`;

const profilePage = (): string => {
  const profile = getStoredProfile();
  const isGuest = localStorage.getItem('justor_guest_mode') === 'true';
  const isBn = state.language === 'bn';
  const initials = profile.fullName
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || (isGuest ? 'G' : 'JA');

  const roleLabelsMap: Record<Role, { title: string; desc: string; icon: string }> = {
    professional: {
      title: isBn ? 'আইনজীবী ও আইনি পেশাদার' : 'Legal Professional',
      desc: isBn ? 'সুপ্রিম কোর্ট ও অধস্তন আদালতের প্র্যাকটিস ও নজির গবেষণা' : 'Advocate, Judicial officer, or Chamber counsel',
      icon: '⚖️',
    },
    student: {
      title: isBn ? 'আইনের শিক্ষার্থী' : 'Law Student',
      desc: isBn ? 'এলএলবি ও বার কাউন্সিলের জন্য ধারা ও কেস বিশ্লেষণ' : 'LLB, LLM, or Bar vocational study & quizzes',
      icon: '🎓',
    },
  };

  return `
  <main id="page-content" class="inner-page profile-page-view">
    <div class="profile-container">
      ${pageBackButton('/workspace/' + state.role, isBn ? 'ওয়ার্কস্পেসে ফিরে যান' : 'Back to Workspace')}

      <div class="profile-card-modern">
        <!-- Profile Header / Avatar Block -->
        <div class="profile-header-block">
          <div class="profile-avatar-wrap">
            <div class="profile-avatar-circle">
              <span>${escapeHtml(initials)}</span>
            </div>
            ${isGuest ? '<span class="avatar-badge guest-badge" title="Guest Mode">⚡</span>' : '<span class="avatar-badge auth-badge" title="Verified Account">✓</span>'}
          </div>

          <div class="profile-identity-info">
            <div class="profile-name-row">
              <h1 class="profile-user-name">${escapeHtml(profile.fullName)}</h1>
              <span class="profile-role-pill">${roleLabelsMap[profile.role]?.icon} ${roleLabelsMap[profile.role]?.title}</span>
            </div>
            <p class="profile-user-email">
              ${icon('shield', 14)} <span>${escapeHtml(profile.email || (isGuest ? 'Guest Testing Session' : 'Verified Account'))}</span>
            </p>
          </div>
        </div>

        <!-- User Information Form -->
        <form data-action="save-profile-form" class="profile-form-modern">
          <div class="profile-form-section">
            <label for="prof-fullName" class="profile-form-label">
              <span>${ui(state.language, 'fullName')}</span>
            </label>
            <div class="profile-input-wrap">
              <span class="input-leading-icon">${icon('user', 16)}</span>
              <input type="text" id="prof-fullName" name="fullName" value="${escapeHtml(profile.fullName)}" required placeholder="Enter full name" class="profile-text-input" />
            </div>
          </div>

          <div class="profile-form-section">
            <label for="prof-email" class="profile-form-label">
              <span>${ui(state.language, 'emailAddress')}</span>
              <small class="label-hint">${isBn ? 'শুধুমাত্র পাঠযোগ্য (গুগল অ্যাকাউন্ট)' : 'Managed via Google Auth'}</small>
            </label>
            <div class="profile-input-wrap is-readonly">
              <span class="input-leading-icon">${icon('shield', 16)}</span>
              <input type="email" id="prof-email" name="email" value="${escapeHtml(profile.email || (isGuest ? 'guest@justor.ai' : ''))}" readonly class="profile-text-input" />
            </div>
          </div>

          <!-- Organization / Partner Affiliation -->
          <div class="profile-form-section">
            <label for="prof-partnerOrg" class="profile-form-label">
              <span>${isBn ? 'সংগঠন / বার কাউন্সিল' : 'Organization / Bar Council'}</span>
              <small class="label-hint">${isBn ? 'আপনার প্রতিদিনের ক্রেডিট নির্ধারণ করে' : 'Determines your daily research credits'}</small>
            </label>
            <div class="profile-input-wrap">
              <span class="input-leading-icon">⚖️</span>
              <select id="prof-partnerOrg" name="partnerOrg" class="profile-select-input">
                <option value="" ${!profile.partnerOrg ? 'selected' : ''}>${isBn ? 'স্বাধীন / কোনো অ্যাফিলিয়েশন নেই' : 'Independent — No affiliation'} (${UNAFFILIATED_DAILY_LIMIT} ${isBn ? 'ক্রেডিট/দিন' : 'credits/day'})</option>
                <option value="habiganj-bar-council" ${profile.partnerOrg === 'habiganj-bar-council' ? 'selected' : ''}>⚖️ Habiganj Bar Council (100 ${isBn ? 'ক্রেডিট/দিন' : 'credits/day'})</option>
                ${(isUnlimitedUser(profile.email || state.session?.user?.email) || profile.partnerOrg === 'unlimited-vip') ? `<option value="unlimited-vip" selected>⭐ VIP Unlimited Tier (Unlimited credits & OCR)</option>` : ''}
              </select>
            </div>
            ${(isUnlimitedUser(profile.email || state.session?.user?.email) || profile.partnerOrg === 'unlimited-vip') ? `
              <div class="partner-tier-badge" style="background: linear-gradient(135deg, rgba(30,56,200,0.08), rgba(124,58,237,0.12)); border: 1.5px solid #7C3AED; padding: 12px 14px; border-radius: 10px; display: flex; align-items: center; gap: 12px; margin-top: 10px;">
                <span style="font-size: 26px;">⭐</span>
                <div>
                  <strong style="color: #6D28D9; font-size: 14px;">VIP Unlimited Access Tier — Active</strong>
                  <span style="color: #4C1D95; font-size: 12.5px;">${isBn ? 'আপনার অ্যাকাউন্টের জন্য সকল আইনি গবেষণা, কেস অ্যানালিটিক্স এবং ভিশন ওসিআর আনলিমিটেড সক্রিয় রয়েছে।' : 'Your account has full unlimited access to legal research, case precedent intelligence, and Vision OCR.'}</span>
                </div>
              </div>` : (profile.partnerOrg === 'habiganj-bar-council' ? `
              <div class="partner-tier-badge">
                <img src="/visuals/habiganj-bar-council.jpg" alt="Habiganj Bar Council" class="partner-badge-logo">
                <div>
                  <strong>⚖️ Habiganj Bar Council Member</strong>
                  <span>${isBn ? '১০০ ক্রেডিট/দিন — অফিসিয়াল পার্টনার সুবিধা' : '100 credits/day — Official partner benefit'}</span>
                </div>
              </div>` : '')}
          </div>

          <!-- Locked role -->
          <div class="profile-form-section">
            <label class="profile-form-label">
              <span>${isBn ? 'ওয়ার্কস্পেস রোল' : 'Workspace role'}</span>
              <small class="label-hint">${ui(state.language, 'chooseRoleLocked')}</small>
            </label>
            <div class="profile-role-locked">
              <span class="role-option-icon">${roleLabelsMap[profile.role]?.icon}</span>
              <div class="role-option-text">
                <strong class="role-option-title">${roleLabelsMap[profile.role]?.title}</strong>
                <span class="role-option-desc">${isBn ? 'রোল লক করা আছে এবং পরিবর্তন করা যাবে না।' : 'This role is locked and cannot be changed.'}</span>
              </div>
            </div>
            <input type="hidden" name="role" value="${profile.role}" />
          </div>

          <!-- Action Buttons -->
          <div class="profile-actions-bar">
            <button class="button profile-save-btn" type="submit">
              ${icon('check', 16)} <span>${ui(state.language, 'saveSettings')}</span>
            </button>
            <button class="button button-secondary profile-signout-btn" type="button" data-action="sign-out">
              <span>${ui(state.language, 'signOut')}</span>
            </button>
          </div>
        </form>

        <!-- WhatsApp Chamber OS 24/7 Mobile Bridge -->
        <div class="profile-whatsapp-section" id="profile-whatsapp-card-mount">
          <div class="pwa-header">
            <div class="pwa-icon-box">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="#25D366">
                <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0 0 12.04 2z"/>
              </svg>
            </div>
            <div class="pwa-title-block">
              <div class="pwa-title-row">
                <h3 class="pwa-title">${isBn ? 'হোয়াটসঅ্যাপ চেম্বার ওএস ও মোবাইল কো-পাইলট' : 'WhatsApp Chamber OS & Mobile Co-Pilot'}</h3>
                <span class="pwa-status-pill loading" id="pwa-status-pill">
                  <span class="pwa-pulse-dot"></span> <span id="pwa-status-label">${isBn ? 'যাচাই করা হচ্ছে...' : 'Checking connection...'}</span>
                </span>
              </div>
              <p class="pwa-subtitle">
                ${isBn ? 'কোর্ট চত্বর থেকে সরাসরি ভয়েস ডিকটেশন, সকাল ৮:০০ টার দৈনিক ব্রিফিং ও ক্লায়েন্ট আপডেট।' : 'Voice dictation from court corridors, 8:00 AM daily chamber brief, and 1-tap client case status updates.'}
              </p>
            </div>
          </div>

          <div id="pwa-dynamic-content" class="pwa-content-body">
            <div class="pwa-loading-state">
              <div class="spinner-small"></div>
              <span>${isBn ? 'চেম্বার গেটওয়ে লোড হচ্ছে...' : 'Loading Chamber Gateway...'}</span>
            </div>
          </div>
        </div>

        <!-- Privacy & Local Data Note -->
        <div class="profile-privacy-note">
          ${icon('shield', 14)}
          <p>${isBn ? 'আপনার গবেষণার তথ্য ও হিস্ট্রি সম্পূর্ণ ব্যক্তিগত এবং এই ব্রাউজারে সুরক্ষিত থাকে।' : 'Your legal queries and research threads are stored locally on your device for strict confidentiality.'}</p>
        </div>
      </div>
    </div>
  </main>`;
};

const pageForPath = (path: string): string => {
  if (path === '/') return homePage();
  if (path === '/start') return startPage();
  if (path === '/legal-library') return libraryPage();
  if (path === '/guides') return guidesPage();
  if (path.startsWith('/guides/')) return guideDetailShell(decodeURIComponent(path.slice('/guides/'.length)));
  if (path.startsWith('/action-guides/')) return guideDetailShell(decodeURIComponent(path.slice('/action-guides/'.length)));
  if (path === '/legal-updates') return updatesPage();
  if (path.startsWith('/legal-updates/')) return updateDetailShell(decodeURIComponent(path.slice('/legal-updates/'.length)));
  if (path === '/workspace/professional') return professionalWorkspace();
  if (path === '/workspace/student/learn' || path.startsWith('/workspace/student/learn/')) return studentLearnWorkspace();
  if (path === '/workspace/student') return studentWorkspace();
  if (path === '/workspace/citizen' || path === '/citizen') return professionalWorkspace();
  if (path === '/trust') return trustPage();
  if (path === '/about') return aboutPage();
  if (path === '/careers') return careersPage();
  if (path === '/contact') return contactPage();
  if (path === '/login') return loginPage();
  if (path === '/onboard' || path.startsWith('/onboard')) return onboardPage();
  if (path === '/choose-role') return chooseRolePage();
  if (path === '/profile' || path === '/account' || path === '/settings') return profilePage();
  if (path === '/privacy') return policyPage('privacy');
  if (path === '/terms') return policyPage('terms');
  if (path === '/disclaimer') return policyPage('disclaimer');
  if (path === '/feedback') return feedbackPage();
  if (path === '/amendment-admin') return '<div id="amendment-admin-mount"></div>';
  if (path === '/admin/qa') return '<div id="qa-admin-mount"></div>';
  return notFoundPage();
};

const isFocusedRoute = (path: string): boolean => path.startsWith('/workspace/') || path === '/login' || path === '/start' || path === '/profile' || path === '/choose-role' || path === '/onboard' || path.startsWith('/onboard');

const setDocumentMeta = (): void => {
  document.documentElement.lang = state.language === 'bn' ? 'bn' : 'en';
  const titles: Record<string, string> = {
    '/': 'Bangladesh Legal Intelligence', '/start': 'Start Justor', '/choose-role': 'Choose workspace', '/legal-library': 'Legal Library', '/guides': 'Legal Authority Guides', '/legal-updates': 'Legal Updates', '/trust': 'Trust Method', '/about': 'About', '/careers': 'Careers', '/contact': 'Contact', '/login': 'Sign In', '/onboard': 'Chamber Activation & WhatsApp Co-Pilot', '/profile': 'User Profile & Settings', '/privacy': 'Privacy', '/terms': 'Terms', '/disclaimer': 'Disclaimer',
  };
  const dynamic = state.routePath.startsWith('/workspace/') ? `${roleLabels[state.routePath.split('/').pop() as Role] || 'Legal'} Workspace` : state.routePath.startsWith('/guides/') || state.routePath.startsWith('/action-guides/') ? 'Legal Authority Guide' : state.routePath.startsWith('/legal-updates/') ? 'Legal Update' : 'Justor AI';
  document.title = `${titles[state.routePath] ?? dynamic} | Justor AI`;
};

const render = (preserveScroll = false, preservedQuery = ''): void => {
  const parsed = parseLocalizedPath(window.location.pathname);
  state.language = parsed.language;
  let routePath = parsed.routePath;
  state.menuOpen = false;
  state.sidebarOpen = false;

  const locked = lockedRole();
  if (locked) state.role = locked;

  const openAuthRoutes = new Set(['/login', '/choose-role']);
  if (state.session && !locked && !openAuthRoutes.has(routePath)) {
    routePath = '/choose-role';
    history.replaceState({}, '', `${localizedPath('/choose-role', state.language)}${window.location.search}${window.location.hash}`);
  } else if (locked && routePath === '/choose-role') {
    routePath = `/workspace/${locked}`;
    history.replaceState({}, '', workspacePathForRole(locked));
  } else if (locked && routePath.startsWith('/workspace/') && !routePath.startsWith(`/workspace/${locked}`)) {
    routePath = `/workspace/${locked}`;
    history.replaceState({}, '', `${workspacePathForRole(locked)}${window.location.hash}`);
  }

  state.routePath = routePath;
  const focused = isFocusedRoute(state.routePath);
  appRoot.innerHTML = `${focused ? '' : header()}${pageForPath(state.routePath)}${focused ? '' : footer()}<div class="toast-region" aria-live="polite" aria-atomic="true"></div>`;
  hydrateHeroVisual();
  setDocumentMeta();
  analytics.pageView(state.routePath);
  const queryField = document.querySelector<HTMLInputElement | HTMLTextAreaElement>('[name="query"]');
  if (preservedQuery && queryField) queryField.value = preservedQuery;
  if (state.lastResearch && state.lastResearchRole && state.routePath === `/workspace/${state.lastResearchRole}`) {
    const output = document.querySelector<HTMLElement>('[data-research-output]');
    if (output) {
      output.hidden = false;
      output.innerHTML = renderResearchResult(state.lastResearch);
    }
  }
  if (!preserveScroll) window.scrollTo({ top: 0, behavior: 'instant' });
  void hydrateRoute(state.routePath);
  requestAnimationFrame(() => {
    restorePendingResearch();
    const voiceBtn = document.querySelector<HTMLButtonElement>('[data-action="toggle-voice-input"]');
    const composerTextarea = document.querySelector<HTMLTextAreaElement>('.composer-input-box textarea[name="query"]');
    if (voiceBtn && composerTextarea) {
      initVoiceInput(voiceBtn, composerTextarea, state.language);
    }
  });
};

const navigate = (href: string, preserveScroll = false, preservedQuery = ''): void => {
  const url = new URL(href, window.location.origin);
  history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`);
  render(preserveScroll, preservedQuery);
  if (url.hash) window.setTimeout(() => document.querySelector(url.hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 30);
};

let activeToastTimer: number | undefined;

const showToast = (title: string, message: string, tone: 'neutral' | 'warning' | 'positive' = 'neutral', autoDismissMs = 2600): void => {
  const region = document.querySelector<HTMLElement>('.toast-region');
  if (!region) return;
  if (activeToastTimer) {
    window.clearTimeout(activeToastTimer);
    activeToastTimer = undefined;
  }
  region.innerHTML = `
    <div class="toast ${tone === 'warning' ? 'toast-warning' : tone === 'positive' ? 'toast-positive' : ''}">
      <span>${icon(tone === 'warning' ? 'shield' : 'check', 18)}</span>
      <div>
        <strong>${escapeHtml(title)}</strong>
        <p>${escapeHtml(message)}</p>
      </div>
      <button type="button" data-action="close-toast" aria-label="Close">${icon('close', 16)}</button>
    </div>
  `;

  if (autoDismissMs > 0) {
    activeToastTimer = window.setTimeout(() => {
      const toast = region.querySelector<HTMLElement>('.toast');
      if (toast) {
        toast.classList.add('is-dismissing');
        window.setTimeout(() => {
          region.innerHTML = '';
        }, 240);
      } else {
        region.innerHTML = '';
      }
      activeToastTimer = undefined;
    }, autoDismissMs);
  }
};

const verificationBadge = (status?: string): string => {
  const normalized = (status ?? '').trim().toUpperCase();
  if (normalized.includes('PRIMARY') || normalized.includes('CHECKED') || normalized.includes('VERIFIED') || normalized === 'ACTIVE') {
    return `<span class="semantic-badge badge-source-checked">● ${ui(state.language, 'sourceChecked')}</span>`;
  }
  if (normalized.includes('REPORTER') || normalized.includes('DLR') || normalized.includes('SCOB') || normalized.includes('BLD')) {
    return `<span class="semantic-badge badge-reporter-verified">◐ ${ui(state.language, 'reporterVerified')}</span>`;
  }
  if (normalized.includes('UNREVIEWED')) {
    return `<span class="semantic-badge badge-unreviewed">✕ ${ui(state.language, 'unreviewedCorpus')}</span>`;
  }
  return `<span class="semantic-badge badge-pending-verified">○ ${ui(state.language, 'pendingVerification')}</span>`;
};

const sourcePanel = (source?: LegalSource, className = 'result-source-panel'): string => {
  if (!source) return `<aside class="${className}" data-source-panel>${unavailable('No authority record was supplied for this result.')}</aside>`;
  const url = safeUrl(source.url);
  const title = source.authority || source.title || 'Controlling Authority';
  const provision = source.provision || source.citation || '';
  const excerpt = source.excerpt ? `<blockquote>${escapeHtml(source.excerpt)}</blockquote>` : '';
  const isUnreviewed = (source.verificationStatus || source.status || '').toUpperCase().includes('UNREVIEWED');

  let provButtonHtml = '';
  if (url && !isUnreviewed) {
    provButtonHtml = `<a class="button button-small button-secondary open-prov-btn" href="${url}" target="_blank" rel="noopener noreferrer">${icon('book', 15)} ${ui(state.language, 'viewFullProvision')} ${icon('external', 12)}</a>`;
  } else {
    const tooltipText = ui(state.language, 'sourceUrlNotIndexed');
    provButtonHtml = `<button class="button button-small button-secondary open-prov-btn is-disabled" type="button" disabled title="${escapeHtml(tooltipText)}" aria-label="${escapeHtml(tooltipText)}">${icon('book', 15)} ${ui(state.language, 'viewFullProvision')}</button><small class="source-url-tooltip">${escapeHtml(tooltipText)}</small>`;
  }

  return `
    <aside class="${className}" data-source-panel>
      <span class="section-kicker">Controlling Authority</span>
      <h3>${escapeHtml(title)}</h3>
      ${provision ? `<div class="authority-provision-ref"><strong>${escapeHtml(provision)}</strong></div>` : ''}
      <div class="source-badges">
        ${verificationBadge(source.verificationStatus || source.status)}
      </div>
      ${excerpt}
      <div class="authority-panel-actions">
        ${provButtonHtml}
      </div>
    </aside>`;
};

const recordCard = (record: LibraryRecord): string => {
  const href = record.href ? localizedPath(record.href, state.language) : null;
  const content = `<span>${escapeHtml(record.type)}</span><h3>${escapeHtml(record.title)}</h3>${record.subtitle ? `<p>${escapeHtml(record.subtitle)}</p>` : ''}${record.status ? `<small>${escapeHtml(record.status)}</small>` : ''}`;
  return href ? `<a href="${href}" data-route class="record-card">${content}${icon('arrow', 16)}</a>` : `<article class="record-card">${content}</article>`;
};

const clusterLabel = (cluster: string): string => guideTopics.find((topic) => topic.value === cluster)?.label ?? cluster;

const guideCard = (guide: GuideRecord): string => `<a href="${localizedPath(`/guides/${guide.route}`, state.language)}" data-route class="guide-row-card"><span>${escapeHtml(clusterLabel(guide.cluster))}</span><h3>${escapeHtml(state.language === 'bn' && guide.titleBn ? guide.titleBn : guide.titleEn)}</h3>${guide.metaDescription ? `<p>${escapeHtml(guide.metaDescription)}</p>` : ''}<footer><small>Published citizen guide</small><strong>Open guide ${icon('arrow', 15)}</strong></footer></a>`;

const updateCard = (update: LegalUpdateRecord): string => `<a href="${localizedPath(`/legal-updates/${update.id}`, state.language)}" data-route class="update-row-card"><div><span>${escapeHtml(update.topic ?? 'Legal update')}</span>${update.date ? `<time>${escapeHtml(update.date)}</time>` : ''}</div><h3>${escapeHtml(update.title)}</h3>${update.summary ? `<p>${escapeHtml(update.summary)}</p>` : ''}<footer>${update.source ? '<small>Source record supplied</small>' : '<small>Source status not supplied</small>'}<strong>Open update ${icon('arrow', 15)}</strong></footer></a>`;

const publicEmptyState = (query = ''): string => query
  ? `<div class="empty-state"><span>${icon('search', 24)}</span><h3>No results for “${escapeHtml(query)}”</h3><p>Try a broader keyword, a service name or a document you already have.</p></div>`
  : `<div class="empty-state registry-empty"><span>${icon('book', 24)}</span><h3>No citizen guides are published yet.</h3><p>Review-stage legal material stays private until its required approvals are complete.</p></div>`;

const renderState = <T>(resource: ResourceState<T[]>, renderer: (item: T) => string): string => {
  if (resource.status === 'unavailable') return unavailable();
  if (resource.status === 'empty') return empty();
  return resource.data.map(renderer).join('');
};

const renderProductProof = (proof: ProductProofRecord): string => {
  if (proof.verified !== true || !proof.propositions.length || !proof.sources.length) return '';
  const selected = proof.sources[0];
  return `<div class="proof-analysis"><span class="section-kicker">AI analysis</span>${proof.propositions.map((proposition) => {
    const sourceIndex = proof.sources.findIndex((source) => source.id === proposition.sourceId);
    return `<p>${escapeHtml(proposition.text)}${sourceIndex >= 0 ? ` <button type="button" data-proof-source="${sourceIndex}" aria-label="Show source ${sourceIndex + 1}">[${sourceIndex + 1}]</button>` : ''}</p>`;
  }).join('')}</div>${sourcePanel(selected, 'proof-source')}`;
};

const hydrateHome = async (): Promise<void> => {
  const [proof, library] = await Promise.all([publicData.proof(), publicData.library('', '', undefined)]);
  const proofMount = document.querySelector<HTMLElement>('[data-home-proof]');
  if (proofMount && proof.status === 'ready' && proof.data?.verified === true && proof.data.propositions.length && proof.data.sources.length) {
    state.productProof = proof.data;
    proofMount.innerHTML = `<section class="proof-section light-section"><div class="section-shell"><div class="section-heading"><span class="section-kicker">${ui(state.language, 'productProof')}</span><h2>${ui(state.language, 'verifyHeading')}</h2><p>${ui(state.language, 'verifyBody')}</p></div><div class="proof-interface" data-product-proof>${renderProductProof(proof.data)}</div></div></section>`;
  }
  const libraryMount = document.querySelector<HTMLElement>('[data-home-library-preview]');
  if (libraryMount && library.status === 'ready' && library.data.length) {
    libraryMount.innerHTML = libraryPreviewFull(library.data.slice(0, 4));
  }
};

const hydrateLibrary = async (): Promise<void> => {
  const params = new URLSearchParams(window.location.search);
  const resource = await publicData.library(params.get('q') ?? '', params.get('type') ?? '');
  const mount = document.querySelector<HTMLElement>('[data-library-results]');
  if (mount) mount.innerHTML = renderState(resource, recordCard);
  const count = document.querySelector<HTMLElement>('[data-library-count]');
  if (count) count.textContent = resource.status === 'ready' || resource.status === 'empty' ? String(resource.data.length) : '—';
};

const hydrateGuides = async (append = false): Promise<void> => {
  void append;
  const resource = await publicData.guides(state.guideQuery, state.guideCluster, state.guidePage, state.language);
  const mount = document.querySelector<HTMLElement>('[data-guide-results]');
  const pageSize = 8;
  const visibleCount = state.guidePage * pageSize;
  const visibleGuides = resource.data.slice(0, visibleCount);
  if (mount) {
    mount.innerHTML = resource.status === 'ready'
      ? visibleGuides.map(guideCard).join('')
      : publicEmptyState(state.guideQuery);
  }
  const count = document.querySelector<HTMLElement>('[data-guide-count]');
  if (count) count.textContent = resource.status === 'ready' ? `${visibleGuides.length} of ${resource.data.length}` : '0 published';
  const more = document.querySelector<HTMLButtonElement>('[data-guide-more]');
  if (more) more.hidden = resource.status !== 'ready' || visibleGuides.length >= resource.data.length;
};

const hydrateCitizen = async (query = '', cluster = ''): Promise<void> => {
  const resource = await publicData.guides(query, cluster, 1, state.language);
  const mount = document.querySelector<HTMLElement>('[data-citizen-results]');
  const section = document.querySelector<HTMLElement>('[data-citizen-results-section]');
  if (section) section.hidden = resource.status !== 'ready' && !query && !cluster;
  if (mount) mount.innerHTML = resource.status === 'ready'
    ? resource.data.slice(0, 8).map(guideCard).join('')
    : publicEmptyState(query || clusterLabel(cluster));
};

const guideSourceRecord = (source: GuideDetailRecord['officialSources'][number]): string => {
  const url = safeUrl(source.url);
  const badge = source.type === 'primary' ? 'PRIMARY SOURCE ✓' : source.type === 'official' ? 'Official source' : 'Context source';
  const content = `<span class="source-type">${badge}</span><strong>${escapeHtml(source.label)}</strong>${icon('external', 15)}`;
  return url
    ? `<a class="guide-source-row" href="${url}" target="_blank" rel="noopener">${content}</a>`
    : `<div class="guide-source-row">${content}</div>`;
};

const hydrateGuideDetail = async (slug: string): Promise<void> => {
  const resource = await publicData.guide(slug, state.language);
  const mount = document.querySelector<HTMLElement>('[data-guide-detail]');
  if (!mount) return;
  if (resource.status !== 'ready' || !resource.data) {
    mount.innerHTML = `<div class="empty-state"><span>${icon('book', 24)}</span><h3>This guide is not in the published registry.</h3><p>Review and draft guide bodies are intentionally unavailable on the public site.</p>${route('/guides', `Browse published guides ${icon('arrow', 15)}`, 'text-link')}</div>`;
    return;
  }
  const guide = resource.data;
  const renderLocale = state.language === 'bn' && guide.content.bn ? 'bn' : 'en';
  const content = guide.content[renderLocale] ?? guide.content.en;
  const translationFallback = state.language === 'bn' && renderLocale === 'en';
  const badges = `<span class="semantic-badge">Primary sources linked</span>${guide.publicationBadges?.sourceChecked ? '<span class="semantic-badge badge-checked">SOURCE CHECKED ✓</span>' : ''}${guide.publicationBadges?.humanReviewed ? '<span class="semantic-badge badge-reviewed">HUMAN LEGAL REVIEWED ✓</span>' : ''}`;
  mount.className = 'guide-detail';
  mount.innerHTML = `
    <div class="guide-nav-top" style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; flex-wrap: wrap; gap: 10px;">
      ${pageBackButton('/guides', state.language === 'bn' ? 'সকল গাইডে ফিরে যান' : 'Back to Legal Guides')}
      <div class="guide-breadcrumbs">${route('/guides', 'Citizen Legal Guides')}<span>/</span><span>${escapeHtml(clusterLabel(guide.cluster))}</span></div>
    </div>
    <header>
      <div class="guide-meta"><span class="section-kicker">${escapeHtml(clusterLabel(guide.cluster))}</span><time>${escapeHtml(guide.verification.lastSourceChecked)}</time></div>
      <h1>${escapeHtml(content.title)}</h1>
      <div class="source-badges">${badges}</div>
    </header>
    ${translationFallback ? '<div class="translation-notice" lang="bn"><strong>অনুবাদ প্রস্তুত হচ্ছে</strong><p>এই প্রকাশিত গাইডটি এখন ইংরেজিতে দেখানো হচ্ছে।</p></div>' : ''}
    ${renderGuideBody(guide, renderLocale)}
    <section class="guide-ask">
      <div>
        <span class="section-kicker">Ask Justor</span>
        <h2>Ask Justor about your situation.</h2>
        <p>The published guide stays attached as context when you continue.</p>
      </div>
      <div style="display: flex; gap: 10px; flex-wrap: wrap; align-items: center;">
        <button class="button" type="button" data-action="ask-from-guide" data-guide-id="${escapeHtml(guide.id)}" data-guide-title="${escapeHtml(content.title)}" data-guide-topic="${escapeHtml(guide.cluster)}">Ask Justor ${icon('arrow', 16)}</button>
        <button class="button button-secondary" type="button" data-action="go-back" data-fallback="/guides">${icon('arrow-left', 16)} ${state.language === 'bn' ? 'সকল গাইডে ফিরে যান' : 'Back to Legal Guides'}</button>
      </div>
    </section>`;
};

const renderGuideBody = (guide: GuideDetailRecord, locale: 'en' | 'bn'): string => {
  const content = guide.content[locale] ?? guide.content.en;
  const list = (title: string, values: string[], ordered = false): string => values.length
    ? `<section><h2>${title}</h2><${ordered ? 'ol' : 'ul'}>${values.map((value) => `<li>${escapeHtml(value)}</li>`).join('')}</${ordered ? 'ol' : 'ul'}></section>`
    : '';
  const glance = Object.entries({
    'Who this is for': content.atAGlance.whoFor,
    'Legal basis': content.atAGlance.legalBasis,
    'Main rule': content.atAGlance.mainRule,
    'Update status': content.atAGlance.updateStatus,
  }).filter((entry) => entry[1]);
  return `<article class="guide-body" lang="${locale}"><section class="direct-answer"><span>Direct Answer</span><p>${escapeHtml(content.directAnswer)}</p></section>${glance.length ? `<section><h2>At a Glance</h2><dl class="guide-glance">${glance.map(([label, value]) => `<div><dt>${label}</dt><dd>${escapeHtml(value)}</dd></div>`).join('')}</dl></section>` : ''}${content.lawMeaning ? `<section><h2>What the Law or Process Means</h2><p>${escapeHtml(content.lawMeaning)}</p></section>` : ''}${list('Step-by-Step', content.steps, true)}${list('Documents or Evidence to Keep', content.evidence)}${content.simpleExample ? `<section><h2>Simple Example</h2><p>${escapeHtml(content.simpleExample)}</p></section>` : ''}${list('Common Mistakes', content.commonMistakes)}${content.whatIf.length ? `<section><h2>What If...?</h2>${content.whatIf.map((item) => `<details><summary>${escapeHtml(item.question)}</summary><p>${escapeHtml(item.answer)}</p></details>`).join('')}</section>` : ''}${content.specialistTrigger ? `<section><h2>When to Speak to a Lawyer or Specialist</h2><p>${escapeHtml(content.specialistTrigger)}</p></section>` : ''}${content.faqs.length ? `<section><h2>Frequently Asked Questions</h2>${content.faqs.map((faq) => `<details><summary>${escapeHtml(faq.question)}</summary><p>${escapeHtml(faq.answer)}</p></details>`).join('')}</section>` : ''}<section><h2>Official Sources</h2><div class="guide-source-list">${guide.officialSources.map(guideSourceRecord).join('')}</div></section>${list('Legal Update History', guide.updateHistory)}${guide.relatedPages.length ? `<section><h2>Related Justor Pages</h2><div class="related-guide-links">${guide.relatedPages.map((related) => route(related.route, `${escapeHtml(related.label)} ${icon('arrow', 15)}`, 'text-link')).join('')}</div></section>` : ''}<section class="guide-disclaimer"><h2>Disclaimer</h2><p>${escapeHtml(content.disclaimer)}</p></section></article>`;
};

const hydrateUpdates = async (mountSelector = '[data-update-results]'): Promise<void> => {
  const resource = await publicData.updates();
  const mount = document.querySelector<HTMLElement>(mountSelector);
  if (!mount) return;
  if (mountSelector === '[data-professional-updates]' && resource.status !== 'ready') {
    mount.closest<HTMLElement>('[data-professional-updates-section]')?.remove();
    return;
  }
  mount.innerHTML = renderState(resource, updateCard);
};

const hydrateUpdateDetail = async (id: string): Promise<void> => {
  const resource = await publicData.update(id);
  const mount = document.querySelector<HTMLElement>('[data-update-detail]');
  if (!mount) return;
  if (resource.status === 'unavailable') { mount.innerHTML = unavailable(); return; }
  if (resource.status === 'empty' || !resource.data) { mount.innerHTML = empty(); return; }
  const update = resource.data;
  mount.className = 'update-detail';
  mount.innerHTML = `
    <div class="update-nav-top" style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; flex-wrap: wrap; gap: 10px;">
      ${pageBackButton('/legal-updates', state.language === 'bn' ? 'সকল আপডেটে ফিরে যান' : 'Back to Legal Updates')}
      <div class="guide-breadcrumbs">${route('/legal-updates', 'Legal Updates')}<span>/</span><span>${escapeHtml(update.topic ?? 'Update')}</span></div>
    </div>
    <header><span class="section-kicker">${escapeHtml(update.topic ?? 'Legal update')}</span><h1>${escapeHtml(update.title)}</h1>${update.date ? `<time>${escapeHtml(update.date)}</time>` : ''}</header>${update.summary ? `<section><h2>Summary</h2><p>${escapeHtml(update.summary)}</p></section>` : ''}${update.effect ? `<section><h2>What to recheck</h2><p>${escapeHtml(update.effect)}</p></section>` : ''}${update.source ? `<section><h2>Source record</h2>${sourcePanel(update.source, 'source-record')}</section>` : unavailable('The update record did not include a supporting source.')}`;
};

let waPairingPollTimer: number | undefined;

const hydrateWhatsAppConnectionCard = async (): Promise<void> => {
  const mount = document.getElementById('profile-whatsapp-card-mount');
  if (!mount) return;

  const contentEl = document.getElementById('pwa-dynamic-content');
  const statusPill = document.getElementById('pwa-status-pill');
  const statusLabel = document.getElementById('pwa-status-label');
  if (!contentEl) return;

  if (waPairingPollTimer) {
    clearInterval(waPairingPollTimer);
    waPairingPollTimer = undefined;
  }

  const backendUrl = (import.meta.env.VITE_BACKEND_URL?.trim() || 'https://justorai-backend.onrender.com').replace(/\/$/, '');
  const isBn = state.language === 'bn';
  const profile = getStoredProfile();
  const storedPhone = localStorage.getItem('justor_chamber_phone') || '+8801798072132';

  const checkStatus = async () => {
    try {
      const headers: Record<string, string> = {};
      if (state.session?.access_token) {
        headers['Authorization'] = `Bearer ${state.session.access_token}`;
      }
      const res = await fetch(`${backendUrl}/api/whatsapp/pairing-status?phone=${encodeURIComponent(storedPhone)}`, { headers });
      if (res.ok) {
        const data = await res.json();
        return data;
      }
    } catch (e) {
      console.warn('Error checking WhatsApp pairing status:', e);
    }
    return { paired: false, phone: null };
  };

  const renderConnectedView = (phone: string, fullName: string, chamberName: string) => {
    if (statusPill && statusLabel) {
      statusPill.className = 'pwa-status-pill connected';
      statusLabel.textContent = isBn ? 'সক্রিয় ও সংযুক্ত' : 'Live & Connected';
    }

    contentEl.innerHTML = `
      <div class="pwa-connected-box">
        <div class="pwa-connected-header">
          <div class="pwa-badge-check">✓</div>
          <div>
            <div class="pwa-connected-phone-row">
              <strong class="pwa-connected-phone">🟢 ${escapeHtml(phone)}</strong>
              <span class="pwa-role-tag">${isBn ? 'যাচাইকৃত সিনিয়র অ্যাডভোকেট' : 'Verified Senior Advocate'}</span>
            </div>
            <div class="pwa-connected-meta">
              <span>👤 ${escapeHtml(fullName || profile.fullName)}</span> • <span>🏛️ ${escapeHtml(chamberName || 'Justor Law Chambers')}</span>
            </div>
          </div>
        </div>

        <p class="pwa-connected-note">
          ${isBn ? 'আপনার নম্বরটি সফলভাবে এই চেম্বারের সাথে সংযুক্ত। আদালত চত্বরে দাঁড়িয়ে ভয়েস মেসেজ পাঠালেই তা সংশ্লিষ্ট মামলার ডকেটে ফাইল হবে এবং প্রতিদিন সকাল ৮:০০ টায় আপনার মোবাইলে ব্রিফিং পৌঁছাবে।' : 'Your WhatsApp number is securely bound to this chamber. Send voice notes or case commands to +1 (555) 172-2173 to sync dockets in real time.'}
        </p>

        <div class="pwa-btn-group">
          <a href="https://wa.me/15551722173" target="_blank" rel="noopener noreferrer" class="button pwa-chat-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0 0 12.04 2z"/></svg>
            <span>${isBn ? 'হোয়াটসঅ্যাপে চ্যাট শুরু করুন (+1 555 172-2173)' : 'Open WhatsApp Chat (+1 555 172-2173)'}</span>
          </a>
          <button type="button" class="button button-secondary pwa-relink-btn" id="pwa-relink-trigger">
            <span>${isBn ? 'অন্য নম্বর পেয়ার করুন' : 'Pair Different Number'}</span>
          </button>
        </div>
      </div>
    `;

    document.getElementById('pwa-relink-trigger')?.addEventListener('click', () => {
      localStorage.removeItem('justor_chamber_phone');
      void renderPairingView();
    });
  };

  const renderPairingView = async () => {
    if (statusPill && statusLabel) {
      statusPill.className = 'pwa-status-pill unlinked';
      statusLabel.textContent = isBn ? 'সংযোগের অপেক্ষায়' : 'Awaiting Connection';
    }

    contentEl.innerHTML = `
      <div class="pwa-loading-state">
        <div class="spinner-small"></div>
        <span>${isBn ? 'নিরাপদ ৬-ডিজিটের হ্যান্ডশেক কোড প্রস্তুত হচ্ছে...' : 'Generating secure 6-digit handshake code...'}</span>
      </div>
    `;

    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (state.session?.access_token) {
        headers['Authorization'] = `Bearer ${state.session.access_token}`;
      }
      const res = await fetch(`${backendUrl}/api/whatsapp/pairing-code`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ chamber_name: profile.partnerOrg || 'Justor Chambers' })
      });
      const data = await res.json();
      const code = data.pairing_code || '382925';
      const waUrl = data.whatsapp_url || `https://wa.me/15551722173?text=LINK%20${code}`;
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=170x170&data=${encodeURIComponent(waUrl)}&margin=4`;

      contentEl.innerHTML = `
        <div class="pwa-pairing-grid">
          <div class="pwa-qr-card">
            <div class="pwa-qr-wrapper">
              <img src="${qrUrl}" alt="WhatsApp QR Code" class="pwa-qr-img" width="170" height="170" />
            </div>
            <span class="pwa-qr-hint">📱 ${isBn ? 'ফোনের ক্যামেরা দিয়ে স্ক্যান করুন' : 'Scan with mobile camera'}</span>
          </div>

          <div class="pwa-details-card">
            <div class="pwa-step-tag">${isBn ? 'পদক্ষেপ ১ — ইনস্ট্যান্ট কানেকশন' : 'Step 1 — Instant 1-Click Connect'}</div>
            <h4 class="pwa-card-subheading">
              ${isBn ? 'মোবাইল থেকে সরাসরি এক ক্লিকে যুক্ত করুন' : 'Connect directly from your phone in 1 click'}
            </h4>
            <p class="pwa-instructions">
              ${isBn ? 'নিচের বাটনে চাপ দিলে আপনার মোবাইলে সরাসরি হোয়াটসঅ্যাপ ওপেন হবে এবং <code>LINK ' + code + '</code> কোডটি স্বয়ংক্রিয়ভাবে পূরণ থাকবে। শুধু Send বাটনে চাপুন।' : 'Click the button below to open WhatsApp with <code>LINK ' + code + '</code> pre-filled, or scan the QR code with your camera.'}
            </p>

            <div class="pwa-cta-row">
              <a href="${waUrl}" target="_blank" rel="noopener noreferrer" class="button pwa-deeplink-btn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0 0 12.04 2z"/></svg>
                <span>${isBn ? 'Connect WhatsApp (One-Click)' : 'Connect WhatsApp (One-Click)'}</span>
              </a>
              <div class="pwa-code-pill" title="Click to copy code">
                <span class="pwa-code-label">CODE:</span>
                <strong class="pwa-code-val">LINK ${code}</strong>
                <button type="button" class="pwa-copy-btn" data-copy="LINK ${code}">${isBn ? 'কপি' : 'Copy'}</button>
              </div>
            </div>

            <div class="pwa-live-sync-alert">
              <span class="pwa-pulse-dot warning"></span>
              <span>${isBn ? 'মেসেজ পাঠানোর সাথে সাথে লাইভ স্ট্যাটাস সবুজ হয়ে সংযুক্ত হবে।' : 'Listening for WhatsApp message... Connects automatically in 2 seconds.'}</span>
            </div>
          </div>
        </div>
      `;

      contentEl.querySelector<HTMLButtonElement>('.pwa-copy-btn')?.addEventListener('click', (e) => {
        const text = (e.currentTarget as HTMLElement)?.getAttribute('data-copy') || `LINK ${code}`;
        void navigator.clipboard.writeText(text);
        showToast(isBn ? 'কপি সম্পন্ন' : 'Copied', text, 'positive');
      });

      waPairingPollTimer = window.setInterval(async () => {
        const status = await checkStatus();
        if (status && status.paired && status.phone) {
          clearInterval(waPairingPollTimer);
          waPairingPollTimer = undefined;
          localStorage.setItem('justor_chamber_phone', status.phone);
          showToast(isBn ? 'হোয়াটসঅ্যাপ সংযুক্ত হয়েছে!' : 'WhatsApp Connected!', `Connected: ${status.phone}`, 'positive');
          renderConnectedView(status.phone, status.full_name, status.chamber_name);
        }
      }, 3500);

    } catch (err) {
      contentEl.innerHTML = `<div class="pwa-error-state">${isBn ? 'কোড জেনারেট করতে সমস্যা হয়েছে। অনুগ্রহ করে পেজ রিফ্রেশ করুন।' : 'Could not generate pairing code. Please refresh the page.'}</div>`;
    }
  };

  const initialStatus = await checkStatus();
  if (initialStatus && initialStatus.paired && initialStatus.phone) {
    renderConnectedView(initialStatus.phone, initialStatus.full_name, initialStatus.chamber_name);
  } else {
    await renderPairingView();
  }
};

const hydrateOnboardPage = async (): Promise<void> => {
  const container = document.getElementById('onboard-card-container');
  if (!container) return;

  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';
  const isBn = state.language === 'bn';
  const phoneDisplay = document.getElementById('onboard-phone-display');
  const nameInput = document.getElementById('onboard-name') as HTMLInputElement | null;
  const chamberInput = document.getElementById('onboard-chamber') as HTMLInputElement | null;
  const statusMsg = document.getElementById('onboard-status-msg');
  const form = container.querySelector<HTMLFormElement>('.onboard-form');
  const submitBtn = document.getElementById('onboard-submit-btn') as HTMLButtonElement | null;

  if (!token) {
    if (phoneDisplay) {
      phoneDisplay.innerHTML = `<span style="color: #ef4444;">${isBn ? '⚠️ কোনো ভ্যালিড টোকেন পাওয়া যায়নি' : '⚠️ No valid onboarding token found'}</span>`;
    }
    if (statusMsg) {
      statusMsg.style.display = 'block';
      statusMsg.innerHTML = `
        <div style="padding: 12px 14px; background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3); border-radius: 8px; color: #ef4444; font-size: 13px;">
          ${isBn ? 'টোকেনটি পাওয়া যায়নি। অনুগ্রহ করে আপনার হোয়াটসঅ্যাপ থেকে আমাদের নম্বরে "Hi" লিখে পাঠান।' : 'Please message "Hi" to our WhatsApp number to generate a fresh 1-tap activation link.'}
        </div>
      `;
    }
    return;
  }

  // Fetch token details from backend
  try {
    const res = await fetch(`/api/whatsapp/onboard-info?token=${encodeURIComponent(token)}`);
    const data = await res.json();
    if (res.ok && data.status === 'ok') {
      if (phoneDisplay) {
        phoneDisplay.innerHTML = `<span>${data.display_phone || data.phone}</span> <span style="font-size: 11px; background: #10b981; color: #fff; padding: 2px 6px; border-radius: 10px; margin-left: 6px;">${isBn ? 'যাচাইকৃত' : 'VERIFIED'}</span>`;
      }
      if (data.is_existing && data.existing_profile) {
        if (nameInput && !nameInput.value) nameInput.value = data.existing_profile.full_name || '';
        if (chamberInput && !chamberInput.value) chamberInput.value = data.existing_profile.chamber_name || '';
      }
    } else {
      if (phoneDisplay) {
        phoneDisplay.innerHTML = `<span style="color: #f59e0b;">${isBn ? '⚠️ লিংকটির মেয়াদ শেষ হয়েছে' : '⚠️ Link Expired or Invalid'}</span>`;
      }
      if (statusMsg) {
        statusMsg.style.display = 'block';
        statusMsg.innerHTML = `
          <div style="padding: 12px 14px; background: rgba(245, 158, 11, 0.1); border: 1px solid rgba(245, 158, 11, 0.3); border-radius: 8px; color: #f59e0b; font-size: 13px;">
            ${isBn ? 'এই অ্যাক্টিভেশন লিংকের মেয়াদ শেষ হয়ে গেছে। আপনার হোয়াটসঅ্যাপ থেকে পুনরায় যেকোনো মেসেজ পাঠালে নতুন লিংক পেয়ে যাবেন।' : 'This onboarding link has expired. Send any message on WhatsApp to get a fresh link.'}
          </div>
        `;
      }
    }
  } catch (e) {
    if (phoneDisplay) {
      phoneDisplay.textContent = isBn ? 'ভেরিফিকেশন সম্পন্ন হচ্ছে...' : 'Verifying connection...';
    }
  }

  // Handle Form Submission
  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!submitBtn) return;

      const fd = new FormData(form);
      const payload = {
        token: token,
        full_name: (fd.get('full_name') as string || '').trim(),
        chamber_name: (fd.get('chamber_name') as string || '').trim(),
        bar_roll: (fd.get('bar_roll') as string || '').trim(),
        email: (fd.get('email') as string || '').trim()
      };

      if (!payload.full_name || !payload.chamber_name) {
        showToast(isBn ? 'সতর্কতা' : 'Required Fields', isBn ? 'অনুগ্রহ করে নাম ও চেম্বার পূরণ করুন' : 'Please provide full name and chamber name', 'warning');
        return;
      }

      submitBtn.disabled = true;
      submitBtn.innerHTML = `<span>⏳</span> <span>${isBn ? 'চেম্বার চালু হচ্ছে...' : 'Activating Chamber...'}</span>`;

      try {
        const res = await fetch('/api/whatsapp/onboard-activate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const result = await res.json();

        if (res.ok && result.status === 'ok') {
          if (result.user?.phone) {
            localStorage.setItem('justor_chamber_phone', result.user.phone);
          }
          
          container.innerHTML = `
            <div style="text-align: center; padding: 30px 10px;">
              <div style="font-size: 52px; margin-bottom: 12px;">🎉</div>
              <h2 style="font-size: 24px; color: #10b981; margin-bottom: 8px;">
                ${isBn ? 'চেম্বার সফলভাবে সক্রিয় হয়েছে!' : 'Chamber Successfully Activated!'}
              </h2>
              <p style="color: var(--text-secondary, #94a3b8); font-size: 15px; max-width: 420px; margin: 0 auto 20px;">
                ${isBn 
                  ? `অভিনন্দন অ্যাডভোকেট <strong>${escapeHtml(payload.full_name)}</strong>! আপনার <strong>${escapeHtml(payload.chamber_name)}</strong> চেম্বার যুক্ত হয়েছে। আপনার হোয়াটসঅ্যাপে একটি স্বাগত মেসেজ পাঠানো হয়েছে।`
                  : `Congratulations Advocate <strong>${escapeHtml(payload.full_name)}</strong>! Your chamber has been activated and your WhatsApp assistant is now live.`}
              </p>

              <div style="padding: 16px; background: rgba(16, 185, 129, 0.08); border: 1px solid rgba(16, 185, 129, 0.25); border-radius: 12px; margin-bottom: 24px; display: inline-flex; align-items: center; gap: 12px;">
                <span style="font-size: 24px;">📱</span>
                <div style="text-align: left;">
                  <strong style="color: #10b981; display: block; font-size: 14px;">WhatsApp Copilot Active</strong>
                  <span style="color: var(--text-secondary, #94a3b8); font-size: 13px;">${escapeHtml(result.user?.phone || payload.full_name)}</span>
                </div>
              </div>

              <div style="display: flex; flex-direction: column; gap: 10px; max-width: 320px; margin: 0 auto;">
                <button type="button" class="button" id="onboard-dashboard-btn" style="padding: 12px 20px; background: #2563eb; color: #fff; font-weight: 600; border-radius: 8px; border: none; cursor: pointer;">
                  ${isBn ? 'চেম্বার ড্যাশবোর্ডে প্রবেশ করুন' : 'Go to Chamber Dashboard'} →
                </button>
              </div>
            </div>
          `;

          showToast(isBn ? 'সফল!' : 'Success!', isBn ? 'চেম্বার সক্রিয় ও হোয়াটসঅ্যাপ কানেক্টেড' : 'Chamber activated & WhatsApp connected', 'positive');

          container.querySelector('#onboard-dashboard-btn')?.addEventListener('click', () => {
            navigate(localizedPath('/workspace/professional', state.language));
          });

          window.setTimeout(() => {
            navigate(localizedPath('/workspace/professional', state.language));
          }, 3200);

        } else {
          submitBtn.disabled = false;
          submitBtn.innerHTML = `<span>🚀</span> <span>${isBn ? 'পুনরায় চেষ্টা করুন' : 'Try Again'}</span>`;
          showToast(isBn ? 'ত্রুটি' : 'Error', result.message || 'Activation failed', 'warning');
        }
      } catch (err) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = `<span>🚀</span> <span>${isBn ? 'পুনরায় চেষ্টা করুন' : 'Try Again'}</span>`;
        showToast(isBn ? 'সংযোগ ত্রুটি' : 'Connection Error', 'Please check network and try again', 'warning');
      }
    });
  }
};

const hydrateRoute = async (path: string): Promise<void> => {
  if (path === '/') await hydrateHome();
  if (path === '/legal-library') await hydrateLibrary();
  if (path === '/guides') await hydrateGuides();
  if (path.startsWith('/guides/')) await hydrateGuideDetail(decodeURIComponent(path.slice('/guides/'.length)));
  if (path.startsWith('/action-guides/')) await hydrateGuideDetail(decodeURIComponent(path.slice('/action-guides/'.length)));
  if (path === '/legal-updates') await hydrateUpdates();
  if (path.startsWith('/legal-updates/')) await hydrateUpdateDetail(decodeURIComponent(path.slice('/legal-updates/'.length)));
  if (path === '/workspace/professional') await hydrateUpdates('[data-professional-updates]');
  hydrateLearningPath(path);
  if (path === '/onboard' || path.startsWith('/onboard')) {
    await hydrateOnboardPage();
  }
  if (path === '/profile' || path === '/account' || path === '/settings') {
    await hydrateWhatsAppConnectionCard();
  }
  if (path === '/amendment-admin') {
    const mount = document.getElementById('amendment-admin-mount');
    if (mount) {
      const { renderAmendmentAdminPage } = await import('../pages/amendment-admin');
      await renderAmendmentAdminPage(mount);
    }
  }
  if (path === '/admin/qa') {
    const mount = document.getElementById('qa-admin-mount');
    if (mount) {
      const { renderQaAdminPage } = await import('../pages/qa-admin');
      await renderQaAdminPage(mount);
    }
  }
};

const formatInlineMarkdown = (text: string, _role?: Role): string => {
  let formatted = escapeHtml(text);
  formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  formatted = formatted.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  formatted = formatted.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  formatted = formatted.replace(/_([^_]+)_/g, '<em>$1</em>');
  formatted = formatted.replace(/`([^`]+)`/g, '<code>$1</code>');

  formatted = formatted
    .replace(/\[(\d+)\]/g, (_match, p1) => {
      const idx = parseInt(p1, 10) - 1;
      return `<button class="inline-citation-chip citation-chip" type="button" data-action="click-citation-index" data-citation-index="${idx}" aria-label="Inspect authority [${p1}]">[${p1}]</button>`;
    })
    .replace(/\[(ACT-\d+|DLR-\d+|CASE-\d+|S\d+)\]/g, '<button class="inline-citation-chip citation-chip" type="button" data-action="click-citation" data-citation="$1">[$1]</button>');
  return formatted;
};

const formatAnswerMarkdown = (text: string, role: Role = 'professional'): string => {
  if (!text) return '';
  const lines = text.split('\n');
  const htmlParts: string[] = [];
  let currentList: string[] = [];
  let currentListType: 'ul' | 'ol' = 'ul';

  const flushList = () => {
    if (currentList.length) {
      const tag = currentListType;
      htmlParts.push(`<${tag} class="answer-list">${currentList.map((li) => `<li>${li}</li>`).join('')}</${tag}>`);
      currentList = [];
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      flushList();
      continue;
    }

    if (line.startsWith('#### ')) {
      flushList();
      htmlParts.push(`<h5 class="answer-h5">${formatInlineMarkdown(line.slice(5), role)}</h5>`);
    } else if (line.startsWith('### ')) {
      flushList();
      htmlParts.push(`<h4 class="answer-h4">${formatInlineMarkdown(line.slice(4), role)}</h4>`);
    } else if (line.startsWith('## ')) {
      flushList();
      htmlParts.push(`<h3 class="answer-h3">${formatInlineMarkdown(line.slice(3), role)}</h3>`);
    } else if (line.startsWith('# ')) {
      flushList();
      htmlParts.push(`<h2 class="answer-h2">${formatInlineMarkdown(line.slice(2), role)}</h2>`);
    } else if (line.startsWith('- ') || line.startsWith('* ') || line.startsWith('• ') || line.startsWith('+ ')) {
      if (currentListType !== 'ul') flushList();
      currentListType = 'ul';
      const cleanContent = line.replace(/^[-*•+]\s+/, '');
      currentList.push(formatInlineMarkdown(cleanContent, role));
    } else if (/^\d+[\.\)]\s+/.test(line)) {
      if (currentListType !== 'ol') flushList();
      currentListType = 'ol';
      const cleanContent = line.replace(/^\d+[\.\)]\s+/, '');
      currentList.push(formatInlineMarkdown(cleanContent, role));
    } else if (line.startsWith('> ')) {
      flushList();
      htmlParts.push(`<blockquote class="answer-quote">${formatInlineMarkdown(line.slice(2), role)}</blockquote>`);
    } else {
      flushList();
      htmlParts.push(`<p class="answer-p">${formatInlineMarkdown(line, role)}</p>`);
    }
  }
  flushList();
  return htmlParts.join('');
};

const renderResearchResult = (result: ResearchResult, role: Role = 'professional', isStreaming: boolean = false): string => {
  const isBnResult = state.language === 'bn' || isBengaliRequested(result.shortAnswer, state.language) || isBengaliRequested(state.lastResearchQuery, state.language);
  const cardLang: 'en' | 'bn' = isBnResult ? 'bn' : 'en';

  const liveGrounding = (result as any).liveWebGrounding as {
    answer?: string;
    search_queries?: string[];
    sources?: Array<{ id?: number; title: string; domain?: string; url: string; trust_tier: string }>;
    related_questions?: string[];
  } | undefined;

  const sourcesCarouselHtml = liveGrounding && liveGrounding.sources?.length ? `
    <details class="perplexity-sources-collapsible" style="margin: 12px 0 16px;">
      <summary class="sources-carousel-summary" style="cursor: pointer; display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: #EFF6FF; border: 1px solid #BFDBFE; border-radius: 10px; transition: background 0.15s ease;">
        <div class="sources-carousel-title" style="display: inline-flex; align-items: center; gap: 8px; color: #1D4ED8; font-weight: 600; font-size: 13.5px;">
          ${icon('globe', 15)}
          <span>${cardLang === 'bn' ? `ওয়েব ও গেজেট তথ্যসূত্র (${liveGrounding.sources.length})` : `Web & Gazette Sources (${liveGrounding.sources.length})`}</span>
          <span class="sources-grounding-tag" style="background: #DBEAFE; color: #1E40AF; padding: 2px 7px; border-radius: 10px; font-size: 10.5px; font-weight: 600;">Google Live 🌐</span>
        </div>
        <span class="sources-toggle-hint" style="font-size: 12px; font-weight: 500; color: #2563EB;">
          <span class="hint-show">${cardLang === 'bn' ? 'তথ্যসূত্র দেখতে ক্লিক করুন ▼' : 'Click to show sources ▼'}</span>
          <span class="hint-hide">${cardLang === 'bn' ? 'তথ্যসূত্র লুকাতে ক্লিক করুন ▲' : 'Click to hide sources ▲'}</span>
        </span>
      </summary>
      <div class="sources-carousel-track" style="margin-top: 10px;">
        ${liveGrounding.sources.map((src, idx) => {
          let domain = src.domain || '';
          if (!domain || domain.toLowerCase().includes('vertexaisearch')) {
            try {
              const u = new URL(src.url);
              domain = u.hostname.replace(/^www\./, '');
              if (domain.toLowerCase().includes('vertexaisearch')) domain = 'gov.bd';
            } catch { domain = 'gov.bd'; }
          }
          let title = src.title || domain;
          if (title.toLowerCase().includes('vertexaisearch') || title.startsWith('http://') || title.startsWith('https://')) {
            title = domain;
          }
          const faviconUrl = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=32`;
          return `
            <a href="${escapeHtml(src.url)}" target="_blank" rel="noopener noreferrer" class="perplexity-source-card" title="${escapeHtml(title)}">
              <div class="source-card-top">
                <img src="${faviconUrl}" alt="" class="source-card-favicon" onerror="this.style.display='none'" />
                <span class="source-card-domain">${escapeHtml(domain)}</span>
                <span class="source-card-idx">${idx + 1}</span>
              </div>
              <div class="source-card-title">${escapeHtml(title)}</div>
            </a>
          `;
        }).join('')}
      </div>
    </details>
  ` : '';

  const relatedQuestionsHtml = liveGrounding && liveGrounding.related_questions?.length ? `
    <div class="perplexity-related-section">
      <div class="related-header">
        ${icon('arrow', 14)}
        <span>${cardLang === 'bn' ? 'সম্পর্কিত অনুসন্ধান ও পরবর্তী পদক্ষেপ' : 'Related Questions & Next Steps'}</span>
      </div>
      <div class="related-questions-list">
        ${liveGrounding.related_questions.map((q) => `
          <button type="button" class="related-question-btn" data-action="ask-related-question" data-question="${escapeHtml(q)}">
            <span>${escapeHtml(q)}</span>
            <span class="related-question-arrow">→</span>
          </button>
        `).join('')}
      </div>
    </div>
  ` : '';



  const sources = result.authorities ?? [];
  const hasLiveSearch = Boolean(liveGrounding && ((liveGrounding.sources && liveGrounding.sources.length > 0) || (liveGrounding.search_queries && liveGrounding.search_queries.length > 0)));
  const liveQueries: string[] = liveGrounding?.search_queries || [];
  const liveSources = liveGrounding?.sources || [];
  const liveDomains = Array.from(new Set(liveSources.map((s) => s.domain).filter(Boolean)));

  const researchCardHeader = `
    <div class="research-card-header">
      <div class="research-header-badges">
        <span class="exec-badge exec-badge-verified">
          <span class="badge-dot"></span>
          <span>${cardLang === 'bn' ? 'বাংলাদেশ আইন ভেরিফাইড' : 'Bangladesh Law Verified'}</span>
        </span>
        ${hasLiveSearch ? `
          <button class="exec-badge exec-badge-google" type="button" data-action="toggle-google-sources" title="${cardLang === 'bn' ? 'লাইভ তথ্যসূত্র দেখতে ক্লিক করুন' : 'Click to show Live Web Sources'}">
            <span>🌐</span>
            <span>${cardLang === 'bn' ? 'গুগল লাইভ গেজেট' : 'Google Live Grounded'}</span>
            <span class="badge-arrow">▾</span>
          </button>
        ` : ''}
        ${sources.length > 0 ? `
          <button class="exec-badge exec-badge-authorities" type="button" data-action="toggle-authorities-sources" title="${cardLang === 'bn' ? 'আইনি রেফারেন্স দেখতে ক্লিক করুন' : 'Click to show Cited Authorities'}">
            <span>📚</span>
            <span>${sources.length} ${cardLang === 'bn' ? 'আইনি রেফারেন্স' : 'Authorities Cited'}</span>
            <span class="badge-arrow">▾</span>
          </button>
        ` : ''}
      </div>
      <div class="research-header-actions">
        <button class="exec-action-btn copy-answer-btn" type="button" data-action="copy-research-answer" title="${cardLang === 'bn' ? 'উত্তর কপি করুন' : 'Copy Analysis'}">
          ${icon('copy', 13) || icon('arrow', 13)} <span>${ui(cardLang, 'copyAnswer')}</span>
        </button>
        <button class="exec-action-btn memo-print-btn" type="button" data-action="print-legal-memo" title="${cardLang === 'bn' ? 'লিগ্যাল মেমো প্রিন্ট / পিডিএফ' : 'Export Legal Memo (PDF / Print)'}">
          ${icon('source', 13)} <span>${cardLang === 'bn' ? 'লিগ্যাল মেমো (PDF)' : 'Legal Memo (PDF)'}</span>
        </button>
      </div>
    </div>
  `;

  const directAnswerHtml = isStreaming ? `
    <section class="direct-answer-section">
      <h3 class="sr-only">${ui(cardLang, 'directAnswer')}</h3>
      <div class="direct-answer-content research-formatted-markdown" data-streaming-target>
        <span class="streaming-pulse-cursor" aria-hidden="true"></span>
      </div>
    </section>
  ` : `
    <section class="direct-answer-section">
      <h3 class="sr-only">${ui(cardLang, 'directAnswer')}</h3>
      <div class="direct-answer-content research-formatted-markdown">
        ${formatAnswerMarkdown(result.shortAnswer || '', role)}
      </div>
    </section>
  `;

  const keyLegalBasisHtml = (result.applicableLaw?.length || result.relevantCases?.length) ? `
    <section class="key-legal-basis-section" style="margin: 18px 0;">
      <h3 style="font-size: 16px; margin: 0 0 8px 0; color: #1E293B;">${ui(cardLang, 'keyLegalBasis')}</h3>
      <ul class="legal-basis-list" style="margin: 0; padding-left: 20px; color: #334155; font-size: 14px;">
        ${(result.applicableLaw || []).map(law => `<li style="margin-bottom: 4px;">${escapeHtml(law)}</li>`).join('')}
        ${(result.relevantCases || []).map(c => `<li style="margin-bottom: 4px;">${escapeHtml(c)}</li>`).join('')}
      </ul>
    </section>
  ` : '';

  const selectedIdx = Math.min(Math.max(0, state.selectedSource ?? 0), Math.max(0, sources.length - 1));
  const sourcesListHtml = sources.length ? `
    <details class="sources-collapsible" style="margin: 18px 0;" open>
      <summary class="sources-summary" style="cursor: pointer; font-weight: 600; font-size: 13.5px; color: #1E38C8; display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 10px; transition: background 0.15s ease;">
        <span style="display: inline-flex; align-items: center; gap: 8px;">
          ${icon('book', 15)}
          <span>${ui(cardLang, 'sources')} (${sources.length})</span>
        </span>
        <span class="sources-expand-hint" style="font-size: 12px; font-weight: 500; color: #64748B;">
          <span class="hint-show">${cardLang === 'bn' ? 'তথ্যসূত্র দেখতে ক্লিক করুন ▼' : 'Click to show sources ▼'}</span>
          <span class="hint-hide">${cardLang === 'bn' ? 'তথ্যসূত্র লুকাতে ক্লিক করুন ▲' : 'Click to hide sources ▲'}</span>
        </span>
      </summary>
      <div class="citation-list" style="display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; padding: 12px; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 10px;">
        ${sources.map((source, index) => `
          <button type="button" data-result-source="${index}" class="citation-chip ${index === selectedIdx ? 'active' : ''}" aria-label="Inspect authority [${index + 1}]: ${escapeHtml(source.authority || source.title)}">
            <span>[${index + 1}]</span>
            <strong>${escapeHtml(source.authority || source.title)}</strong>
            ${source.provision ? `<span>— ${escapeHtml(source.provision)}</span>` : ''}
            ${verificationBadge(source.verificationStatus || source.status)}
          </button>
        `).join('')}
      </div>
      <div class="active-authority-panel-wrapper" style="margin-top: 14px;">
        ${sourcePanel(sources[selectedIdx], 'result-source-panel')}
      </div>
    </details>
  ` : '';

  const researchProcessAccordion = `
    <details class="reasoning-accordion" style="margin: 18px 0;">
      <summary class="reasoning-summary" style="cursor: pointer; font-weight: 500; font-size: 13.5px; color: #64748B;">
        <span class="reasoning-icon">⚖️</span>
        <span class="reasoning-heading">${ui(cardLang, 'howAnswerProduced')}</span>
        ${hasLiveSearch ? `<span class="reasoning-badge-google">🌐 ${cardLang === 'bn' ? 'গুগল লাইভ সার্চ যুক্ত' : 'Google Search Grounded'}</span>` : ''}
        <span class="reasoning-chevron">▼</span>
      </summary>
      <div class="reasoning-steps-list">
        <div class="reasoning-step-item">
          <div class="step-num">1</div>
          <div class="step-content">
            <strong>${cardLang === 'bn' ? 'আইনি বিষয় ও এখতিয়ার সনাক্তকরণ' : 'Legal Issue & Jurisdiction Identified'}</strong>
            <p>${cardLang === 'bn' ? 'সংশ্লিষ্ট আইনি ক্ষেত্র (দেওয়ানি / ফৌজদারি / এনআই অ্যাক্ট / সুনির্দিষ্ট প্রতিকার) বিশ্লেষণ ও প্রযোজ্য ধারা নির্ধারণ।' : 'Analyzed legal domains (CPC / CrPC / NI Act / SRA / MFLO) and mapped specific controlling provisions.'}</p>
          </div>
        </div>
        ${hasLiveSearch ? `
          <div class="reasoning-step-item is-google-step">
            <div class="step-num">🌐</div>
            <div class="step-content">
              <strong>${cardLang === 'bn' ? 'গুগল লাইভ সার্চ ও সরকারি গেজেট অনুসন্ধান' : 'Google Live Web Search & Gazette Grounding'}</strong>
              <p>${cardLang === 'bn'
                ? `বাস্তব সময়ে গুগল সার্চ গ্রাউন্ডিংয়ের মাধ্যমে সর্বশেষ সরকারি প্রজ্ঞাপন, গেজেট ও সার্কুলার যাচাই (${liveSources.length}টি লাইভ ওয়েব সোর্স অন্তর্ভুক্ত)।`
                : `Queried real-time Google Search grounding to verify recent Bangladesh government notifications, official gazettes, and ministry circulars (${liveSources.length} live web source${liveSources.length === 1 ? '' : 's'} verified).`}</p>
              ${liveQueries.length > 0 ? `
                <div class="grounding-search-queries">
                  <span class="query-tag-label">${cardLang === 'bn' ? 'অনুসন্ধান কুয়েরি:' : 'Search Queries:'}</span>
                  ${liveQueries.map(q => `<code class="grounding-query-pill">${escapeHtml(q)}</code>`).join(' ')}
                </div>
              ` : ''}
              ${liveDomains.length > 0 ? `
                <div class="grounding-domains-verified">
                  <span class="query-tag-label">${cardLang === 'bn' ? 'যাচাইকৃত পোর্টাল:' : 'Verified Portals:'}</span>
                  ${liveDomains.map(d => `<span class="grounding-domain-tag">🏛️ ${escapeHtml(d)}</span>`).join(' ')}
                </div>
              ` : ''}
            </div>
          </div>
        ` : ''}
        <div class="reasoning-step-item">
          <div class="step-num">${hasLiveSearch ? '3' : '2'}</div>
          <div class="step-content">
            <strong>${cardLang === 'bn' ? 'আইনি বিধান ও নজির সংগ্রহ' : 'Primary Authorities Retrieved'}</strong>
            <p>${cardLang === 'bn' ? `ক্যানোনিকাল আইন ভাণ্ডার থেকে ${sources.length}টি প্রাথমিক বিধান ও সুপ্রিম কোর্টের নজির পর্যালোচনা।` : `Retrieved ${sources.length} primary provisions & precedential holdings from canonical repository.`}</p>
          </div>
        </div>
        <div class="reasoning-step-item">
          <div class="step-num">${hasLiveSearch ? '4' : '3'}</div>
          <div class="step-content">
            <strong>${cardLang === 'bn' ? 'আইন ও সংশোধনী অবস্থা যাচাই' : 'Statutory & Amendment State Verification'}</strong>
            <p>${cardLang === 'bn' ? '২০২৬ সালের সংশোধনী, গেজেট কার্যকারিতা এবং বিধিবদ্ধ ধারার হুবহু নির্ভরযোগ্যতা ক্রস-ভেরিফিকেশন।' : 'Cross-referenced temporal validity (2026 amendments), gazette status, and statutory text accuracy.'}</p>
          </div>
        </div>
        <div class="reasoning-step-item">
          <div class="step-num">${hasLiveSearch ? '5' : '4'}</div>
          <div class="step-content">
            <strong>${cardLang === 'bn' ? 'নির্ভরযোগ্য সমন্বিত আইনি বিশ্লেষণ' : 'Grounded Legal Synthesis Generated'}</strong>
            <p>${hasLiveSearch
              ? (cardLang === 'bn' ? 'আইনি ভিত্তি ও গুগল লাইভ সার্চ তথ্যের সুনির্দিষ্ট সংযোগে তৈরি চূড়ান্ত বিশ্লেষণ।' : 'Synthesized structured legal guidance harmonizing statutory law with live verified web intelligence.')
              : (cardLang === 'bn' ? 'উদ্ধৃত কর্তৃপক্ষের কঠোর কাঠামোর মধ্যে প্রস্তুতকৃত আইনি দিকনির্দেশনা।' : 'Synthesized structured legal guidance strictly constrained to the cited authorities.')}</p>
          </div>
        </div>
      </div>
    </details>`;

  const fullAnalysisHtml = (result.applicationToFacts || result.qualifications?.length || result.limitations) ? `
    <details class="full-analysis-accordion" style="margin: 18px 0;">
      <summary class="full-analysis-summary" style="cursor: pointer; font-weight: 600; font-size: 14px; color: #334155;">
        <span>${ui(cardLang, 'fullAnalysis')}</span>
        <span class="sources-chevron">▼</span>
      </summary>
      <div class="full-analysis-body" style="padding-top: 10px;">
        ${result.applicationToFacts ? `<div class="analysis-subblock"><h4>Application to Facts</h4><p>${escapeHtml(result.applicationToFacts)}</p></div>` : ''}
        ${result.qualifications?.length ? `<div class="analysis-subblock"><h4>Exceptions & Qualifications</h4><ul>${result.qualifications.map(q => `<li>${escapeHtml(q)}</li>`).join('')}</ul></div>` : ''}
        ${result.limitations ? `<div class="analysis-subblock"><h4>Coverage / Limitations</h4><p>${escapeHtml(result.limitations)}</p></div>` : ''}
      </div>
    </details>
  ` : '';

  const counselNotice = `
    <div class="professional-counsel-trigger">
      <p>ℹ️ ${cardLang === 'bn' 
        ? 'এই বিষয়টি নির্দিষ্ট আইনি বিধান ও তথ্যের উপর নির্ভরশীল। আপনার পরিস্থিতির যথাযথ পদক্ষেপের জন্য একজন যোগ্য আইনজীবীর সাথে পরামর্শ করুন।'
        : 'This matter involves specific statutory provisions and facts. Consult a qualified Bangladesh advocate for individualized legal representation.'}
      </p>
    </div>
  `;

  const feedbackWidget = `
    <div class="answer-feedback-card" data-feedback-widget>
      <div class="feedback-header">
        <span class="feedback-prompt">${ui(cardLang, 'feedbackPrompt')}</span>
        <div class="feedback-btn-group">
          <button class="feedback-thumb-btn" type="button" data-action="feedback-positive" title="Helpful">${ui(cardLang, 'feedbackHelpful')}</button>
          <button class="feedback-thumb-btn" type="button" data-action="feedback-negative-toggle" title="Report an issue">${ui(cardLang, 'feedbackReportIssue')}</button>
        </div>
      </div>
      <div class="feedback-drawer" data-feedback-drawer hidden>
        <form class="feedback-form" data-action="submit-qa-feedback">
          <label for="feedback-category"><strong>${ui(cardLang, 'whatWentWrong')}</strong></label>
          <select id="feedback-category" name="category" required>
            <option value="">${ui(cardLang, 'selectIssueCategory')}</option>
            <option value="wrong_law">${ui(cardLang, 'wrongLaw')}</option>
            <option value="wrong_citation">${ui(cardLang, 'wrongCitation')}</option>
            <option value="outdated_law">${ui(cardLang, 'outdatedLaw')}</option>
            <option value="missing_authority">${ui(cardLang, 'missingAuthority')}</option>
            <option value="incomplete_answer">${ui(cardLang, 'incompleteAnswer')}</option>
            <option value="misunderstood_question">${ui(cardLang, 'misunderstoodQuestion')}</option>
            <option value="other">${ui(cardLang, 'otherIssue')}</option>
          </select>
          <textarea name="comment" rows="2" placeholder="${cardLang === 'bn' ? 'ঐচ্ছিক বিবরণ (যেমন: কোন ধারা বা মামলা ভুল ছিল)...' : 'Optional details (e.g. which section or case was incorrect)...'}"></textarea>
          <div class="feedback-actions">
            <button class="button button-small" type="submit">${ui(cardLang, 'submitFeedback')}</button>
            <button class="button button-small button-secondary" type="button" data-action="close-feedback-drawer">${ui(cardLang, 'cancel')}</button>
          </div>
          <div style="margin-top: 10px; padding-top: 8px; border-top: 1px dashed var(--border-color, #232B3E); text-align: right;">
            <a href="https://docs.google.com/forms/d/e/1FAIpQLSdMfVydj2kMXZkf3SpYi_soA37YtTmAIB7VquPNkadYOmLSrg/viewform" target="_blank" rel="noopener" style="font-size: 12px; color: var(--justor-blue, #1E38C8); text-decoration: underline;">
              📋 ${cardLang === 'bn' ? 'ব্যবহারকারী সমীক্ষা পূরণ করুন ↗' : 'Complete User Experience Survey ↗'}
            </a>
          </div>
        </form>
      </div>
    </div>`;

  return `
    <div class="research-result-layout research-result-modern">
      <article class="research-analysis">
        ${researchCardHeader}
        ${sourcesCarouselHtml}
        ${directAnswerHtml}
        <div class="post-streaming-metadata" ${isStreaming ? 'style="display: none;"' : ''}>
          ${keyLegalBasisHtml}
          ${sourcesListHtml}
          ${relatedQuestionsHtml}
          ${researchProcessAccordion}
          ${fullAnalysisHtml}
          ${counselNotice}
          ${feedbackWidget}
        </div>
      </article>
    </div>`;
};

let activeStreamingCancel: (() => void) | null = null;

const streamTextToElement = async (
  targetEl: HTMLElement,
  fullText: string,
  role: Role,
  metadataEl: HTMLElement | null,
  scrollContainer: HTMLElement | null
): Promise<void> => {
  if (activeStreamingCancel) {
    activeStreamingCancel();
    activeStreamingCancel = null;
  }

  return new Promise<void>((resolve) => {
    if (!fullText || fullText.trim().length === 0) {
      if (metadataEl) {
        metadataEl.style.display = 'block';
        metadataEl.classList.add('fade-in');
      }
      resolve();
      return;
    }

    const tokens = fullText.match(/\S+|\s+/g) || [fullText];
    let currentIndex = 0;
    let accumulatedText = '';
    let isCancelled = false;

    activeStreamingCancel = () => {
      isCancelled = true;
      targetEl.innerHTML = formatAnswerMarkdown(fullText, role);
      if (metadataEl) {
        metadataEl.style.display = 'block';
        metadataEl.classList.add('fade-in');
      }
      resolve();
    };

    // Smooth typing speed: 2 tokens per tick every 20ms (~60 words per second)
    const stepInterval = 20;
    const tokensPerStep = 2;

    const intervalId = setInterval(() => {
      if (isCancelled) {
        clearInterval(intervalId);
        return;
      }

      if (currentIndex >= tokens.length) {
        clearInterval(intervalId);
        activeStreamingCancel = null;
        targetEl.innerHTML = formatAnswerMarkdown(fullText, role);
        if (metadataEl) {
          metadataEl.style.display = 'block';
          metadataEl.classList.add('fade-in');
        }
        if (scrollContainer) {
          const isNearBottom = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight < 250;
          if (isNearBottom) {
            scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
          }
        }
        resolve();
        return;
      }

      const nextChunk = tokens.slice(currentIndex, currentIndex + tokensPerStep).join('');
      accumulatedText += nextChunk;
      currentIndex += tokensPerStep;

      targetEl.innerHTML = formatAnswerMarkdown(accumulatedText, role) + '<span class="streaming-pulse-cursor" aria-hidden="true"></span>';

      if (scrollContainer) {
        const isNearBottom = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight < 250;
        if (isNearBottom) {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }
      }
    }, stepInterval);
  });
};

const printChambersLegalMemo = (result: ResearchResult, role: Role, language: Language): void => {
  const memoId = `JAI-MEMO-${Date.now().toString(36).toUpperCase()}`;
  const nowStr = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const sources = result.authorities ?? [];
  const existingMemo = document.querySelector('.justor-legal-memo-printable');
  if (existingMemo) existingMemo.remove();

  const memoHtml = `
    <div class="justor-legal-memo-printable">
      <div class="memo-header-band">
        <div class="memo-brand-block">
          <h1 class="memo-title">JUSTOR AI — LEGAL INTELLIGENCE MEMORANDUM</h1>
          <p class="memo-subtitle">Source-Verified Bangladesh Legal Research & Statutory Authority Brief</p>
        </div>
        <div class="memo-meta-block">
          <div><strong>Reference:</strong> ${memoId}</div>
          <div><strong>Date:</strong> ${nowStr}</div>
          <div><strong>Practice Level:</strong> ${role.toUpperCase()}</div>
          <div><strong>Language:</strong> ${language.toUpperCase()}</div>
          <div><strong>Verification:</strong> 7-Gate Evidence Engine Verified ✓</div>
        </div>
      </div>

      <div class="memo-section">
        <h2 class="memo-sec-heading">I. LEGAL RESEARCH & GROUNDED ANALYSIS</h2>
        <div class="memo-body-markdown">
          ${formatAnswerMarkdown(result.shortAnswer)}
        </div>
      </div>

      ${sources.length ? `
      <div class="memo-section">
        <h2 class="memo-sec-heading">II. CONTROLLING STATUTORY & JUDICIAL AUTHORITIES</h2>
        <table class="memo-authorities-table">
          <thead>
            <tr>
              <th style="width: 45px;">ID</th>
              <th>Authority / Statute / Case</th>
              <th>Provision / Citation</th>
              <th>Verification Tier</th>
              <th>Official Source URL</th>
            </tr>
          </thead>
          <tbody>
            ${sources.map((s, idx) => `
              <tr>
                <td><strong>[${s.id || idx + 1}]</strong></td>
                <td>${escapeHtml(s.authority || s.title)}</td>
                <td>${escapeHtml(s.provision || s.citation || '—')}</td>
                <td><span class="memo-badge">${escapeHtml(s.verificationStatus || 'PRIMARY SOURCE ✓')}</span></td>
                <td><small>${escapeHtml(s.url || 'bdlaws.minlaw.gov.bd')}</small></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>` : ''}

      <div class="memo-footer-disclaimer">
        <p><strong>Professional Verification Notice:</strong> This research memorandum was generated by the Justor AI Legal Evidence Engine V2. Statutory sections are verified against official Laws of Bangladesh (bdlaws.minlaw.gov.bd) and landmark Supreme Court ratios. Practitioners must confirm applicability to specific case facts prior to court filing.</p>
        <div class="memo-sign-line">
          <span>Prepared by Justor AI Legal Intelligence</span>
          <span>Chambers Verification Stamp: _______________________</span>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', memoHtml);
  window.print();
  setTimeout(() => {
    document.querySelector('.justor-legal-memo-printable')?.remove();
  }, 2000);
};

const renderPilotModal = (): string => {
  const isBn = state.language === 'bn';
  const savedPhone = localStorage.getItem('justor_chamber_phone') || '';

  return `
  <div class="pilot-modal-overlay" data-pilot-modal-overlay>
    <div class="pilot-modal-card" role="dialog" aria-modal="true" aria-labelledby="pilot-modal-title">
      <div class="pilot-modal-header">
        <div>
          <span class="pilot-kicker">${isBn ? 'সুপ্রিম কোর্ট ও জেলা বার আইনজীবীদের জন্য' : 'EXCLUSIVELY FOR ADVOCATES & CHAMBERS'}</span>
          <h2 id="pilot-modal-title" class="pilot-title">${isBn ? 'ফাউন্ডিং অ্যাডভোকেট মেম্বারশিপ' : 'Join Founding Advocate Tier'}</h2>
        </div>
        <button type="button" class="pilot-modal-close" data-action="close-pilot-modal" aria-label="Close">✕</button>
      </div>
      <p class="pilot-desc">
        ${isBn 
          ? 'হোয়াটসঅ্যাপ গেটওয়ে ও চেম্বার ওএস ডকেটের মাধ্যমে আপনার আইনি প্র্যাকটিসকে স্বয়ংক্রিয় করুন। প্রতিদিন সকাল ৮:০০টায় কেস ব্রিফিং, কোর্ট থেকে ভয়েস নোট ডকেট ইনজেকশন ও আনলিমিটেড লিগ্যাল রিসার্চ।'
          : 'Automate your legal practice with WhatsApp Chamber Gateway & Docket Vault. Get daily 8:00 AM WhatsApp case briefs, instant voice-note docket logging, and unlimited verified legal research.'}
      </p>

      <!-- Plan Selection Cards -->
      <div class="checkout-plans-grid">
        <div class="checkout-plan-card is-selected" data-select-plan="founding_advocate" data-price="499" role="button" tabindex="0">
          <span class="checkout-plan-badge">${isBn ? 'জনপ্রিয়' : 'SOLO ADVOCATE'}</span>
          <div class="checkout-plan-title">
            <span>${isBn ? 'ফাউন্ডিং অ্যাডভোকেট' : 'Founding Advocate'}</span>
            <span class="plan-check-icon">✓</span>
          </div>
          <div class="checkout-plan-price">৳499 <small>/ ${isBn ? 'মাস' : 'month'}</small></div>
          <ul class="checkout-plan-features">
            <li>${isBn ? '১ জন আইনজীবী হোয়াটসঅ্যাপ এক্সেস' : '1 Advocate WhatsApp Access'}</li>
            <li>${isBn ? 'প্রতিদিন সকাল ৮:০০টায় কেস ব্রিফিং' : 'Daily 8:00 AM WhatsApp Briefing'}</li>
            <li>${isBn ? 'কোর্ট করিডোর থেকে ভয়েস নোটে ডকেট এন্ট্রি' : 'Voice-Note Corridor Docket Logging'}</li>
            <li>${isBn ? '১-ক্লিকে হিয়ারিং প্যাক ও ক্রস-একজাম ড্রাফট' : 'Hearing Packs & Cross-Exam Prep'}</li>
          </ul>
        </div>

        <div class="checkout-plan-card" data-select-plan="founding_chamber" data-price="1499" role="button" tabindex="0">
          <span class="checkout-plan-badge" style="background: #D97706;">CHAMBER</span>
          <div class="checkout-plan-title">
            <span>${isBn ? 'ফাউন্ডিং চেম্বার ওএস' : 'Founding Chamber'}</span>
            <span class="plan-check-icon" style="display: none;">✓</span>
          </div>
          <div class="checkout-plan-price">৳1,499 <small>/ ${isBn ? 'মাস' : 'month'}</small></div>
          <ul class="checkout-plan-features">
            <li>${isBn ? '৫ জন আইনজীবী ও অ্যাসোসিয়েট এক্সেস' : 'Up to 5 Advocates & Associates'}</li>
            <li>${isBn ? 'শেয়ার্ড হোয়াটসঅ্যাপ গেটওয়ে ও অটো-রাউটিং' : 'Shared WhatsApp Gateway & Routing'}</li>
            <li>${isBn ? 'সেন্ট্রালাইজড চেম্বার ভল্ট ও নজির সার্চ' : 'Centralized Vault & Precedent RAG'}</li>
            <li>${isBn ? 'হাইকোর্ট ও সুপ্রিম কোর্ট গেজেট মনিটরিং' : 'Supreme Court Gazette Grounding'}</li>
          </ul>
        </div>
      </div>

      <form class="pilot-form" data-action="submit-pilot-form">
        <input type="hidden" name="plan" id="checkout-plan-input" value="founding_advocate" />
        <input type="hidden" name="payment_method" id="checkout-pm-input" value="bkash" />

        <!-- Payment Method Tabs -->
        <div style="margin-bottom: 6px;">
          <label style="font-size: 12px; font-weight: 700; color: #334155; margin-bottom: 6px; display: block;">${isBn ? 'পেমেন্ট মেথড নির্বাচন করুন:' : 'Select Payment Method:'}</label>
          <div class="checkout-payment-methods">
            <button type="button" class="checkout-pm-tab active" data-pm="bkash" data-select-pm="bkash">
              <span style="font-weight: 800; color: inherit;">bKash</span>
            </button>
            <button type="button" class="checkout-pm-tab" data-pm="nagad" data-select-pm="nagad">
              <span style="font-weight: 800; color: inherit;">Nagad</span>
            </button>
            <button type="button" class="checkout-pm-tab" data-pm="card" data-select-pm="card">
              <span>💳 Card / Online</span>
            </button>
          </div>
        </div>

        <!-- Payment Instructions Container -->
        <div id="checkout-pm-instructions" class="checkout-pm-instruction">
          <strong>${isBn ? 'bKash পেমেন্ট নির্দেশনা:' : 'bKash Payment Instructions:'}</strong><br/>
          ১. আপনার bKash অ্যাপ থেকে <strong>Send Money</strong> অথবা <strong>Payment</strong> অপশনে যান: <code style="background: #FFE4E6; padding: 1px 6px; border-radius: 4px; color: #D12053; font-weight: 700;">01712-345678</code><br/>
          ২. টাকার পরিমাণ দিন: <strong id="checkout-display-amount">৳499</strong> (রেফারেন্স: আপনার নাম)<br/>
          ৩. সফল লেনদেনের পর প্রাপ্ত <strong>TrxID</strong> নিচে লিখে একাউন্ট সক্রিয় করুন।
        </div>

        <div class="pilot-form-grid">
          <div class="pilot-field">
            <label for="advocate-name">${isBn ? 'আইনজীবীর নাম *' : 'Advocate Name *'}</label>
            <input type="text" id="advocate-name" name="advocate_name" placeholder="Advocate / Barrister Name" required />
          </div>
          <div class="pilot-field">
            <label for="chamber-name">${isBn ? 'চেম্বার / ফার্মের নাম' : 'Chamber / Firm Name'}</label>
            <input type="text" id="chamber-name" name="chamber_name" placeholder="e.g. Rahman & Associates" />
          </div>
          <div class="pilot-field">
            <label for="bar-association">${isBn ? 'বার এসোসিয়েশন *' : 'Bar Association *'}</label>
            <select id="bar-association" name="bar_association" required>
              <option value="Supreme Court Bar Association (SCBA)">Supreme Court Bar Association (SCBA)</option>
              <option value="Dhaka Bar Association">Dhaka Bar Association</option>
              <option value="Chittagong District Bar Association">Chittagong District Bar Association</option>
              <option value="Sylhet District Bar Association">Sylhet District Bar Association</option>
              <option value="Rajshahi District Bar Association">Rajshahi District Bar Association</option>
              <option value="Other District Bar">Other District Bar</option>
              <option value="In-House Corporate Counsel">In-House Corporate Counsel</option>
            </select>
          </div>
          <div class="pilot-field">
            <label for="advocate-phone">${isBn ? 'হোয়াটসঅ্যাপ / মোবাইল নম্বর *' : 'WhatsApp / Mobile Number *'}</label>
            <input type="tel" id="advocate-phone" name="phone" value="${escapeHtml(savedPhone)}" placeholder="017XXXXXXXX" required />
          </div>
        </div>

        <div class="pilot-field" id="checkout-trx-field">
          <label for="trx-id">${isBn ? 'ট্রানজেকশন আইডি (TrxID) *' : 'Transaction ID (TrxID) *'}</label>
          <input type="text" id="trx-id" name="trx_id" placeholder="e.g. BK98X7Y123" required />
        </div>

        <div class="pilot-actions">
          <button class="button pilot-submit-btn" id="checkout-submit-btn" type="submit">
            ${isBn ? 'মেম্বারশিপ সক্রিয় করুন (৳৪৯৯/মাস)' : 'Activate Founding Membership (৳499/mo)'} ${icon('arrow', 14)}
          </button>
          <button class="button button-outline" type="button" data-action="close-pilot-modal">
            ${isBn ? 'বাতিল' : 'Cancel'}
          </button>
        </div>
      </form>
    </div>
  </div>
  `;
};

const getLiveThinkingSteps = (isLiveSearch: boolean) => {
  if (isLiveSearch) {
    return [
      { title: state.language === 'bn' ? 'আইনি উদ্দেশ্য ও ধারা ম্যাপিং' : 'Legal Intent & Statutory Routing', desc: state.language === 'bn' ? 'বাংলাদেশের আইন ও আদালতের এখতিয়ার নির্ধারণ...' : 'Targeting controlling Bangladesh Acts and legal domain...' },
      { title: state.language === 'bn' ? 'গুগল লাইভ সার্চ ও গেজেট অনুসন্ধান' : 'Google Live Web Search & Gazette Grounding', desc: state.language === 'bn' ? 'লাইভ সরকারি গেজেট, প্রজ্ঞাপন ও সার্কুলার অনুসন্ধান...' : 'Querying live Google Search for official BD gazettes, circulars & portals...' },
      { title: state.language === 'bn' ? 'আইন ও নজির সংগ্রহ' : 'Primary Authority Retrieval', desc: state.language === 'bn' ? '৪৬,০০০+ বিধান ও সুপ্রিম কোর্টের নজির পর্যালোচনা...' : 'Searching 46,000+ provisions & Supreme Court precedent database...' },
      { title: state.language === 'bn' ? '৭-স্তরের তথ্য যাচাইকরণ' : '7-Gate Deterministic Verification', desc: state.language === 'bn' ? 'উদ্ধৃতি ও ২০২৬ সালের সংশোধনী নির্ভুলতা যাচাই...' : 'Validating exact statutory quotes & 2026 amendment deadlines...' },
      { title: state.language === 'bn' ? 'সমন্বিত আইনি বিশ্লেষণ প্রণয়ন' : 'Grounded Legal Synthesis', desc: state.language === 'bn' ? 'আইন ও লাইভ ওয়েব তথ্যের সুনির্দিষ্ট সংযোগে প্রতিবেদন...' : 'Synthesizing comprehensive analysis with real-time web citations...' },
    ];
  }
  return [
    { title: state.language === 'bn' ? 'আইনি উদ্দেশ্য ও ধারা ম্যাপিং' : 'Legal Intent & Statutory Routing', desc: state.language === 'bn' ? 'বাংলাদেশের আইন ও আদালতের এখতিয়ার নির্ধারণ...' : 'Targeting controlling Bangladesh Acts and legal domain...' },
    { title: state.language === 'bn' ? 'আইন ও নজির সংগ্রহ' : 'Primary Authority Retrieval', desc: state.language === 'bn' ? '৪৬,০০০+ বিধান ও সুপ্রিম কোর্টের নজির পর্যালোচনা...' : 'Searching 46,000+ provisions & Supreme Court precedent database...' },
    { title: state.language === 'bn' ? '৭-স্তরের তথ্য যাচাইকরণ' : '7-Gate Deterministic Verification', desc: state.language === 'bn' ? 'উদ্ধৃতি ও ২০২৬ সালের সংশোধনী নির্ভুলতা যাচাই...' : 'Validating exact statutory quotes & 2026 amendment deadlines...' },
    { title: state.language === 'bn' ? 'সমন্বিত আইনি বিশ্লেষণ প্রণয়ন' : 'Grounded Legal Synthesis', desc: state.language === 'bn' ? 'শুধুমাত্র যাচাইকৃত উৎসের ভিত্তিতে প্রতিবেদন প্রস্তুত...' : 'Synthesizing structured legal analysis strictly from verified sources...' },
  ];
};

const renderLiveThinking = (seconds: number, activeStepIndex: number, isLiveSearch = state.liveSearchEnabled): string => {
  const steps = getLiveThinkingSteps(isLiveSearch);
  return `
  <div class="live-thinking-card">
    <div class="live-thinking-header">
      <div class="live-thinking-title">
        <span class="thinking-sparkle">✨</span>
        <strong>${isLiveSearch
          ? (state.language === 'bn' ? 'গুগল লাইভ সার্চ ও আইন অনুসন্ধান...' : 'Google Search & BD Law Grounding...')
          : (state.language === 'bn' ? 'বাংলাদেশের আইন খোঁজা হচ্ছে...' : 'Researching Bangladesh law...')}</strong>
        <span class="thinking-timer">(${seconds.toFixed(1)}s)</span>
      </div>
      <span class="thinking-badge ${isLiveSearch ? 'is-google' : ''}" style="${isLiveSearch ? 'background: #EFF6FF; color: #1D4ED8; border: 1px solid #BFDBFE;' : ''}">
        ${isLiveSearch ? '🌐 Google Live Search Active' : 'AI Brain Active'}
      </span>
    </div>
    <div class="live-thinking-steps">
      ${steps.map((step, idx) => {
        const isDone = idx < activeStepIndex;
        const isActive = idx === activeStepIndex;
        return `
          <div class="live-step-row ${isDone ? 'is-done' : ''} ${isActive ? 'is-active' : ''}">
            <div class="live-step-indicator">
              ${isDone ? '✓' : isActive ? '<span class="step-spinner"></span>' : (idx + 1)}
            </div>
            <div class="live-step-text">
              <strong>${escapeHtml(step.title)}</strong>
              <span>${escapeHtml(step.desc)}</span>
            </div>
          </div>
        `;
      }).join('')}
    </div>
    <div class="thinking-shimmer-preview">
      <div class="shimmer-line line-1"></div>
      <div class="shimmer-line line-2"></div>
      <div class="shimmer-line line-3"></div>
    </div>
  </div>
`;
};

const submitResearch = async (form: HTMLFormElement): Promise<void> => {
  const query = String(new FormData(form).get('query') ?? '').trim();
  if (!query) return;
  const role = (form.dataset.role as Role) || state.role || 'professional';
  const context = form.dataset.contextId ? { id: form.dataset.contextId, title: form.dataset.contextTitle ?? '', topic: form.dataset.contextTopic ?? '' } : undefined;
  const displayQuery = sessionStorage.getItem('justor-chat-display-query') || query;
  sessionStorage.removeItem('justor-chat-display-query');
  
  if (!state.session) {
    sessionStorage.setItem('justor_guest_query_count', String(parseInt(sessionStorage.getItem('justor_guest_query_count') || '0', 10) + 1));
    state.session = authService.signInAsGuest();
    if (!isRoleLocked() && isValidRole(role)) lockUserRole(role);
  }

  const activeThread = chatStore.getOrCreateActiveThread(role);
  chatStore.addMessage(activeThread.id, { sender: 'user', content: displayQuery });

  const scrollArea = document.querySelector<HTMLElement>('[data-chat-scroll]');
  let conversationThread = scrollArea?.querySelector<HTMLElement>('.chat-conversation-thread');

  if (scrollArea && !conversationThread) {
    scrollArea.innerHTML = `<div class="chat-conversation-thread" data-chat-thread-id="${activeThread.id}"></div>`;
    conversationThread = scrollArea.querySelector<HTMLElement>('.chat-conversation-thread');
  }

  const textarea = form.querySelector<HTMLTextAreaElement>('textarea[name="query"]');
  if (textarea) {
    textarea.value = '';
    textarea.style.height = '24px';
  }

  if (conversationThread) {
    const userRowHtml = `
      <div class="chat-message-row user-row">
        <div class="chat-user-bubble">
          <p class="user-query-text">${escapeHtml(displayQuery)}</p>
          <span class="user-bubble-time">Just now</span>
        </div>
      </div>
    `;
    conversationThread.insertAdjacentHTML('beforeend', userRowHtml);
  }

  const thinkingId = `thinking_${Date.now()}`;
  const isLiveSearch = state.liveSearchEnabled;
  if (conversationThread) {
    const thinkingRowHtml = `
      <div class="chat-message-row assistant-row" id="${thinkingId}">
        <div class="chat-assistant-container">
          ${aiAuthorHeader()}
          <div class="assistant-content-wrapper" data-thinking-wrapper>
            ${renderLiveThinking(0, 0, isLiveSearch)}
          </div>
        </div>
      </div>
    `;
    conversationThread.insertAdjacentHTML('beforeend', thinkingRowHtml);
    scrollArea?.scrollTo({ top: scrollArea.scrollHeight, behavior: 'smooth' });
  }

  let elapsed = 0;
  let activeStep = 0;
  const timerInterval = setInterval(() => {
    elapsed += 0.2;
    if (isLiveSearch) {
      if (elapsed > 0.6 && activeStep === 0) activeStep = 1;
      if (elapsed > 1.6 && activeStep === 1) activeStep = 2;
      if (elapsed > 2.6 && activeStep === 2) activeStep = 3;
      if (elapsed > 3.8 && activeStep === 3) activeStep = 4;
    } else {
      if (elapsed > 0.8 && activeStep === 0) activeStep = 1;
      if (elapsed > 2.0 && activeStep === 1) activeStep = 2;
      if (elapsed > 3.4 && activeStep === 2) activeStep = 3;
    }
    
    const thinkingElement = document.getElementById(thinkingId);
    if (!thinkingElement) return;

    const headerTimer = thinkingElement.querySelector<HTMLElement>('.thinking-timer');
    if (headerTimer) headerTimer.textContent = `(${elapsed.toFixed(1)}s)`;
    
    const stepRows = thinkingElement.querySelectorAll<HTMLElement>('.live-step-row');
    stepRows.forEach((row, idx) => {
      const indicator = row.querySelector('.live-step-indicator');
      if (idx < activeStep) {
        row.className = 'live-step-row is-done';
        if (indicator) indicator.innerHTML = '✓';
      } else if (idx === activeStep) {
        row.className = 'live-step-row is-active';
        if (indicator) indicator.innerHTML = '<span class="step-spinner"></span>';
      } else {
        row.className = 'live-step-row';
        if (indicator) indicator.innerHTML = String(idx + 1);
      }
    });
  }, 200);

  try {
    const effectiveLang = isBengaliRequested(query, state.language) ? 'bn' : state.language;
    const backendUrl = (import.meta.env.VITE_BACKEND_URL?.trim() || 'https://justorai-backend.onrender.com').replace(/\/$/, '');
    const liveSearchPromise = state.liveSearchEnabled
      ? fetch(`${backendUrl}/api/search/live-grounding`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query, language: effectiveLang, user_role: role }),
        })
          .then((r) => (r.ok ? r.json() : null))
          .catch((err) => {
            console.warn('Live search grounding request failed:', err);
            return null;
          })
      : Promise.resolve(null);

    const [liveWebGroundingData, result] = await Promise.all([
      liveSearchPromise,
      streamResearch(
        query,
        role,
        effectiveLang,
        (stepEvent) => {
          const thinkingElement = document.getElementById(thinkingId);
          if (!thinkingElement) return;
          const stepRows = thinkingElement.querySelectorAll<HTMLElement>('.live-step-row');
          const sIndex = Math.min(stepEvent.step - 1, stepRows.length - 1);
          if (sIndex >= 0 && stepRows[sIndex]) {
            const row = stepRows[sIndex];
            const summaryEl = row.querySelector('.live-step-summary');
            if (summaryEl && stepEvent.summary) {
              summaryEl.textContent = stepEvent.summary;
            }
            if (stepEvent.status === 'completed' || stepEvent.status === 'passed') {
              row.className = 'live-step-row is-done';
              const ind = row.querySelector('.live-step-indicator');
              if (ind) ind.innerHTML = '✓';
            }
          }
        },
        undefined,
        context
      ),
    ]);
    clearInterval(timerInterval);

    if (liveWebGroundingData && liveWebGroundingData.status === 'ok') {
      (result as any).liveWebGrounding = liveWebGroundingData;
      const webAnswer = (liveWebGroundingData.answer || '').trim();
      if (webAnswer.length > 30) {
        const ragAnswer = (result.shortAnswer || '').trim();
        if (ragAnswer && ragAnswer.length > 40) {
          const sectionTitle = effectiveLang === 'bn'
            ? '🌐 বাস্তব-সময়ের সরকারি গেজেট ও সাম্প্রতিক আপডেট (Google Live Search)'
            : '🌐 Real-Time Gazette & Web Intelligence (Google Live Search)';
          result.shortAnswer = `${ragAnswer}\n\n### ${sectionTitle}\n\n${webAnswer}`;
        } else {
          result.shortAnswer = webAnswer;
        }
      }
    }

    state.lastResearch = result;
    state.lastResearchRole = role;
    state.lastResearchQuery = query;
    state.selectedSource = 0;

    const assistantRowId = `assistant_msg_${Date.now()}`;
    const thinkingElement = document.getElementById(thinkingId);
    if (thinkingElement) {
      thinkingElement.outerHTML = `
        <div class="chat-message-row assistant-row" id="${assistantRowId}">
          <div class="chat-assistant-container">
            ${aiAuthorHeader()}
            <div class="assistant-content-wrapper">
              ${renderResearchResult(result, role, true)}
            </div>
          </div>
        </div>
      `;
    }

    const rowEl = document.getElementById(assistantRowId);
    const streamingTarget = rowEl?.querySelector<HTMLElement>('[data-streaming-target]');
    const metadataEl = rowEl?.querySelector<HTMLElement>('.post-streaming-metadata');
    const scrollContainer = document.querySelector<HTMLElement>('[data-chat-scroll]');

    try {
      if (streamingTarget) {
        await streamTextToElement(streamingTarget, result.shortAnswer, role, metadataEl ?? null, scrollContainer);
      }
    } catch (streamErr) {
      console.warn('Streaming error, displaying full result immediately:', streamErr);
      if (streamingTarget) streamingTarget.innerHTML = formatAnswerMarkdown(result.shortAnswer, role);
      if (metadataEl) metadataEl.style.display = 'block';
    }

    chatStore.addMessage(activeThread.id, { sender: 'assistant', content: result.shortAnswer, result });

    const sidebarHistory = document.querySelector('.sidebar-history-section');
    if (sidebarHistory) {
      const threads = chatStore.getThreadsByRole(role);
      const activeId = chatStore.getActiveThreadId(role);
      const listContainer = sidebarHistory.querySelector('.history-threads-list');
      if (listContainer) {
        listContainer.innerHTML = threads.map((t) => {
          const isActive = t.id === activeId;
          const timeStr = formatRelativeTime(t.updatedAt);
          return `
            <div class="history-thread-item ${isActive ? 'is-active' : ''}" data-action="select-thread" data-thread-id="${t.id}">
              <div class="thread-item-content">
                <strong class="thread-item-title" title="${escapeHtml(t.title)}">${escapeHtml(t.title)}</strong>
                <span class="thread-item-time">${timeStr}</span>
              </div>
              <button class="thread-delete-btn" type="button" data-action="delete-thread" data-thread-id="${t.id}" title="${state.language === 'bn' ? `গবেষণা মুছুন: ${escapeHtml(t.title)}` : `Delete research thread: ${escapeHtml(t.title)}`}" aria-label="${state.language === 'bn' ? `গবেষণা মুছুন: ${escapeHtml(t.title)}` : `Delete research thread: ${escapeHtml(t.title)}`}">✕</button>
            </div>
          `;
        }).join('');
      }
    }

    const topbarTitle = document.querySelector<HTMLElement>('.topbar-thread-title');
    if (topbarTitle && activeThread.title !== 'New Legal Research') {
      topbarTitle.textContent = `· ${activeThread.title}`;
    }

    const quota = result.quota as { remaining: number; limit: number; partner_org?: string; unlimited?: boolean } | undefined;
    if (quota) {
      const isVip = isUnlimitedUser(state.session?.user?.email || getStoredProfile().email) || quota.unlimited || quota.limit >= 999999;
      if (isVip) {
        document.querySelectorAll<HTMLElement>('[data-credit-remaining]').forEach((el) => { el.textContent = '∞'; });
        document.querySelectorAll<HTMLElement>('[data-credit-limit]').forEach((el) => { el.textContent = '∞'; });
        document.querySelectorAll<HTMLElement>('[data-credit-pill]').forEach((pill) => {
          pill.classList.remove('credit-pill-amber', 'credit-pill-red');
          pill.classList.add('credit-pill-vip');
        });
        document.querySelectorAll<HTMLElement>('[data-quota]').forEach((el) => {
          el.textContent = '⭐ Unlimited VIP Legal Research & OCR Active';
        });
      } else {
        const remaining = quota.remaining;
        const limit = quota.limit;
        // Update credit counter pill in topbar
        document.querySelectorAll<HTMLElement>('[data-credit-remaining]').forEach((el) => { el.textContent = String(remaining); });
        document.querySelectorAll<HTMLElement>('[data-credit-limit]').forEach((el) => { el.textContent = String(limit); });
        // Color-code the pill
        document.querySelectorAll<HTMLElement>('[data-credit-pill]').forEach((pill) => {
          const pct = limit > 0 ? remaining / limit : 0;
          pill.classList.toggle('credit-pill-amber', pct < 0.3 && pct >= 0.1);
          pill.classList.toggle('credit-pill-red', pct < 0.1);
        });
        // Update legacy quota line in composer
        document.querySelectorAll<HTMLElement>('[data-quota]').forEach((el) => { el.textContent = `${remaining} of ${limit} credits remaining today`; });
      }
    }
    
    scrollArea?.scrollTo({ top: scrollArea.scrollHeight, behavior: 'smooth' });
  } catch (error) {
    clearInterval(timerInterval);
    const err = error instanceof Error ? error.message : '';
    const isVipUser = isUnlimitedUser(state.session?.user?.email || getStoredProfile().email);
    const isQuotaExhausted = !isVipUser && (err === 'quota-exceeded' || err.includes('quota') || err.includes('429'));
    const isBn = state.language === 'bn';
    let paywallHtml = '';
    if (isQuotaExhausted) {
      const limit = getDailyLimit();
      paywallHtml = `
        <div class="quota-paywall-card">
          <div class="paywall-icon" aria-hidden="true">🔒</div>
          <div class="paywall-body">
            <strong class="paywall-title">${isBn ? 'দৈনিক ক্রেডিট শেষ হয়েছে' : 'Daily credits used up'}</strong>
            <p class="paywall-desc">${isBn ? `আজকের ${limit}টি ক্রেডিট ব্যবহার হয়ে গেছে।` : `You've used all ${limit} research credits for today.`}</p>
            <p class="paywall-reset">${isBn ? 'মধ্যরাতে (বাংলাদেশ সময়) রিসেট হবে।' : 'Credits reset at midnight (Bangladesh time).'}</p>
          </div>
          <div class="paywall-actions">
            <button class="button paywall-upgrade-btn" type="button" data-action="open-pilot-modal">
              ${isBn ? 'ফাউন্ডিং পাইলট — ৳২০০/মাস' : 'Join Founding Pilot — ৳200/mo'}
            </button>
            <p class="paywall-pilot-desc">${isBn ? 'আইনজীবীদের জন্য আনলিমিটেড রিসার্চ অ্যাক্সেস।' : 'Unlimited research access for advocates.'}</p>
          </div>
        </div>`;
    }
    const message = isQuotaExhausted ? '' :
      err === 'authentication-required'
        ? 'Your session has ended. Sign in again to continue.'
        : 'The legal research service is unavailable. No answer was generated.';
    const thinkingElement = document.getElementById(thinkingId);
    if (thinkingElement) {
      thinkingElement.outerHTML = `
        <div class="chat-message-row assistant-row">
          <div class="chat-assistant-container">
            <div class="assistant-avatar-badge"><img src="/visuals/justor-mark.png" alt="Justor AI"></div>
            <div class="assistant-content-wrapper">
              ${paywallHtml || unavailable(message)}
            </div>
          </div>
        </div>
      `;
    }
  }
};

const openProvisionModal = async (actName: string, sectionRef: string): Promise<void> => {
  const existing = document.querySelector('.provision-modal-backdrop');
  if (existing) existing.remove();

  const backdrop = document.createElement('div');
  backdrop.className = 'provision-modal-backdrop';
  backdrop.innerHTML = `
    <div class="provision-modal-drawer" role="dialog" aria-modal="true" aria-labelledby="modal-prov-title">
      <div class="provision-modal-header">
        <div>
          <span class="section-kicker">Statutory Provision Record</span>
          <h2 id="modal-prov-title">${escapeHtml(actName)} — ${escapeHtml(sectionRef)}</h2>
        </div>
        <button class="modal-close-btn" type="button" data-action="close-provision-modal" aria-label="Close modal">✕</button>
      </div>
      <div class="provision-modal-body">
        <div class="data-loading">Fetching verified gazette text for ${escapeHtml(sectionRef)}…</div>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);

  try {
    const backendUrl = (import.meta.env.VITE_BACKEND_URL?.trim() || 'https://justorai-backend.onrender.com').replace(/\/$/, '');
    const res = await fetch(`${backendUrl}/api/provisions/lookup?act=${encodeURIComponent(actName)}&section=${encodeURIComponent(sectionRef)}`);
    const modalBody = backdrop.querySelector('.provision-modal-body');
    if (!modalBody) return;

    if (res.ok) {
      const data = await res.json() as { title: string; section_number: string; heading?: string; content: string; gazette_reference?: string; act_year?: number };
      modalBody.innerHTML = `
        <div class="provision-detail-content">
          ${data.heading ? `<h3 class="provision-heading">${escapeHtml(data.heading)}</h3>` : ''}
          <div class="provision-verbatim-text">
            <pre>${escapeHtml(data.content || 'Verbatim provision text loaded.')}</pre>
          </div>
          <div class="provision-meta-box">
            <span><strong>Act:</strong> ${escapeHtml(data.title)} (${data.act_year || 'Controlling'})</span>
            <span><strong>Reference:</strong> ${escapeHtml(data.section_number)}</span>
            ${data.gazette_reference ? `<span><strong>Gazette:</strong> ${escapeHtml(data.gazette_reference)}</span>` : ''}
          </div>
        </div>
      `;
    } else {
      modalBody.innerHTML = `
        <div class="provision-fallback">
          <p>Official gazette provision text for <strong>${escapeHtml(actName)} (${escapeHtml(sectionRef)})</strong> is indexed and verified in Justor's statutory database.</p>
          <small>Source verification: Ministry of Law, Justice and Parliamentary Affairs / Bangladesh Gazette.</small>
        </div>
      `;
    }
  } catch {
    const modalBody = backdrop.querySelector('.provision-modal-body');
    if (modalBody) {
      modalBody.innerHTML = `<div class="provision-fallback"><p>Official text for <strong>${escapeHtml(actName)} (${escapeHtml(sectionRef)})</strong>.</p></div>`;
    }
  }
};

document.addEventListener('input', (event) => {
  const target = event.target as HTMLElement;
  if (target.matches('[data-auto-resize]')) {
    target.style.height = 'auto';
    target.style.height = `${Math.min(target.scrollHeight, 160)}px`;
  }
});

document.addEventListener('keydown', (event) => {
  const target = event.target as HTMLElement;
  if (target.matches('.composer-input-box textarea') && event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    const form = target.closest<HTMLFormElement>('form');
    if (form) void submitResearch(form);
  }
});

document.addEventListener('click', (event) => {
  const target = event.target as HTMLElement;
  const link = target.closest<HTMLAnchorElement>('[data-route]');
  if (link) {
    event.preventDefault();
    if (state.menuOpen) {
      state.menuOpen = false;
      document.querySelector('.mobile-drawer')?.classList.remove('is-open');
      document.querySelector('.mobile-drawer-overlay')?.classList.remove('is-open');
    }
    if (state.sidebarOpen) {
      state.sidebarOpen = false;
      document.querySelector('.workspace-sidebar')?.classList.remove('is-open');
      document.querySelector('.workspace-sidebar-overlay')?.classList.remove('is-open');
      const btn = document.querySelector('.workspace-menu-button');
      if (btn) {
        btn.setAttribute('aria-expanded', 'false');
        btn.innerHTML = icon('menu');
      }
      document.body.classList.remove('sidebar-open');
    }
    const role = link.dataset.role as Role | undefined;
    const locked = lockedRole();
    if (role && locked && role !== locked) {
      showToast(ui(state.language, 'chooseRoleLocked'), ui(state.language, 'roleLockedToast'), 'warning');
      return;
    }
    if (role && !locked) { state.role = role; localStorage.setItem('justor-role', role); }
    const destPath = parseLocalizedPath(new URL(link.href, window.location.origin).pathname).routePath;
    if (locked && destPath.startsWith('/workspace/') && !destPath.startsWith(`/workspace/${locked}`)) {
      showToast(ui(state.language, 'chooseRoleLocked'), ui(state.language, 'roleLockedToast'), 'warning');
      return;
    }
    navigate(link.href);
    return;
  }
  const actionElement = target.closest<HTMLElement>('[data-action]');
  const action = actionElement?.dataset.action;

  if (action === 'open-matter-modal' || action === 'open-matter-workspace') {
    event.preventDefault();
    openMatterWorkspaceModal(
      state.language,
      (matterPrompt) => {
        const homePath = localizedPath('/', state.language);
        if (window.location.pathname !== homePath && !window.location.pathname.endsWith('/')) {
          navigate(homePath);
        }
        setTimeout(() => {
          const textarea = document.querySelector<HTMLTextAreaElement>('.composer-input-box textarea[name="query"], .chat-floating-composer textarea');
          if (textarea) {
            textarea.value = matterPrompt;
            textarea.dispatchEvent(new Event('input', { bubbles: true }));
            textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const form = textarea.closest<HTMLFormElement>('form');
            if (form) void submitResearch(form);
          }
        }, 150);
      },
      (act, sectionRef) => {
        void openProvisionModal(act, sectionRef);
      }
    );
    return;
  }

  if (action === 'open-whatsapp-modal') {
    event.preventDefault();
    if (state.role !== 'professional') {
      alert(state.language === 'bn'
        ? 'হোয়াটসঅ্যাপ চেম্বার ইন্টিগ্রেশন সুবিধাটি শুধুমাত্র নিবন্ধিত আইনজীবী ও চেম্বারের জন্য নির্ধারিত।'
        : 'WhatsApp Chamber Integration is exclusively available for Advocates and Legal Chambers in Chamber OS.');
      return;
    }
    const activeMatter = getActiveMatter();
    openWhatsAppModal(activeMatter);
    return;
  }

  if (action === 'open-ocr-modal') {
    event.preventDefault();
    openDocumentOcrModal(state.language, (summaryPrompt) => {
      const textarea = document.querySelector<HTMLTextAreaElement>('.composer-input-box textarea[name="query"]');
      if (textarea) {
        textarea.value = summaryPrompt;
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
        const form = textarea.closest<HTMLFormElement>('form');
        if (form) void submitResearch(form);
      }
    });
    return;
  }

  if (action === 'toggle-live-search') {
    event.preventDefault();
    state.liveSearchEnabled = !state.liveSearchEnabled;
    document.querySelectorAll<HTMLElement>('.composer-search-toggle, .live-search-chip-btn').forEach((btn) => {
      btn.classList.toggle('is-active', state.liveSearchEnabled);
      btn.setAttribute('aria-pressed', String(state.liveSearchEnabled));
      const span = btn.querySelector('.search-toggle-label, span');
      if (span) {
        if (btn.classList.contains('composer-search-toggle')) {
          span.textContent = state.language === 'bn' ? 'লাইভ সার্চ' : 'Search';
        } else {
          span.textContent = `${state.language === 'bn' ? 'গুগল লাইভ সার্চ' : 'Google Live Search'}${state.liveSearchEnabled ? ' (ON)' : ''}`;
        }
      }
    });
    if (state.liveSearchEnabled) {
      showToast(
        state.language === 'bn' ? 'গুগল লাইভ সার্চ সক্রিয় 🌐' : 'Google Live Search Enabled 🌐',
        state.language === 'bn' 
          ? 'বাংলাদেশ সরকারি গেজেট ও পোর্টালের তথ্যে লাইভ যাচাই সক্রিয় করা হয়েছে।' 
          : 'Queries will be verified against live Bangladesh government gazettes and court portals via Google Search.',
        'positive'
      );
    } else {
      showToast(
        state.language === 'bn' ? 'গুগল লাইভ সার্চ নিষ্ক্রিয়' : 'Google Live Search Disabled',
        state.language === 'bn' 
          ? 'শুধুমাত্র অভ্যন্তরীণ অনুমোদিত বিধিবদ্ধ আইন ও রেফারেন্স ব্যবহৃত হবে।' 
          : 'Restricted to canonical statutory provisions and case precedents.',
        'neutral'
      );
    }
    return;
  }

  if (action === 'ask-related-question') {
    event.preventDefault();
    const question = actionElement?.dataset.question;
    if (!question) return;
    const textarea = document.querySelector<HTMLTextAreaElement>('.composer-input-box textarea[name="query"]');
    if (textarea) {
      textarea.value = question;
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
      state.liveSearchEnabled = true;
      document.querySelectorAll<HTMLElement>('.composer-search-toggle, .live-search-chip-btn').forEach((btn) => {
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        const span = btn.querySelector('.search-toggle-label, span');
        if (span && !btn.classList.contains('composer-search-toggle')) {
          span.textContent = `${state.language === 'bn' ? 'গুগল লাইভ সার্চ' : 'Google Live Search'} (ON)`;
        }
      });
      const form = textarea.closest<HTMLFormElement>('form');
      if (form) void submitResearch(form);
    }
    return;
  }

  if (action === 'menu') {
    state.menuOpen = !state.menuOpen;
    const drawer = document.querySelector('.mobile-drawer');
    const overlay = document.querySelector('.mobile-drawer-overlay');
    const btn = document.querySelector('.menu-button');
    if (drawer && overlay) {
      drawer.classList.toggle('is-open', state.menuOpen);
      drawer.setAttribute('aria-hidden', String(!state.menuOpen));
      overlay.classList.toggle('is-open', state.menuOpen);
      overlay.setAttribute('aria-hidden', String(!state.menuOpen));
      if (btn) {
        btn.setAttribute('aria-expanded', String(state.menuOpen));
        btn.innerHTML = icon(state.menuOpen ? 'close' : 'menu');
      }
    } else {
      render(true);
    }
  }
  if (action === 'language') {
    const preservedQuery = document.querySelector<HTMLInputElement | HTMLTextAreaElement>('[name="query"]')?.value ?? '';
    const nextLanguage: Language = state.language === 'en' ? 'bn' : 'en';
    const next = new URL(window.location.href);
    next.pathname = localizedPath(state.routePath, nextLanguage);
    navigate(`${next.pathname}${next.search}${next.hash}`, true, preservedQuery);
  }
  if (action === 'switch-experience') {
    if (isRoleLocked()) {
      showToast(ui(state.language, 'chooseRoleLocked'), ui(state.language, 'roleLockedToast'), 'warning');
      return;
    }
    navigate(localizedPath('/start', state.language));
  }
  if (action === 'toggle-sidebar') {
    state.sidebarOpen = !state.sidebarOpen;
    document.querySelector('.workspace-sidebar')?.classList.toggle('is-open', state.sidebarOpen);
    document.querySelector('.workspace-sidebar-overlay')?.classList.toggle('is-open', state.sidebarOpen);
    const btn = document.querySelector('.workspace-menu-button');
    if (btn) {
      btn.setAttribute('aria-expanded', String(state.sidebarOpen));
      btn.innerHTML = icon(state.sidebarOpen ? 'close' : 'menu');
    }
    document.body.classList.toggle('sidebar-open', state.sidebarOpen);
  }
  if (action === 'close-sidebar') {
    state.sidebarOpen = false;
    document.querySelector('.workspace-sidebar')?.classList.remove('is-open');
    document.querySelector('.workspace-sidebar-overlay')?.classList.remove('is-open');
    const btn = document.querySelector('.workspace-menu-button');
    if (btn) {
      btn.setAttribute('aria-expanded', 'false');
      btn.innerHTML = icon('menu');
    }
    document.body.classList.remove('sidebar-open');
  }
  if (action === 'lock-role') {
    const role = actionElement?.dataset.role as Role | undefined;
    if (!role || !isValidRole(role) || !state.session) return;
    lockUserRole(role);
    navigate(workspacePathForRole(role));
    return;
  }
  if (action === 'new-research') {
    state.lastResearch = null;
    state.lastResearchRole = null;
    chatStore.createThread(state.role);
    render(true);
  }
  if (action === 'select-thread') {
    const threadId = actionElement?.dataset.threadId;
    if (threadId) {
      chatStore.setActiveThreadId(state.role, threadId);
      render(true);
    }
  }
  if (action === 'delete-thread') {
    event.stopPropagation();
    const threadId = actionElement?.dataset.threadId;
    if (threadId) {
      chatStore.deleteThread(threadId, state.role);
      render(true);
    }
  }
  if (action === 'clear-threads') {
    chatStore.clearAllForRole(state.role);
    render(true);
  }
  const roleOption = target.closest<HTMLElement>('.profile-role-option');
  if (roleOption) {
    const radio = roleOption.querySelector<HTMLInputElement>('input[type="radio"]');
    if (radio) {
      radio.checked = true;
      document.querySelectorAll('.profile-role-option').forEach((el) => {
        el.classList.remove('is-selected');
        const check = el.querySelector('.role-option-check');
        if (check) check.textContent = '';
      });
      roleOption.classList.add('is-selected');
      const check = roleOption.querySelector('.role-option-check');
      if (check) check.textContent = '✓';
    }
  }
  const suggestedCard = target.closest<HTMLElement>('[data-suggested-query]');
  if (suggestedCard) {
    const query = suggestedCard.dataset.suggestedQuery;
    if (query) {
      const textarea = document.querySelector<HTMLTextAreaElement>('textarea[name="query"]');
      if (textarea) {
        textarea.value = query;
        const form = textarea.closest<HTMLFormElement>('form');
        if (form) void submitResearch(form);
      }
    }
  }
  if (action === 'close-toast') actionElement?.closest('.toast')?.remove();
  if (action === 'close-provision-modal') document.querySelector('.provision-modal-backdrop')?.remove();
  if (action === 'view-provision') {
    const act = actionElement?.dataset.act || '';
    const sec = actionElement?.dataset.section || '';
    void openProvisionModal(act, sec);
  }
  if (action === 'click-citation') {
    const tag = actionElement?.dataset.citation || '';
    const sources = state.lastResearch?.authorities || [];
    let idx = -1;
    if (tag.startsWith('ACT-') || tag.startsWith('S') || tag.startsWith('CASE-') || tag.startsWith('DLR-')) {
      const num = parseInt(tag.replace(/[^0-9]/g, ''), 10) - 1;
      if (num >= 0 && num < sources.length) idx = num;
    }
    if (idx < 0) {
      idx = sources.findIndex((s) => (s.provision || s.citation || '').includes(tag) || (s.authority || s.title || '').includes(tag));
    }
    if (idx >= 0) {
      state.selectedSource = idx;
      const sourcesDetails = document.querySelector<HTMLDetailsElement>('.sources-collapsible');
      if (sourcesDetails) sourcesDetails.open = true;
      document.querySelectorAll('[data-result-source]').forEach((button, i) => button.classList.toggle('active', i === idx));
      const panel = document.querySelector<HTMLElement>('[data-source-panel]');
      if (panel) panel.outerHTML = sourcePanel(sources[idx], 'result-source-panel');
      const s = sources[idx];
      void openProvisionModal(s.authority || s.title, s.provision || s.citation || '');
    }
  }
  if (action === 'close-menu') {
    state.menuOpen = false;
    const drawer = document.querySelector('.mobile-drawer');
    const overlay = document.querySelector('.mobile-drawer-overlay');
    const btn = document.querySelector('.menu-button');
    if (drawer && overlay) {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      if (btn) {
        btn.setAttribute('aria-expanded', 'false');
        btn.innerHTML = icon('menu');
      }
    } else {
      render(true);
    }
  }
  if (action === 'click-citation-index') {
    const idx = Number(actionElement?.dataset.citationIndex ?? 0);
    const sources = state.lastResearch?.authorities || [];
    if (idx >= 0 && idx < sources.length) {
      state.selectedSource = idx;
      const sourcesDetails = document.querySelector<HTMLDetailsElement>('.sources-collapsible');
      if (sourcesDetails) sourcesDetails.open = true;
      document.querySelectorAll('[data-result-source]').forEach((button, i) => button.classList.toggle('active', i === idx));
      const panel = document.querySelector<HTMLElement>('[data-source-panel]');
      if (panel) panel.outerHTML = sourcePanel(sources[idx], 'result-source-panel');
      const s = sources[idx];
      if (s) void openProvisionModal(s.authority || s.title, s.provision || s.citation || '');
    }
  }
  if (action === 'focus-composer') {
    const textarea = document.querySelector<HTMLTextAreaElement>('.composer-input-box textarea, .chat-floating-composer textarea');
    if (textarea) {
      textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
      textarea.focus();
    }
  }
  if (action === 'copy-research-answer') {
    if (state.lastResearch?.shortAnswer) {
      void navigator.clipboard.writeText(state.lastResearch.shortAnswer);
      showToast('Copied to clipboard', 'Full legal analysis copied successfully.', 'positive');
    }
  }
  if (action === 'print-legal-memo') {
    if (state.lastResearch) {
      printChambersLegalMemo(state.lastResearch, state.role, state.language);
    }
  }
  if (action === 'toggle-authorities-sources') {
    const card = actionElement?.closest('.research-analysis');
    const postMeta = card?.querySelector<HTMLElement>('.post-streaming-metadata');
    if (postMeta && postMeta.style.display === 'none') {
      postMeta.style.display = 'block';
    }
    const details = card?.querySelector<HTMLDetailsElement>('.sources-collapsible');
    if (details) {
      details.open = !details.open;
      if (details.open) {
        details.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }
  if (action === 'toggle-google-sources') {
    const card = actionElement?.closest('.research-analysis');
    const details = card?.querySelector<HTMLDetailsElement>('.perplexity-sources-collapsible');
    if (details) {
      details.open = !details.open;
      if (details.open) {
        details.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }
  if (action === 'feedback-positive') {
    const widget = actionElement?.closest('[data-feedback-widget]');
    if (widget) {
      widget.innerHTML = `<div class="feedback-submitted-success"><span>✓</span> ${escapeHtml(ui(state.language, 'feedbackRecorded'))}</div>`;
    }
    const backendUrl = (import.meta.env.VITE_BACKEND_URL?.trim() || 'https://justorai-backend.onrender.com').replace(/\/$/, '');
    void fetch(`${backendUrl}/api/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query_run_id: `run_${Date.now()}`,
        rating: 1,
        category: 'helpful',
        comment: 'Accurate and helpful'
      })
    });
  }
  if (action === 'open-pilot-modal') {
    if (state.menuOpen) {
      state.menuOpen = false;
      document.querySelector('.mobile-drawer')?.classList.remove('is-open');
      document.querySelector('.mobile-drawer-overlay')?.classList.remove('is-open');
    }
    const existing = document.querySelector('[data-pilot-modal-overlay]');
    if (!existing) {
      document.body.insertAdjacentHTML('beforeend', renderPilotModal());
    }
  }
  if (action === 'close-pilot-modal') {
    document.querySelector('[data-pilot-modal-overlay]')?.remove();
  }

  // Plan selection in Checkout Modal
  const selectPlanEl = (event.target as HTMLElement)?.closest<HTMLElement>('[data-select-plan]');
  if (selectPlanEl) {
    const plan = selectPlanEl.dataset.selectPlan || 'founding_advocate';
    const price = selectPlanEl.dataset.price || '499';
    const container = selectPlanEl.closest('.pilot-modal-card');
    if (container) {
      container.querySelectorAll('.checkout-plan-card').forEach(c => {
        c.classList.remove('is-selected');
        const icon = c.querySelector<HTMLElement>('.plan-check-icon');
        if (icon) icon.style.display = 'none';
      });
      selectPlanEl.classList.add('is-selected');
      const checkIcon = selectPlanEl.querySelector<HTMLElement>('.plan-check-icon');
      if (checkIcon) checkIcon.style.display = 'inline';

      const planInput = container.querySelector<HTMLInputElement>('#checkout-plan-input');
      if (planInput) planInput.value = plan;

      const displayAmount = container.querySelector<HTMLElement>('#checkout-display-amount');
      if (displayAmount) displayAmount.textContent = plan === 'founding_chamber' ? '৳1,499' : '৳499';

      const submitBtn = container.querySelector<HTMLButtonElement>('#checkout-submit-btn');
      if (submitBtn) {
        submitBtn.innerHTML = `${state.language === 'bn' ? `মেম্বারশিপ সক্রিয় করুন (৳${price === '1499' ? '১,৪৯৯' : '৪৯৯'}/মাস)` : `Activate Membership (৳${price}/mo)`} ${icon('arrow', 14)}`;
      }
    }
  }

  // Payment method tab in Checkout Modal
  const selectPmEl = (event.target as HTMLElement)?.closest<HTMLElement>('[data-select-pm]');
  if (selectPmEl) {
    const pm = selectPmEl.dataset.selectPm || 'bkash';
    const container = selectPmEl.closest('.pilot-modal-card');
    if (container) {
      container.querySelectorAll('.checkout-pm-tab').forEach(t => t.classList.remove('active'));
      selectPmEl.classList.add('active');

      const pmInput = container.querySelector<HTMLInputElement>('#checkout-pm-input');
      if (pmInput) pmInput.value = pm;

      const planInput = container.querySelector<HTMLInputElement>('#checkout-plan-input');
      const price = planInput?.value === 'founding_chamber' ? '1,499' : '499';
      const isBn = state.language === 'bn';

      const instructions = container.querySelector<HTMLElement>('#checkout-pm-instructions');
      const trxField = container.querySelector<HTMLElement>('#checkout-trx-field');
      const trxInput = container.querySelector<HTMLInputElement>('#trx-id');

      if (instructions) {
        if (pm === 'bkash') {
          instructions.innerHTML = `
            <strong>${isBn ? 'bKash পেমেন্ট নির্দেশনা:' : 'bKash Payment Instructions:'}</strong><br/>
            ১. আপনার bKash অ্যাপ থেকে <strong>Send Money</strong> অথবা <strong>Payment</strong> অপশনে যান: <code style="background: #FFE4E6; padding: 1px 6px; border-radius: 4px; color: #D12053; font-weight: 700;">01712-345678</code><br/>
            ২. টাকার পরিমাণ দিন: <strong id="checkout-display-amount">৳${price}</strong> (রেফারেন্স: আপনার নাম)<br/>
            ৩. সফল লেনদেনের পর প্রাপ্ত <strong>TrxID</strong> নিচে লিখে একাউন্ট সক্রিয় করুন।
          `;
          if (trxField) trxField.style.display = 'flex';
          if (trxInput) trxInput.required = true;
        } else if (pm === 'nagad') {
          instructions.innerHTML = `
            <strong>${isBn ? 'Nagad পেমেন্ট নির্দেশনা:' : 'Nagad Payment Instructions:'}</strong><br/>
            ১. আপনার Nagad ওয়ালেট অ্যাপ থেকে <strong>Send Money</strong> অপশনে যান: <code style="background: #FFF7ED; padding: 1px 6px; border-radius: 4px; color: #EA580C; font-weight: 700;">01712-345678</code><br/>
            ২. টাকার পরিমাণ দিন: <strong id="checkout-display-amount">৳${price}</strong><br/>
            ৩. প্রাপ্ত <strong>TrxID (৮ সংখ্যার কোড)</strong> নিচে লিখে সক্রিয় বাটনে ক্লিক করুন।
          `;
          if (trxField) trxField.style.display = 'flex';
          if (trxInput) trxInput.required = true;
        } else {
          instructions.innerHTML = `
            <strong>${isBn ? 'অনলাইন / কার্ড পেমেন্ট (SSLCommerz Gateway):' : 'Online / Card Payment (SSLCommerz):'}</strong><br/>
            যেকোনো ভিসা, মাস্টারকার্ড বা ইন্টারনেট ব্যাংকিং-এর মাধ্যমে তাত্ক্ষণিক স্বয়ংক্রিয় অ্যাক্টিভেশন।<br/>
            <em>TrxID প্রয়োজন নেই — সাবমিট করলেই তাত্ক্ষণিক মেম্বারশিপ সক্রিয় হবে।</em>
          `;
          if (trxField) trxField.style.display = 'none';
          if (trxInput) trxInput.required = false;
        }
      }
    }
  }
  if (action === 'feedback-negative-toggle') {
    const drawer = document.querySelector<HTMLElement>('[data-feedback-drawer]');
    if (drawer) drawer.hidden = !drawer.hidden;
  }
  if (action === 'close-feedback-drawer') {
    const drawer = document.querySelector<HTMLElement>('[data-feedback-drawer]');
    if (drawer) drawer.hidden = true;
  }
  if (action === 'go-back') {
    if (window.history.length > 1 && document.referrer && document.referrer.includes(window.location.host)) {
      window.history.back();
    } else {
      const fallback = actionElement?.dataset.fallback || '/';
      navigate(localizedPath(fallback, state.language));
    }
  }
  if (action === 'sign-out') void authService.signOut().then(() => { state.session = null; render(); });
  if (action === 'google-sign-in') {
    const next = actionElement?.dataset.next ?? localizedPath('/choose-role', state.language);
    void authService.signInWithGoogle(next).then((result) => {
      if (result.error) showToast('Sign-in unavailable', result.error, 'warning');
    });
  }
  if (action === 'guest-sign-in') {
    const next = actionElement?.dataset.next ?? localizedPath('/choose-role', state.language);
    state.session = authService.signInAsGuest();
    showToast('Guest Mode Activated', 'Choose your workspace role to continue.', 'positive');
    navigate(next);
  }
  if (action === 'export-chat-history') {
    const threads = chatStore.getThreadsByRole(state.role);
    const blob = new Blob([JSON.stringify({ exportDate: new Date().toISOString(), role: state.role, profile: getStoredProfile(), threads }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `justor_legal_research_${state.role}_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(ui(state.language, 'exportData'), 'Research history exported successfully.', 'positive');
  }
  if (action === 'clear-threads-profile') {
    chatStore.clearAllForRole(state.role);
    showToast(ui(state.language, 'clearAllHistory'), 'All local research threads cleared.', 'positive');
    render(true);
  }
  if (action === 'ask-from-guide') {
    const context = { id: actionElement?.dataset.guideId ?? '', title: actionElement?.dataset.guideTitle ?? '', topic: actionElement?.dataset.guideTopic ?? '' };
    sessionStorage.setItem('justor-guide-context', JSON.stringify(context));
    navigate(`${localizedPath('/workspace/' + state.role, state.language)}#ask`);
  }
  if (action === 'remove-context') {
    sessionStorage.removeItem('justor-guide-context');
    actionElement?.closest('.research-context')?.remove();
    const form = document.querySelector<HTMLFormElement>('[data-research-form]');
    if (form) {
      delete form.dataset.contextId;
      delete form.dataset.contextTitle;
      delete form.dataset.contextTopic;
    }
  }
  const prompt = target.closest<HTMLButtonElement>('[data-prompt]');
  if (prompt) {
    const textarea = document.querySelector<HTMLTextAreaElement>('.composer-input-box textarea, .research-composer textarea');
    if (textarea) { textarea.value = prompt.dataset.prompt ?? ''; textarea.focus(); }
  }
  const topic = target.closest<HTMLButtonElement>('[data-citizen-topic]');
  if (topic) {
    const value = topic.dataset.citizenTopic ?? '';
    state.citizenHasSearched = true;
    document.querySelector<HTMLElement>('[data-citizen-mascot]')?.remove();
    const input = document.querySelector<HTMLInputElement>('[data-citizen-guide-form] input');
    if (input) input.value = clusterLabel(value);
    void hydrateCitizen('', value);
  }
  const cluster = target.closest<HTMLButtonElement>('[data-guide-cluster]');
  if (cluster) {
    state.guideCluster = cluster.dataset.guideCluster ?? '';
    state.guidePage = 1;
    document.querySelectorAll('[data-guide-cluster]').forEach((button) => button.classList.toggle('active', button === cluster));
    void hydrateGuides();
  }
  const more = target.closest<HTMLButtonElement>('[data-guide-more]');
  if (more) { state.guidePage += 1; void hydrateGuides(true); }
  const libraryType = target.closest<HTMLButtonElement>('[data-library-type]');
  if (libraryType) {
    const params = new URLSearchParams(window.location.search);
    const value = libraryType.dataset.libraryType ?? '';
    value ? params.set('type', value) : params.delete('type');
    navigate(`${localizedPath('/legal-library', state.language)}${params.size ? `?${params}` : ''}`);
  }
  const resultSource = target.closest<HTMLButtonElement>('[data-result-source]');
  if (resultSource && state.lastResearch) {
    const index = Number(resultSource.dataset.resultSource ?? 0);
    state.selectedSource = index;
    document.querySelectorAll('[data-result-source]').forEach((button) => button.classList.toggle('active', button === resultSource));
    const s = state.lastResearch.authorities?.[index];
    const panel = document.querySelector<HTMLElement>('[data-source-panel]');
    if (s && panel) panel.outerHTML = sourcePanel(s, 'result-source-panel');
    if (s) void openProvisionModal(s.authority || s.title, s.provision || s.citation || '');
  }
  const proofSource = target.closest<HTMLButtonElement>('[data-proof-source]');
  if (proofSource) {
    document.querySelectorAll('[data-proof-source]').forEach((button) => button.classList.toggle('active', button === proofSource));
    const index = Number(proofSource.dataset.proofSource ?? 0);
    const source = state.productProof?.sources[index];
    const panel = document.querySelector<HTMLElement>('[data-product-proof] .proof-source');
    if (source && panel) panel.outerHTML = sourcePanel(source, 'proof-source');
  }
});

document.addEventListener('submit', (event) => {
  const form = event.target as HTMLFormElement;
  if (form.matches('[data-research-form]')) { event.preventDefault(); void submitResearch(form); }
  if (form.matches('[data-library-home-search]')) {
    event.preventDefault();
    const query = String(new FormData(form).get('query') ?? '').trim();
    navigate(`${localizedPath('/legal-library', state.language)}${query ? `?q=${encodeURIComponent(query)}` : ''}`);
  }
  if (form.matches('[data-library-search]')) {
    event.preventDefault();
    const query = String(new FormData(form).get('query') ?? '').trim();
    const params = new URLSearchParams(window.location.search);
    query ? params.set('q', query) : params.delete('q');
    navigate(`${localizedPath('/legal-library', state.language)}${params.size ? `?${params}` : ''}`);
  }
  if (form.matches('[data-guide-directory-search]')) {
    event.preventDefault();
    state.guideQuery = String(new FormData(form).get('query') ?? '').trim();
    state.guidePage = 1;
    void hydrateGuides();
  }
  if (form.matches('[data-citizen-guide-form]')) {
    event.preventDefault();
    const query = String(new FormData(form).get('query') ?? '').trim();
    state.citizenHasSearched = true;
    document.querySelector<HTMLElement>('[data-citizen-mascot]')?.remove();
    void hydrateCitizen(query);
  }
  if (form.matches('[data-action="save-profile-form"]')) {
    event.preventDefault();
    const formData = new FormData(form);
    const fullName = String(formData.get('fullName') || '').trim();
    const role = lockedRole() || (formData.get('role') as Role) || state.role;
    const partnerOrg = String(formData.get('partnerOrg') || '').trim();

    saveStoredProfile({ fullName, role, partnerOrg });
    state.role = role;

    // Sync partnerOrg to Supabase profiles table so backend quota can read it
    if (state.session?.user?.id) {
      void authService.updateProfile(state.session.user.id, { partner_org: partnerOrg || null });
    }

    showToast(ui(state.language, 'saveSettings'), 'Profile updated successfully.', 'positive');
    render(true);
  }
  if (form.matches('[data-action="submit-pilot-form"]')) {
    event.preventDefault();
    const formData = new FormData(form);
    const plan = String(formData.get('plan') || 'founding_advocate');
    const paymentMethod = String(formData.get('payment_method') || 'bkash');
    const trxId = String(formData.get('trx_id') || '').trim();
    const phone = String(formData.get('phone') || '').trim();
    const advocateName = String(formData.get('advocate_name') || '').trim();
    const chamberName = String(formData.get('chamber_name') || '').trim();
    const barAssociation = String(formData.get('bar_association') || 'Supreme Court Bar Association (SCBA)').trim();

    const isBn = state.language === 'bn';
    const isChamber = plan === 'founding_chamber';
    const amountStr = isChamber ? '৳১,৪৯৯/মাস' : '৳৪৯৯/মাস';
    const planTitle = isChamber 
      ? (isBn ? 'ফাউন্ডিং চেম্বার ওএস (Founding Chamber)' : 'Founding Chamber Tier')
      : (isBn ? 'ফাউন্ডিং অ্যাডভোকেট (Solo Advocate)' : 'Founding Advocate Tier');

    const modalContainer = form.closest('.pilot-modal-card');
    if (modalContainer) {
      modalContainer.innerHTML = `
        <div style="text-align: center; padding: 24px 14px;">
          <div style="width: 52px; height: 52px; border-radius: 50%; background: #ECFDF5; color: #10B981; font-size: 26px; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 14px; border: 2px solid #A7F3D0;">
            ✓
          </div>
          <h3 style="color: #0F172A; margin: 0 0 6px 0; font-size: 19px; font-weight: 700;">
            ${isBn ? 'অভিনন্দন! মেম্বারশিপ সক্রিয় হয়েছে' : 'Welcome! Membership Activated'}
          </h3>
          <p style="color: #475467; font-size: 13px; line-height: 1.5; margin: 0 0 16px 0;">
            ${isBn 
              ? `বিজ্ঞ অ্যাডভোকেট <strong>${escapeHtml(advocateName)}</strong>, জাসটর চেম্বার ওএস-এ আপনার <strong>${planTitle}</strong> অ্যাকাউন্ট সফলভাবে সক্রিয় করা হয়েছে।`
              : `Advocate <strong>${escapeHtml(advocateName)}</strong>, your <strong>${planTitle}</strong> account is now live.`}
          </p>

          <div style="background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 8px; padding: 12px 14px; margin-bottom: 18px; text-align: left; font-size: 12px; line-height: 1.6;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
              <span style="color: #64748B;">Plan:</span>
              <strong style="color: #0F172A;">${planTitle} (${amountStr})</strong>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
              <span style="color: #64748B;">Payment:</span>
              <span style="color: #0F172A; font-family: monospace;">${paymentMethod.toUpperCase()} ${trxId ? `(${trxId})` : ''}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
              <span style="color: #64748B;">WhatsApp Gateway:</span>
              <span style="color: #25D366; font-weight: 600;">📱 ${escapeHtml(phone)}</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="color: #64748B;">Daily 8:00 AM Brief:</span>
              <span style="color: #10B981; font-weight: 600;">✅ Active</span>
            </div>
          </div>

          <div style="display: flex; gap: 10px; justify-content: center; flex-wrap: wrap;">
            <button class="button" type="button" id="btn-open-wa-after-sub" style="background: #25D366; color: #0F172A; font-weight: 700; border: none;">
              💬 ${isBn ? 'হোয়াটসঅ্যাপ সহকারী খুলুন' : 'Open WhatsApp Assistant'}
            </button>
            <button class="button button-outline" type="button" data-action="close-pilot-modal">
              ${isBn ? 'চেম্বার ওএস-এ প্রবেশ করুন' : 'Enter Chamber OS'}
            </button>
          </div>
        </div>
      `;

      modalContainer.querySelector('#btn-open-wa-after-sub')?.addEventListener('click', async () => {
        modalContainer.closest('[data-pilot-modal-overlay]')?.remove();
        const { openWhatsAppModal } = await import('./whatsapp-modal');
        openWhatsAppModal();
      });
    }

    // Persist subscription in localStorage
    localStorage.setItem('justor_subscription', JSON.stringify({
      plan,
      planTitle,
      amount: isChamber ? 1499 : 499,
      status: 'active',
      phone,
      advocateName,
      chamberName,
      barAssociation,
      activatedAt: new Date().toISOString()
    }));
    localStorage.setItem('justor_chamber_phone', phone);

    // Call backend checkout
    const backendUrl = (import.meta.env.VITE_BACKEND_URL?.trim() || 'https://justorai-backend.onrender.com').replace(/\/$/, '');
    void fetch(`${backendUrl}/api/subscription/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        plan,
        payment_method: paymentMethod,
        trx_id: trxId,
        sender_phone: phone,
        advocate_name: advocateName,
        chamber_name: chamberName,
        bar_association: barAssociation
      })
    });

    showToast(
      isBn ? 'মেম্বারশিপ সক্রিয়!' : 'Membership Activated!',
      isBn ? `স্বাগতম অ্যাডভোকেট ${advocateName}। হোয়াটসঅ্যাপ ব্রিফিং কনফার্মেশন পাঠানো হয়েছে।` : `Welcome Advocate ${advocateName}. WhatsApp confirmation sent.`,
      'positive'
    );
  }
  if (form.matches('[data-action="submit-qa-feedback"]')) {
    event.preventDefault();
    const data = new FormData(form);
    const category = String(data.get('category') || 'other');
    const comment = String(data.get('comment') || '');
    const widget = form.closest('[data-feedback-widget]');
    if (widget) {
      widget.innerHTML = `<div class="feedback-submitted-success"><span>✓</span> ${escapeHtml(ui(state.language, 'feedbackRecorded'))}</div>`;
    }
    const backendUrl = (import.meta.env.VITE_BACKEND_URL?.trim() || 'https://justorai-backend.onrender.com').replace(/\/$/, '');
    void fetch(`${backendUrl}/api/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query_run_id: `run_${Date.now()}`,
        rating: -1,
        category,
        comment,
        query: state.lastResearch?.shortAnswer?.slice(0, 100)
      })
    });
  }
});

window.addEventListener('popstate', () => render());

const restorePendingContext = (): void => {
  if (!state.session) return;
  const raw = sessionStorage.getItem('justor-guide-context');
  if (!raw) return;
  try {
    const context = JSON.parse(raw) as { id: string; title: string; topic: string };
    const handoff = document.querySelector<HTMLElement>('.citizen-ai-handoff');
    if (handoff && !handoff.querySelector('.chat-floating-composer')) {
      handoff.insertAdjacentHTML('beforeend', renderBottomChatBar(state.role, 'Describe your specific situation...', ['Explain my next step', 'What authorities apply?'], context));
    }
  } catch { sessionStorage.removeItem('justor-guide-context'); }
};

function restorePendingResearch(): void {
  if (!state.session) return;
  const raw = sessionStorage.getItem('justor-pending-research');
  if (!raw) return;
  try {
    const pending = JSON.parse(raw) as { query?: string; displayQuery?: string; role?: Role };
    if (!pending.query || !pending.role || state.routePath !== `/workspace/${pending.role}`) return;
    const form = document.querySelector<HTMLFormElement>('[data-research-form]');
    const textarea = form?.querySelector<HTMLTextAreaElement>('textarea[name="query"]');
    if (!form || !textarea) return;
    textarea.value = pending.query;
    if (pending.displayQuery) sessionStorage.setItem('justor-chat-display-query', pending.displayQuery);
    sessionStorage.removeItem('justor-pending-research');
    void submitResearch(form);
  } catch {
    sessionStorage.removeItem('justor-pending-research');
  }
}

function hydrateLearningPath(path: string): void {
  if (!path.startsWith('/workspace/student/learn')) return;
  const slug = path.split('/')[4];
  if (!slug) return;
  const section = getSection(slug);
  const mount = document.querySelector<HTMLElement>('[data-learn-mount]');
  if (!section || !mount) return;
  const reviewOnly = new URLSearchParams(window.location.search).get('review') === '1';
  bindLearningSession(mount, section, state.language, {
    reviewOnly,
    onExit: () => navigate(localizedPath('/workspace/student/learn', state.language)),
    onComplete: () => navigate(`${localizedPath(`/workspace/student/learn/${slug}`, state.language)}?review=1`),
    onGoDeeper: (handoff) => {
      const { query, displayQuery } = buildGoDeeperQuery(section, handoff);
      sessionStorage.setItem('justor-pending-research', JSON.stringify({
        query,
        displayQuery,
        role: 'student',
      }));
      navigate(`${localizedPath('/workspace/student', state.language)}#ask`);
    },
    onViewProvision: (act, sectionLabel) => { void openProvisionModal(act, sectionLabel); },
    localizedPath: (p) => localizedPath(p, state.language),
  });
}

export function mountApp(): void {
  render();
  const updateHeader = (): void => {
    document.querySelector('[data-header]')?.classList.toggle('scrolled', window.scrollY > 12);
  };
  window.addEventListener('scroll', updateHeader, { passive: true });
  updateHeader();

  if (typeof BroadcastChannel !== 'undefined') {
    try {
      const authChannel = new BroadcastChannel('justor_auth');
      authChannel.onmessage = (event) => {
        if (event.data?.type === 'SIGNED_OUT') {
          state.session = null;
          render(true);
        }
      };
    } catch {}
  }

  void authService.session().then((session) => {
    if (session !== state.session) { state.session = session; render(true); restorePendingContext(); restorePendingResearch(); }
  });
  authService.subscribe((session) => {
    state.session = session;
    render(true);
    restorePendingContext();
    restorePendingResearch();
  });
}
